import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Container, Row, Col, Overlay, Tooltip, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  CardNumberElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { FaShieldAlt, FaCheckCircle, FaTag, FaTimes, FaCheck } from 'react-icons/fa';
import useUser from '../../hooks/useUser';
import useABTest from '../../hooks/useABTest';
import TrustStrip from '../../components/TrustStrip';
import StripeErrorBoundary from '../../components/StripeErrorBoundary';
import { confirmSetupWithClient, confirmPaymentWithClient } from '../../utils/stripeClient';
import {
  CheckoutContactFields,
  CheckoutTrialCallout,
  CheckoutPaymentContainer,
  HowFoundUsDropdown,
} from '../../components/CheckoutFormShared';
import {
  buildSmsConsentPayload,
} from '../../components/SmsConsent';
import {
  pushEvent,
  ANALYTICS_EVENTS,
  generateCheckoutTransactionId,
  identifyUserFromLeadCapture,
  capturePostHogEvent,
} from '../../utils/analytics';
import { getAffiliateCodeForApi, getUtmParamsForApi, getAttributionParams } from '../../utils/utmTracker';
import { loadPromptFromSession } from '../../utils/aiPromptStorage';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import { convertLeadToTrial } from '../../utils/stripeConversion';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import { getAgentHandoff } from '../../utils/aiAgentHandoff';
import { validatePromoCode } from '../../api/checkout';
import { buildCouponDescription } from '../../utils/couponDescription';
import fetchWithTimeout from '../../utils/fetchWithTimeout';
import { completeStripeAutoLogin } from '../../utils/stripeAutoLogin';
import { applyThemeMode, useThemeMode } from '../../design-system';
import { getCheckoutOverlayBackground, locationToPath } from '../../utils/checkoutOverlayNavigation';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import './StarterCheckoutPage.css';

const stripeKey = process.env.REACT_APP_STRIPE_PK || '';
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
const PAYMENT_INTENT_FETCH_TIMEOUT_MS = 45000;
const AUTO_LOGIN_FETCH_TIMEOUT_MS = 10000;
const SHOULD_LOG_CHECKOUT_DEBUG = process.env.NODE_ENV !== 'production';

const TERMS_DOC_TYPE = 'main_tos';
const SALES_PAGE_EXPERIMENT_PARAM_KEYS = [
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
];
const SALES_SIGNUP_V4_FEATURE_FLAG = 'sales_page_signup_framing_v4_variant';
const SALES_SIGNUP_V4_EXPERIMENT_NAME = 'sales_page_signup_framing_v4';
const SALES_SIGNUP_V4_ROUTE_VARIANTS = ['full_form', 'email_only', 'auth_first'];
const SALES_SIGNUP_V4_POSTHOG_VARIANTS = ['control', 'email_only', 'auth_first'];
const SALES_SIGNUP_V5_FEATURE_FLAG = 'sales_page_signup_framing_v5_variant';
const SALES_SIGNUP_V5_EXPERIMENT_NAME = 'sales_page_signup_framing_v5';
const SALES_SIGNUP_V5_ROUTE_VARIANTS = ['email_only', 'auth_first'];
const SALES_SIGNUP_V5_POSTHOG_VARIANTS = ['control', 'auth_first'];
const CHECKOUT_DESIGN_VARIANTS = ['control', 'test'];
const CHECKOUT_THEME_VARIANTS = ['light', 'dark'];
let googlePayScriptPromise = null;
let applePayScriptPromise = null;
const checkoutExpressAvailabilityCache = {
  expressCheckoutAvailable: false,
  expressCheckoutChecked: false,
  availableWalletMethods: [],
  linkCheckoutExpected: true,
  linkCheckoutAvailable: false,
};
const EMPTY_SAVED_PAYMENT_METHODS = Object.freeze({
  paymentMethods: [],
  loading: false,
  error: null,
  pending: false,
  hasMethods: false,
});
const CHECKOUT_RECOVERY_STORAGE_PREFIX = 'botspot_checkout_recovery_required';
const CHECKOUT_RECOVERY_MESSAGE =
  'Your payment status needs manual review. Do not submit again; contact support.';
const getCheckoutRecoveryStorageKey = (productSlug) =>
  `${CHECKOUT_RECOVERY_STORAGE_PREFIX}:${productSlug || 'checkout'}`;
const hasStoredCheckoutRecoveryRequirement = (storageKey) => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
};
const storeCheckoutRecoveryRequirement = (storageKey) => {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(storageKey, '1');
    }
  } catch {
    // The in-memory parent state still blocks resubmission when storage is unavailable.
  }
};
const REDESIGN_STRIPE_PAYMENT_ELEMENT_METHOD = 'stripe_payment_element';
const REDESIGN_DYNAMIC_PAYMENT_METHODS = new Set([REDESIGN_STRIPE_PAYMENT_ELEMENT_METHOD]);
const isRedesignDynamicPaymentMethod = (method) => REDESIGN_DYNAMIC_PAYMENT_METHODS.has(method);
const BANK_VERIFICATION_METHOD = 'automatic';

/**
 * @param {{
 *   checkoutTheme?: string,
 *   presentation?: 'page' | 'overlay',
 *   onClose?: (() => void) | null,
 *   planDisplayName?: string | null,
 * }} props
 */
export function CheckoutRedesignLoadingShell({
  checkoutTheme = 'dark',
  presentation = 'overlay',
  onClose = null,
  planDisplayName = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const overlayClass = presentation === 'overlay' ? ' checkout-redesign-page--overlay' : '';
  const loadingPlanDisplayName = planDisplayName || (
    location.pathname === '/checkout/light-plan' ? 'BotSpot Light' : 'Secure checkout'
  );
  const checkoutOverlayBackground = getCheckoutOverlayBackground(location.state);
  const loadingExitPath = checkoutOverlayBackground
    ? locationToPath(checkoutOverlayBackground)
    : '/pricing';
  const handleLoadingCloseClick = useCallback((event) => {
    if (!checkoutOverlayBackground) return;

    event.preventDefault();
    navigate(loadingExitPath, {
      replace: true,
      state: checkoutOverlayBackground.state || null,
    });
  }, [checkoutOverlayBackground, loadingExitPath, navigate]);

  return (
    <div
      className={`checkout-landing-page checkout-redesign-page bsui-scope${overlayClass}`}
      data-bs-theme={checkoutTheme}
      aria-busy="true"
      aria-label="Loading checkout"
    >
      <div className="checkout-redesign-container">
        <div className="checkout-redesign-shell checkout-redesign-shell--loading">
          <header className="checkout-redesign-topbar" aria-label="Checkout">
            {onClose ? (
              <button
                type="button"
                className="checkout-redesign-exit-link"
                aria-label="Close checkout"
                onClick={onClose}
              >
                <FaTimes aria-hidden="true" focusable="false" />
              </button>
            ) : (
              <Link
                to={loadingExitPath}
                className="checkout-redesign-exit-link"
                aria-label="Close checkout"
                onClick={handleLoadingCloseClick}
              >
                <FaTimes aria-hidden="true" focusable="false" />
              </Link>
            )}
          </header>

          <div className="landing-checkout-form checkout-redesign-layout checkout-redesign-layout--loading">
            <section className="checkout-redesign-hero">
              <div className="checkout-redesign-hero__content">
                <span className="checkout-redesign-hero-mark">
                  <img
                    src={botspotLogo}
                    alt=""
                    width="56"
                    height="56"
                    className="checkout-redesign-hero-mark__image"
                  />
                </span>
                <h1>
                  {loadingPlanDisplayName === 'BotSpot Light' ? (
                    <>BotSpot <span className="checkout-redesign-plan-accent">Light</span></>
                  ) : loadingPlanDisplayName}
                </h1>
                <span
                  className="checkout-redesign-skeleton checkout-redesign-skeleton--subtitle"
                  aria-hidden="true"
                />
              </div>
            </section>

            <div className="checkout-redesign-loading-main" aria-hidden="true">
              <div className="checkout-redesign-loading-field checkout-redesign-loading-field--wide">
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--label" />
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--control" />
              </div>
              <div className="checkout-redesign-loading-row">
                <div className="checkout-redesign-loading-field">
                  <span className="checkout-redesign-skeleton checkout-redesign-skeleton--label" />
                  <span className="checkout-redesign-skeleton checkout-redesign-skeleton--control" />
                </div>
                <div className="checkout-redesign-loading-field">
                  <span className="checkout-redesign-skeleton checkout-redesign-skeleton--label" />
                  <span className="checkout-redesign-skeleton checkout-redesign-skeleton--control" />
                </div>
              </div>
              <div className="checkout-redesign-loading-field checkout-redesign-loading-field--wide">
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--label checkout-redesign-skeleton--label-long" />
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--control" />
              </div>
              <div className="checkout-redesign-loading-payment">
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--label checkout-redesign-skeleton--label-long" />
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--payment-option" />
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--payment-fields" />
              </div>
            </div>

            <aside className="checkout-redesign-loading-side" aria-hidden="true">
              <div className="checkout-redesign-loading-summary">
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--label checkout-redesign-skeleton--label-long" />
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--price" />
                <span className="checkout-redesign-skeleton checkout-redesign-skeleton--meta" />
              </div>
              <span className="checkout-redesign-skeleton checkout-redesign-skeleton--promo" />
              <span className="checkout-redesign-skeleton checkout-redesign-skeleton--terms" />
              <span className="checkout-redesign-skeleton checkout-redesign-skeleton--button" />
            </aside>

            <span className="visually-hidden" role="status" aria-live="polite">
              Loading checkout…
            </span>

            <div
              className="checkout-security-badges checkout-redesign-security checkout-redesign-security--footer"
              aria-label="Payment protections"
            >
              <span className="checkout-redesign-security__assurances">
                <span className="checkout-redesign-security__item">Stripe-secured</span>
                <span className="checkout-redesign-security__separator" aria-hidden="true">·</span>
                <span className="checkout-redesign-security__item">Encrypted</span>
                <span className="checkout-redesign-security__separator" aria-hidden="true">·</span>
                <span className="checkout-redesign-security__item">30-day refund</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const updateCheckoutExpressAvailabilityCache = (patch) => {
  Object.assign(checkoutExpressAvailabilityCache, patch);
};

const dollarsToCents = (amount, fallback = 0) => {
  const parsedAmount = Number(amount);
  if (!Number.isFinite(parsedAmount)) {
    return fallback;
  }
  return Math.max(0, Math.round(parsedAmount * 100));
};

const centsToUsd = (amountCents) => {
  const parsedAmount = Number(amountCents);
  if (!Number.isFinite(parsedAmount)) {
    return 0;
  }
  return Math.max(0, parsedAmount) / 100;
};

const CHECKOUT_EXIT_FALLBACK_PATH = '/pricing';
const CHECKOUT_EXIT_QUERY_KEYS = ['returnUrl', 'cancelUrl', 'backUrl'];
const CHECKOUT_SOURCE_EXIT_PATHS = {
  marketplace: '/marketplace',
  ai_agent: '/ai-agent',
  ai_agent_paywall: '/ai-agent',
  paywall_upgrade: '/ai-agent',
  deploy: '/deploy',
};
const TERMS_ACCEPTANCE_SHORT_MESSAGE = 'Please accept the Terms of Service to continue';
const TERMS_ACCEPTANCE_ERROR_MESSAGE = 'Please accept the Terms of Service to continue with your subscription.';

const isTermsAcceptanceErrorMessage = (message) => (
  message === TERMS_ACCEPTANCE_SHORT_MESSAGE ||
  message === TERMS_ACCEPTANCE_ERROR_MESSAGE
);

const getSafeCheckoutExitPath = (value) => {
  if (!value || typeof value !== 'string') return null;

  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://botspot.trade';
    const parsed = new URL(value, origin);
    if (parsed.origin !== origin) return null;
    if (!parsed.pathname.startsWith('/') || parsed.pathname.startsWith('//')) return null;
    if (parsed.pathname === '/checkout' || parsed.pathname.startsWith('/checkout/')) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const loadGooglePayScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Pay requires a browser environment'));
  }
  if (window.google?.payments?.api) {
    return Promise.resolve(window.google);
  }
  if (!googlePayScriptPromise) {
    googlePayScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-google-pay-api="true"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.google), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.async = true;
      script.dataset.googlePayApi = 'true';
      script.onload = () => resolve(window.google);
      script.onerror = () => reject(new Error('Google Pay script failed to load'));
      document.head.appendChild(script);
    });
  }
  return googlePayScriptPromise;
};

const loadApplePayScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Apple Pay requires a browser environment'));
  }
  if (window.customElements?.get?.('apple-pay-button')) {
    return Promise.resolve();
  }
  if (!applePayScriptPromise) {
    applePayScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-apple-pay-js="true"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
      script.async = true;
      script.dataset.applePayJs = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Apple Pay script failed to load'));
      document.head.appendChild(script);
    });
  }
  return applePayScriptPromise;
};

const normalizeSalesSignupV4RouteVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value === 'control' ? 'full_form' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V4_ROUTE_VARIANTS.includes(normalized) ? normalized : null;
};

const normalizeSalesSignupV4PostHogVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value === 'full_form' ? 'control' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V4_POSTHOG_VARIANTS.includes(normalized) ? normalized : null;
};

const normalizeSalesSignupV5RouteVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value === 'control' ? 'email_only' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V5_ROUTE_VARIANTS.includes(normalized) ? normalized : null;
};

const normalizeSalesSignupV5PostHogVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value === 'email_only' ? 'control' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V5_POSTHOG_VARIANTS.includes(normalized) ? normalized : null;
};

const getSalesSignupV4FormVariant = (routeVariant) => (
  routeVariant === 'email_only' ? 'email_only' : 'full_form'
);

const getSalesSignupV4FramingVariant = (routeVariant) => (
  routeVariant === 'auth_first' ? 'auth_first' : 'create_account'
);

const getSalesSignupV4DesignVariant = (routeVariant) => (
  routeVariant === 'auth_first' ? 'login_menu' : 'sales07'
);

export const buildSalesSignupAttribution = (params = {}) => {
  const isV5 =
    params.feature_flag === SALES_SIGNUP_V5_FEATURE_FLAG ||
    params.experiment_name === SALES_SIGNUP_V5_EXPERIMENT_NAME ||
    params.sales_page_signup_framing_v5_experiment_name === SALES_SIGNUP_V5_EXPERIMENT_NAME ||
    Boolean(
      params[`$feature/${SALES_SIGNUP_V5_FEATURE_FLAG}`] ||
      params[`$feature_flag_${SALES_SIGNUP_V5_FEATURE_FLAG}`] ||
      params.sales_page_signup_framing_v5_variant ||
      params.sales_page_signup_framing_v5_route_variant
    );
  const featureFlag = isV5 ? SALES_SIGNUP_V5_FEATURE_FLAG : SALES_SIGNUP_V4_FEATURE_FLAG;
  const experimentName = isV5 ? SALES_SIGNUP_V5_EXPERIMENT_NAME : SALES_SIGNUP_V4_EXPERIMENT_NAME;
  const experimentNameKey = `${experimentName}_experiment_name`;
  const variantKey = `${experimentName}_variant`;
  const routeVariantKey = `${experimentName}_route_variant`;
  const normalizeRouteVariant = isV5
    ? normalizeSalesSignupV5RouteVariant
    : normalizeSalesSignupV4RouteVariant;
  const normalizePostHogVariant = isV5
    ? normalizeSalesSignupV5PostHogVariant
    : normalizeSalesSignupV4PostHogVariant;
  const rawPostHogVariant =
    params[`$feature/${featureFlag}`] ||
    params[`$feature_flag_${featureFlag}`] ||
    params[variantKey] ||
    params.sales_page_signup_framing_variant;
  const rawRouteVariant =
    params[routeVariantKey] ||
    params.sales_page_signup_framing_variant ||
    rawPostHogVariant;
  const routeVariant = normalizeRouteVariant(rawRouteVariant);
  const posthogVariant =
    normalizePostHogVariant(rawPostHogVariant) ||
    normalizePostHogVariant(routeVariant);

  if (!posthogVariant && !routeVariant) {
    return {};
  }

  const resolvedRouteVariant =
    routeVariant ||
    (posthogVariant === 'control' ? (isV5 ? 'email_only' : 'full_form') : posthogVariant);

  return {
    feature_flag: featureFlag,
    experiment_name: experimentName,
    experiment_variant: resolvedRouteVariant,
    [experimentNameKey]: experimentName,
    sales_page_signup_framing_variant: resolvedRouteVariant,
    [variantKey]: posthogVariant || resolvedRouteVariant,
    [routeVariantKey]: resolvedRouteVariant,
    sales_page_form_variant: params.sales_page_form_variant || getSalesSignupV4FormVariant(resolvedRouteVariant),
    sales_page_framing_variant: params.sales_page_framing_variant || getSalesSignupV4FramingVariant(resolvedRouteVariant),
    sales_page_design_variant: params.sales_page_design_variant || getSalesSignupV4DesignVariant(resolvedRouteVariant),
    [`$feature_flag_${featureFlag}`]: posthogVariant || resolvedRouteVariant,
    [`$feature/${featureFlag}`]: posthogVariant || resolvedRouteVariant,
  };
};

const buildPostHogFeatureFlagReplayProps = (featureFlagKey, featureFlagVariant) => {
  const posthogProps = window.posthog?.persistence?.props || {};
  const flagDetails = posthogProps.$feature_flag_details?.[featureFlagKey] || {};
  const flagMetadata = flagDetails.metadata || {};
  const flagReason = flagDetails.reason || {};
  const enabledFeatureFlags = {
    ...(posthogProps.$enabled_feature_flags || {}),
    [featureFlagKey]: featureFlagVariant,
  };
  const activeFeatureFlags = Array.isArray(posthogProps.$active_feature_flags)
    ? [...new Set([...posthogProps.$active_feature_flags, featureFlagKey])]
    : [featureFlagKey];

  return {
    [`$feature/${featureFlagKey}`]: featureFlagVariant,
    $active_feature_flags: activeFeatureFlags,
    $enabled_feature_flags: enabledFeatureFlags,
    ...(flagMetadata.id && { $feature_flag_id: flagMetadata.id }),
    ...(flagMetadata.version && { $feature_flag_version: flagMetadata.version }),
    ...(flagReason.description && { $feature_flag_reason: flagReason.description }),
    ...(posthogProps.$feature_flag_request_id && { $feature_flag_request_id: posthogProps.$feature_flag_request_id }),
    ...(posthogProps.$feature_flag_payloads && { $feature_flag_payloads: posthogProps.$feature_flag_payloads }),
  };
};

const replaySalesSignupExposureAfterIdentify = ({ attribution = {}, utmData = {} }) => {
  const featureFlagKey = attribution.feature_flag || SALES_SIGNUP_V4_FEATURE_FLAG;
  const isV5 = featureFlagKey === SALES_SIGNUP_V5_FEATURE_FLAG;
  const experimentName = isV5 ? SALES_SIGNUP_V5_EXPERIMENT_NAME : SALES_SIGNUP_V4_EXPERIMENT_NAME;
  const variantKey = `${experimentName}_variant`;
  const routeVariantKey = `${experimentName}_route_variant`;
  const normalizeRouteVariant = isV5
    ? normalizeSalesSignupV5RouteVariant
    : normalizeSalesSignupV4RouteVariant;
  const normalizePostHogVariant = isV5
    ? normalizeSalesSignupV5PostHogVariant
    : normalizeSalesSignupV4PostHogVariant;
  const featureFlagVariant = normalizePostHogVariant(
    attribution[`$feature/${featureFlagKey}`] ||
    attribution[`$feature_flag_${featureFlagKey}`] ||
    attribution[variantKey],
  );

  if (!featureFlagVariant) {
    return;
  }

  const routeVariant =
    normalizeRouteVariant(attribution[routeVariantKey]) ||
    (featureFlagVariant === 'control' ? (isV5 ? 'email_only' : 'full_form') : featureFlagVariant);
  const replayContext = {
    ...utmData,
    ...attribution,
    experiment_name: experimentName,
    experiment_variant: routeVariant,
    feature_flag: featureFlagKey,
    sales_page_signup_framing_variant: routeVariant,
    [`${experimentName}_experiment_name`]: experimentName,
    [variantKey]: featureFlagVariant,
    [routeVariantKey]: routeVariant,
    [`$feature_flag_${featureFlagKey}`]: featureFlagVariant,
    [`$feature/${featureFlagKey}`]: featureFlagVariant,
  };
  const replayFlagProps = buildPostHogFeatureFlagReplayProps(featureFlagKey, featureFlagVariant);

  pushEvent({
    ...replayContext,
    ...replayFlagProps,
    event: '$feature_flag_called',
    $feature_flag: featureFlagKey,
    $feature_flag_response: featureFlagVariant,
    variant: featureFlagVariant,
    is_experiment_active: true,
    source: 'starter_checkout_contact_form',
    exposure_replayed_after_identify: true,
  });

  pushEvent({
    ...replayContext,
    event: 'experiment_exposure',
    variant: featureFlagVariant,
    feature_flag: featureFlagKey,
    feature_flag_value: featureFlagVariant,
    is_experiment_active: true,
    source: 'starter_checkout_contact_form',
    exposure_replayed_after_identify: true,
  });
};

const replayCheckoutDesignExposureAfterIdentify = ({
  checkoutDesignVariant,
  checkoutDesignExperimentActive,
  checkoutDesignEventProps = {},
  utmData = {},
}) => {
  if (!checkoutDesignExperimentActive || !CHECKOUT_DESIGN_VARIANTS.includes(checkoutDesignVariant)) {
    return;
  }

  const featureFlagKey = ACTIVE_EXPERIMENTS.CHECKOUT_REDESIGN_2026_07.featureFlag;
  const experimentName = ACTIVE_EXPERIMENTS.CHECKOUT_REDESIGN_2026_07.name;
  const replayFlagProps = buildPostHogFeatureFlagReplayProps(featureFlagKey, checkoutDesignVariant);
  const replayContext = {
    ...utmData,
    ...checkoutDesignEventProps,
    ...replayFlagProps,
    experiment_name: experimentName,
    experiment_variant: checkoutDesignVariant,
    checkout_design_variant: checkoutDesignVariant,
    checkout_design_experiment_name: experimentName,
    checkout_design_feature_flag: featureFlagKey,
  };

  capturePostHogEvent('$feature_flag_called', {
    ...replayContext,
    $feature_flag: featureFlagKey,
    $feature_flag_response: checkoutDesignVariant,
    variant: checkoutDesignVariant,
    is_experiment_active: true,
    source: 'starter_checkout_payment_submission',
    exposure_replayed_after_identify: true,
  });

  capturePostHogEvent('experiment_exposure', {
    ...replayContext,
    variant: checkoutDesignVariant,
    feature_flag: featureFlagKey,
    feature_flag_value: checkoutDesignVariant,
    is_experiment_active: true,
    source: 'starter_checkout_payment_submission',
    exposure_replayed_after_identify: true,
  });
};

// Starter Pricing ABC Test - Variant configurations
// Variants: control (7-day trial), no_trial ($99 upfront), discounttest-2 ($49 with coupon)
// Each variant now supports both monthly and annual billing periods
const VARIANT_CONFIG = {
  control: {
    // Trial config (only applies to monthly)
    trialDays: 7,
    // Monthly pricing
    monthlyPrice: 99,
    monthlyPriceText: '$99/month',
    monthlyPlanKey: 'default',  // Use existing default plan (has 7-day trial)
    // Annual pricing (2 months free = $990/year instead of $1188)
    annualPrice: 990,
    annualPriceText: '$990/year',
    annualMonthlyEquivalent: '$82.50/month',
    annualSavingsText: '2 months free',
    annualPlanKey: 'annual',  // Backend plan key for annual billing
    annualTrialDays: 0,  // No trial for annual - pay upfront for full year
    // Display text (used when monthly is selected - default)
    priceText: 'Only $99/month',
    ctaText: 'Start 7 days before billing',
    annualCtaText: 'Start annual plan - $990/year',
    headerText: 'Test Your Strategy Before Risking Real Capital',
    subheaderText: 'Start today; billing begins after 7 days',
    termsText: '7-day access period before billing starts',
    annualTermsText: 'subscription',
    planKey: 'default',  // Legacy field - use monthlyPlanKey/annualPlanKey instead
    promoCode: null,
    showUrgencyTimer: false,
  },
  no_trial: {
    // No trial for this variant
    trialDays: 0,
    // Monthly pricing
    monthlyPrice: 99,
    monthlyPriceText: '$99/month',
    monthlyPlanKey: 'no_trial',
    // Annual pricing (2 months free)
    annualPrice: 990,
    annualPriceText: '$990/year',
    annualMonthlyEquivalent: '$82.50/month',
    annualSavingsText: '2 months free',
    annualPlanKey: 'annual',  // Annual never has trial, use same plan for all variants
    annualTrialDays: 0,
    // Display text
    priceText: 'Only $99/month',
    ctaText: 'Start monthly plan - $99/month',
    annualCtaText: 'Start annual plan - $990/year',
    headerText: 'Test Your Strategy Before Risking Real Capital',
    subheaderText: 'Get instant access at $99/month',
    termsText: 'subscription',
    annualTermsText: 'subscription',
    planKey: 'no_trial',  // Legacy field
    promoCode: null,
    showUrgencyTimer: false,
  },
  'discounttest-2': {
    // No trial for discount variant
    trialDays: 0,
    // Monthly pricing with 50% discount
    monthlyPrice: 49,  // After coupon
    monthlyPriceText: '$49/month',
    monthlyOriginalPrice: '$99',  // Show strikethrough
    // The control design intentionally keeps its historical rounded copy. The
    // redesign must match the exact 50%-off Stripe charge ($99 -> $49.50).
    redesignMonthlyAmountCents: 4950,
    redesignMonthlyOriginalAmountCents: 9900,
    redesignMonthlyPriceText: '$49.50/month',
    redesignPromotionDuration: 'forever',
    monthlyPlanKey: 'no_trial',
    // Annual pricing with 50% discount (coupon applies to annual too)
    // Original annual: $990, with 50% off: $495
    annualPrice: 495,
    redesignAnnualAmountCents: 49500,
    redesignAnnualOriginalAmountCents: 99000,
    annualPriceText: '$495/year',
    annualOriginalPrice: '$990',
    annualMonthlyEquivalent: '$41.25/month',
    annualSavingsText: '50% off + 2 months free',
    annualPlanKey: 'annual',  // Annual never has trial, use same plan for all variants
    annualTrialDays: 0,
    // Display text
    priceText: 'only $49/month',
    originalPrice: '$99',
    ctaText: 'Claim 50% Off Now',
    annualCtaText: 'Claim 50% Off - $495/year',
    headerText: 'Test Your Strategy Before Risking Real Capital',
    subheaderText: '50% off for a limited time',
    termsText: 'subscription',
    annualTermsText: 'subscription',
    planKey: 'no_trial',  // Legacy field
    promoCode: 'STARTER50OFF',  // Auto-apply coupon - works for both monthly and annual
    showUrgencyTimer: true,
    // Note: urgency banner adds "Was/Now" prefixes, so priceText should include "only"
  },
  // Light Plan variant - Pure price test vs discounttest-2 Starter.
  // Identical UX to discounttest-2 (discount framing, no trial).
  // Only the underlying product + price differ: Light $49 → $24 vs Starter $99 → $49.
  // The control design keeps its historical rounded display. The redesign uses
  // exact cents so its UI, analytics, and Stripe Elements amount all agree.
  // Light includes one deployment, but its AI usage remains materially lower than Starter.
  light: {
    trialDays: 0,
    // Monthly pricing with 50% discount
    monthlyPrice: 24,
    monthlyPriceText: '$24/month',
    monthlyOriginalPrice: '$49',
    redesignMonthlyAmountCents: 2450,
    redesignMonthlyOriginalAmountCents: 4900,
    redesignMonthlyPriceText: '$24.50/month',
    redesignPromotionDuration: 'forever',
    monthlyPlanKey: 'default',
    // No annual plan configured for Light yet — hide the billing toggle.
    annualPlanKey: null,
    annualTrialDays: 0,
    // Display text
    priceText: 'only $24/month',
    originalPrice: '$49',
    ctaText: 'Claim 50% Off Now',
    headerText: 'Build Your Trading Bot With AI',
    subheaderText: '50% off for a limited time',
    termsText: 'subscription',
    planKey: 'default',
    planDisplayName: 'BotSpot Light',
    promoCode: 'LIGHT50OFF',
    showUrgencyTimer: true,
  },
};

// Default to control if variant not found
const getVariantConfig = (variant) => VARIANT_CONFIG[variant] || VARIANT_CONFIG.control;

const planHasDiscount = (plan) => plan?.discountedAmount != null || Boolean(plan?.appliedPromotion);

const findDiscountedPlanForCheckout = (catalog, productSlug, planKey) => {
  const product = catalog?.products?.find((p) => p.slug === productSlug);
  if (!product) return null;

  return (
    product.plans?.find((plan) => plan.planKey === planKey && planHasDiscount(plan)) ||
    product.plans?.find(planHasDiscount) ||
    null
  );
};

const couponStateFromPlan = (code, plan) => ({
  code: code.toUpperCase(),
  description: buildCouponDescription(plan?.appliedPromotion),
  discountedAmount: plan?.discountedAmount,
  originalAmount: plan?.amount,
  promotion: plan?.appliedPromotion || null,
});

const formatCheckoutAmount = (amountCents) => {
  const parsedAmount = Number(amountCents);
  if (!Number.isFinite(parsedAmount)) return null;

  const amount = Math.max(0, parsedAmount) / 100;
  const hasPennies = Math.round(amount * 100) % 100 !== 0;
  return `$${hasPennies ? amount.toFixed(2) : amount.toFixed(0)}`;
};

const getVariantAmountCents = (variantConfig, billingPeriod) => {
  const isAnnual = billingPeriod === 'annual';
  const explicitAmountCents = isAnnual
    ? variantConfig.annualAmountCents
    : variantConfig.monthlyAmountCents;
  if (Number.isFinite(Number(explicitAmountCents))) {
    return Math.max(0, Math.round(Number(explicitAmountCents)));
  }

  const amountDollars = isAnnual ? variantConfig.annualPrice : variantConfig.monthlyPrice;
  return dollarsToCents(amountDollars, isAnnual ? 99000 : 9900);
};

const getVariantOriginalAmountCents = (variantConfig, billingPeriod, fallbackAmountCents) => {
  const explicitAmountCents = billingPeriod === 'annual'
    ? variantConfig.annualOriginalAmountCents
    : variantConfig.monthlyOriginalAmountCents;
  if (Number.isFinite(Number(explicitAmountCents))) {
    return Math.max(0, Math.round(Number(explicitAmountCents)));
  }
  return fallbackAmountCents;
};

const getCheckoutMonetaryEventProps = ({
  variantConfig,
  billingPeriod,
  stripeMode,
  appliedCoupon,
  promotionDuration,
  termsText,
  isCheckoutRedesign,
}) => {
  const planAmountCents = getVariantAmountCents(variantConfig, billingPeriod);
  const configuredOriginalAmountCents = getVariantOriginalAmountCents(
    variantConfig,
    billingPeriod,
    planAmountCents,
  );
  const originalAmountCents = appliedCoupon?.originalAmount ?? configuredOriginalAmountCents;
  const chargeAmountCents = Math.max(0, appliedCoupon?.discountedAmount ?? planAmountCents);
  const amountDueTodayCents = Math.max(0, stripeMode === 'setup' ? 0 : chargeAmountCents);
  const discountAmountCents = Math.max(0, originalAmountCents - chargeAmountCents);
  const isRecurringPlan = variantConfig.setupFutureUsage !== false && termsText !== 'purchase';
  const effectivePromotionDuration = appliedCoupon?.promotion?.duration || promotionDuration || null;
  const futureRecurringAmountCents = !isRecurringPlan
    ? 0
    : effectivePromotionDuration === 'forever'
      ? chargeAmountCents
      : appliedCoupon || effectivePromotionDuration
        ? originalAmountCents
        : chargeAmountCents;
  const potentialRevenueCents = isCheckoutRedesign ? chargeAmountCents : originalAmountCents;

  return {
    currency: 'USD',
    checkout_currency: 'USD',
    checkout_plan_amount_cents: planAmountCents,
    checkout_original_amount_cents: originalAmountCents,
    checkout_amount_due_today_cents: amountDueTodayCents,
    checkout_amount_due_today_usd: centsToUsd(amountDueTodayCents),
    checkout_discount_amount_cents: discountAmountCents,
    checkout_discount_amount_usd: centsToUsd(discountAmountCents),
    checkout_potential_revenue_cents: potentialRevenueCents,
    checkout_potential_revenue_usd: centsToUsd(potentialRevenueCents),
    checkout_initial_charge_cents: amountDueTodayCents,
    checkout_future_recurring_amount_cents: isCheckoutRedesign
      ? futureRecurringAmountCents
      : originalAmountCents,
    checkout_has_initial_charge: amountDueTodayCents > 0,
    checkout_is_setup_mode: stripeMode === 'setup',
    checkout_revenue_tracking_basis: stripeMode === 'setup'
      ? 'payment_method_setup_then_future_subscription'
      : 'initial_payment',
  };
};

const resolveRedesignExactPricing = (variantConfig) => {
  const amountCents = Number(variantConfig?.redesignMonthlyAmountCents);
  if (!Number.isFinite(amountCents)) return variantConfig;

  const exactAmountCents = Math.max(0, Math.round(amountCents));
  const annualAmountCents = Number(variantConfig.redesignAnnualAmountCents);
  const hasExactAnnualAmount = Number.isFinite(annualAmountCents);
  const exactPriceText = variantConfig.redesignMonthlyPriceText ||
    `${formatCheckoutAmount(exactAmountCents)}/month`;

  return {
    ...variantConfig,
    monthlyAmountCents: exactAmountCents,
    monthlyOriginalAmountCents:
      variantConfig.redesignMonthlyOriginalAmountCents ?? variantConfig.monthlyOriginalAmountCents,
    monthlyPrice: exactAmountCents / 100,
    monthlyPriceText: exactPriceText,
    priceText: exactPriceText,
    promotionDuration: variantConfig.redesignPromotionDuration || variantConfig.promotionDuration,
    ...(hasExactAnnualAmount ? {
      annualAmountCents: Math.max(0, Math.round(annualAmountCents)),
      annualOriginalAmountCents:
        variantConfig.redesignAnnualOriginalAmountCents ?? variantConfig.annualOriginalAmountCents,
    } : {}),
  };
};

// Collapsible promo code input (mirrors CouponCodeSection from CheckoutPage)
function CouponCodeInput({
  onApply,
  onClearValidationError,
  isValidating,
  validationError,
  disabled,
  variant = 'default',
}) {
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const isCompact = variant === 'compact';
  const errorId = isCompact ? 'checkout-redesign-promo-error' : 'checkout-promo-error';
  const couponInputHints = {
    autoComplete: 'nope',
    autoCorrect: 'off',
    autoCapitalize: 'characters',
    spellCheck: false,
    inputMode: 'text',
    name: 'checkout-discount',
    'data-1p-ignore': 'true',
    'data-lpignore': 'true',
    'data-form-type': 'other',
  };

  const handleApplyClick = () => {
    if (inputValue.trim() && !isValidating && !disabled) {
      onApply(inputValue.trim().toUpperCase());
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (validationError) {
      onClearValidationError?.();
    }
  };

  const handleCancel = () => {
    setIsExpanded(false);
    setInputValue('');
    if (validationError) {
      onClearValidationError?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyClick();
    }
  };

  useEffect(() => {
    if (!isExpanded) return undefined;
    const focusId = window.requestAnimationFrame(() => {
      try {
        inputRef.current?.focus({ preventScroll: true });
      } catch {
        inputRef.current?.focus();
      }
    });
    return () => window.cancelAnimationFrame(focusId);
  }, [isExpanded]);

  if (!isExpanded) {
    return (
      <button
        type="button"
        className={`coupon-code-toggle${isCompact ? ' coupon-code-toggle--compact' : ''}`}
        onClick={() => setIsExpanded(true)}
        disabled={disabled}
        aria-label={isCompact ? 'Add promo code' : undefined}
      >
        {!isCompact && <FaTag aria-hidden="true" />}
        {isCompact ? (
          <>
            <span className="coupon-code-toggle__icon" aria-hidden="true">
              <FaTag />
            </span>
            <span className="coupon-code-toggle__copy">
              <span className="coupon-code-toggle__label">Promo code</span>
              <span className="coupon-code-toggle__hint">Apply a promo code</span>
            </span>
            <span className="coupon-code-toggle__action" aria-hidden="true">Add</span>
          </>
        ) : (
          'Have a promo code?'
        )}
      </button>
    );
  }

  if (isCompact) {
    return (
      <div className="coupon-code-form coupon-code-form--compact">
        <div className="coupon-code-form__input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className={`coupon-code-form__input${validationError ? ' coupon-code-form__input--error' : ''}`}
            placeholder="Enter promo code"
            aria-label="Promo code"
            aria-invalid={Boolean(validationError)}
            aria-describedby={validationError ? errorId : undefined}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={disabled || isValidating}
            {...couponInputHints}
          />
          <button
            type="button"
            className="coupon-code-form__submit"
            onClick={handleApplyClick}
            disabled={!inputValue.trim() || isValidating || disabled}
            aria-label={isValidating ? 'Applying promo code' : 'Apply promo code'}
            aria-busy={isValidating}
          >
            {isValidating ? (
              <span>Applying</span>
            ) : (
              <span>Apply</span>
            )}
          </button>
          <button
            type="button"
            className="coupon-code-form__cancel"
            onClick={handleCancel}
            disabled={isValidating}
            aria-label="Cancel promo code"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        {validationError && (
          <div id={errorId} className="coupon-code-form__error" role="alert">{validationError}</div>
        )}
      </div>
    );
  }

  return (
    <div className="coupon-code-form">
      <div className="coupon-code-form__input-wrapper">
        <input
          type="text"
          className={`coupon-code-form__input${validationError ? ' coupon-code-form__input--error' : ''}`}
          placeholder="Enter promo code"
          aria-label="Promo code"
          aria-invalid={Boolean(validationError)}
          aria-describedby={validationError ? errorId : undefined}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isValidating}
          {...couponInputHints}
        />
        <button
          type="button"
          className="coupon-code-form__submit"
          onClick={handleApplyClick}
          disabled={!inputValue.trim() || isValidating || disabled}
          aria-label={isValidating ? 'Applying promo code' : 'Apply promo code'}
          aria-busy={isValidating}
        >
          {isValidating ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <FaCheck aria-hidden="true" />
          )}
        </button>
      </div>
      {validationError && (
        <div id={errorId} className="coupon-code-form__error" role="alert">{validationError}</div>
      )}
      <button
        type="button"
        className="coupon-code-form__cancel"
        onClick={handleCancel}
        disabled={isValidating}
      >
        Cancel
      </button>
    </div>
  );
}

const buildTermsPayload = () => {
  const timestamp = new Date().toISOString();
  return {
    docType: TERMS_DOC_TYPE,
    version: timestamp.split('T')[0],
    acceptedAt: timestamp,
    hash: null,
  };
};

/**
 * StarterCheckoutForm - Inner form component rendered within Stripe Elements.
 *
 * IMPORTANT: This component receives billingPeriod and onBillingPeriodChange as props
 * because the billing period affects which Stripe mode (setup vs payment) is used.
 * The mode must be known BEFORE Elements initialization, so state is lifted to parent.
 *
 * @param {string} billingPeriod - 'monthly' or 'annual' (controlled by parent)
 * @param {function} onBillingPeriodChange - Callback to change billing period
 * @param {string} stripeMode - 'setup' or 'payment' (determined by parent based on trial days)
 */
function StarterCheckoutForm({
  productSlug,
  initialUserInfo,
  variantConfig,
  experimentVariant,
  checkoutDesignVariant = 'control',
  checkoutDesignExperimentActive = false,
  checkoutTheme = 'dark',
  billingPeriod,
  onBillingPeriodChange,
  stripeMode,
  appliedCoupon,
  onAppliedCouponChange,
  savedMethods,
  checkoutUser,
  checkoutStartedAtMs,
  checkoutRecoveryRequired = false,
  onCheckoutRecoveryRequired,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshPaymentStatus } = usePaymentStatus();
  const { isAuthenticated, user, getApiToken, handleCustomJwtAuth } = checkoutUser;
  const authenticatedAccountEmail = String(
    checkoutUser?.userProfile?.email || user?.email || '',
  ).trim().toLowerCase();
  const isCheckoutRedesign = checkoutDesignVariant === 'test';
  const [localCheckoutRecoveryRequired, setLocalCheckoutRecoveryRequired] = useState(false);
  const checkoutRecoveryBlocked =
    isCheckoutRedesign && (checkoutRecoveryRequired || localCheckoutRecoveryRequired);

  const [contactData, setContactData] = useState(() => ({
    name: initialUserInfo?.name || '',
    email: initialUserInfo?.email || '',
    phone: initialUserInfo?.phone || '',
    howFoundUs: initialUserInfo?.howFoundUs || '',
    howFoundUsOtherText: initialUserInfo?.howFoundUsOtherText || '',
  }));

  useEffect(() => {
    setContactData((prev) => ({
      name: prev.name || initialUserInfo?.name || '',
      email: prev.email || initialUserInfo?.email || '',
      phone: prev.phone || initialUserInfo?.phone || '',
      howFoundUs: prev.howFoundUs || initialUserInfo?.howFoundUs || '',
      howFoundUsOtherText: prev.howFoundUsOtherText || initialUserInfo?.howFoundUsOtherText || '',
    }));
  }, [
    initialUserInfo?.name,
    initialUserInfo?.email,
    initialUserInfo?.phone,
    initialUserInfo?.howFoundUs,
    initialUserInfo?.howFoundUsOtherText,
  ]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const lastValidationErrorRef = useRef('');
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [expressCheckoutAvailable, setExpressCheckoutAvailable] = useState(
    false,
  );
  const [expressCheckoutChecked, setExpressCheckoutChecked] = useState(
    checkoutDesignVariant === 'test',
  );
  const [walletPaymentRequest, setWalletPaymentRequest] = useState(null);
  const [availableWalletMethods, setAvailableWalletMethods] = useState(
    [],
  );
  const [linkCheckoutExpected, setLinkCheckoutExpected] = useState(
    false,
  );
  const [linkCheckoutAvailable, setLinkCheckoutAvailable] = useState(
    false,
  );
  const [paymentMethodAvailability, setPaymentMethodAvailability] = useState(null);
  const [paymentMethodAvailabilityChecked, setPaymentMethodAvailabilityChecked] = useState(
    checkoutDesignVariant === 'test',
  );
  const [paymentMethodAvailabilityError, setPaymentMethodAvailabilityError] = useState('');
  const [, setGooglePayButtonReady] = useState(false);
  const [, setGooglePayButtonFailed] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    checkoutDesignVariant === 'test' ? REDESIGN_STRIPE_PAYMENT_ELEMENT_METHOD : 'wallet',
  );
  const [selectedSavedPaymentMethodId, setSelectedSavedPaymentMethodId] = useState(null);
  const bankVerificationMethod = BANK_VERIFICATION_METHOD;
  const [manualPaymentMethodSelected, setManualPaymentMethodSelected] = useState(false);
  const [dynamicPaymentElementMounted, setDynamicPaymentElementMounted] = useState(false);
  const [dynamicPaymentElementReady, setDynamicPaymentElementReady] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponValidationError, setCouponValidationError] = useState('');
  const buttonRef = useRef(null);
  const checkoutMountedAtMsRef = useRef(checkoutStartedAtMs || Date.now());
  const checkoutPageLoadedTrackedRef = useRef(false);
  const checkoutReadyTrackedRef = useRef(false);
  const paymentElementReadyTrackedRef = useRef(false);
  const paymentElementCompleteTrackedRef = useRef(false);
  const expressCheckoutAvailableTrackedRef = useRef(false);
  const walletAvailabilityTimeoutTrackedRef = useRef(false);
  const activeWalletTypeRef = useRef(null);
  const cardPaymentSelectedByUserRef = useRef(false);
  const manualPaymentMethodSelectedRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const googlePayWalletClickRef = useRef(null);
  const trackCheckoutDiagnosticRef = useRef(null);
  const googlePayButtonContainerRef = useRef(null);
  const paymentElementValidationErrorRef = useRef('');
  const checkoutExperimentName = ACTIVE_EXPERIMENTS.LIGHT_VS_STARTER_CHECKOUT.name;
  const checkoutDesignExperimentName = ACTIVE_EXPERIMENTS.CHECKOUT_REDESIGN_2026_07.name;
  const checkoutDesignFeatureFlag = ACTIVE_EXPERIMENTS.CHECKOUT_REDESIGN_2026_07.featureFlag;
  const checkoutDesignEventProps = useMemo(() => ({
    checkout_design_variant: checkoutDesignVariant,
    checkout_design_experiment_name: checkoutDesignExperimentName,
    checkout_design_feature_flag: checkoutDesignFeatureFlag,
    checkout_design_experiment_active: Boolean(checkoutDesignExperimentActive),
    [`$feature/${checkoutDesignFeatureFlag}`]: checkoutDesignVariant,
    [`$feature_flag_${checkoutDesignFeatureFlag}`]: checkoutDesignVariant,
  }), [
    checkoutDesignExperimentActive,
    checkoutDesignExperimentName,
    checkoutDesignFeatureFlag,
    checkoutDesignVariant,
  ]);

  // Use variant config values - extract both monthly and annual pricing
  const {
    // Trial (only applies to monthly)
    trialDays: monthlyTrialDays,
    annualTrialDays = 0,
    // Monthly pricing
    monthlyPriceText,
    monthlyOriginalPrice,
    monthlyPlanKey,
    // Annual pricing
    annualPriceText,
    annualOriginalPrice,
    annualMonthlyEquivalent,
    annualSavingsText,
    annualPlanKey,
    // Display text
    priceText: defaultPriceText,
    originalPrice: defaultOriginalPrice,
    ctaText: monthlyCtaText,
    annualCtaText,
    termsText: monthlyTermsText,
    annualTermsText,
    // Legacy planKey for backwards compatibility
    planKey: legacyPlanKey,
    promoCode,
    promotionDuration,
    showUrgencyTimer,
  } = variantConfig;

  // Compute values based on selected billing period
  const isAnnual = billingPeriod === 'annual';
  const trialDays = isAnnual ? annualTrialDays : monthlyTrialDays;
  const priceText = isAnnual ? annualPriceText : (monthlyPriceText || defaultPriceText);
  const originalPrice = isAnnual ? annualOriginalPrice : (monthlyOriginalPrice || defaultOriginalPrice);
  const ctaText = isAnnual ? (annualCtaText || monthlyCtaText) : monthlyCtaText;
  const termsText = isCheckoutRedesign
    ? monthlyTermsText
    : (isAnnual ? (annualTermsText || monthlyTermsText) : monthlyTermsText);
  const planKey = isAnnual ? annualPlanKey : (monthlyPlanKey || legacyPlanKey);
  const planDisplayName = variantConfig.planDisplayName || (
    experimentVariant === 'light' || productSlug === 'light-plan'
      ? 'BotSpot Light'
      : 'Starter Plan'
  );
  const redesignPlanDescription = variantConfig.redesignPlanDescription || (
    experimentVariant === 'light' || productSlug === 'light-plan'
      ? 'AI Agent access for building strategies, backtesting, paper trading, and one live bot.'
      : 'Build, backtest, and launch trading bots with AI.'
  );
  const configuredPlanAmountCents = useMemo(
    () => getVariantAmountCents(variantConfig, billingPeriod),
    [billingPeriod, variantConfig],
  );
  const elementsAmountCents = stripeMode === 'payment'
    ? (appliedCoupon?.discountedAmount ?? configuredPlanAmountCents)
    : null;
  const elementsContextKey = `${stripeMode}:${elementsAmountCents ?? 'setup'}`;
  const confirmedElementsContextKeyRef = useRef(elementsContextKey);
  const pendingElementsContextKeyRef = useRef(elementsContextKey);
  const [, setElementsUpdateVersion] = useState(0);
  pendingElementsContextKeyRef.current = elementsContextKey;
  if (!elements) {
    confirmedElementsContextKeyRef.current = elementsContextKey;
  }
  const elementsUpdating = Boolean(
    isCheckoutRedesign &&
    elements &&
    confirmedElementsContextKeyRef.current !== elementsContextKey
  );

  useEffect(() => {
    if (!isCheckoutRedesign || !elements?.on) return undefined;

    let isActive = true;
    elements.on('update-end', () => {
      if (!isActive) return;
      confirmedElementsContextKeyRef.current = pendingElementsContextKeyRef.current;
      setElementsUpdateVersion((version) => version + 1);
    });

    return () => {
      isActive = false;
    };
  }, [elements, isCheckoutRedesign]);

  const configuredOriginalAmountCents = useMemo(
    () => getVariantOriginalAmountCents(variantConfig, billingPeriod, configuredPlanAmountCents),
    [billingPeriod, configuredPlanAmountCents, variantConfig],
  );
  const checkoutOriginalAmountCents = appliedCoupon?.originalAmount ?? configuredOriginalAmountCents;
  const checkoutChargeAmountCents = Math.max(
    0,
    appliedCoupon?.discountedAmount ?? configuredPlanAmountCents,
  );
  const checkoutAmountDueTodayCents = Math.max(
    0,
    isCheckoutRedesign
      ? stripeMode === 'setup' ? 0 : checkoutChargeAmountCents
      : appliedCoupon?.discountedAmount ?? (
        stripeMode === 'setup' ? 0 : configuredPlanAmountCents
      ),
  );
  const checkoutDiscountAmountCents = Math.max(
    0,
    checkoutOriginalAmountCents - checkoutChargeAmountCents,
  );
  const isRecurringPlan = variantConfig.setupFutureUsage !== false && termsText !== 'purchase';
  const effectivePromotionDuration =
    appliedCoupon?.promotion?.duration || promotionDuration || null;
  const effectivePromotionDurationInMonths =
    appliedCoupon?.promotion?.durationInMonths || variantConfig.promotionDurationInMonths || null;
  const checkoutFutureRecurringAmountCents = !isRecurringPlan
    ? 0
    : effectivePromotionDuration === 'forever'
      ? checkoutChargeAmountCents
      : appliedCoupon || effectivePromotionDuration
        ? checkoutOriginalAmountCents
        : checkoutChargeAmountCents;
  const checkoutMonetaryEventProps = useMemo(() => ({
    currency: 'USD',
    checkout_currency: 'USD',
    checkout_plan_amount_cents: configuredPlanAmountCents,
    checkout_original_amount_cents: checkoutOriginalAmountCents,
    checkout_amount_due_today_cents: checkoutAmountDueTodayCents,
    checkout_amount_due_today_usd: centsToUsd(checkoutAmountDueTodayCents),
    checkout_discount_amount_cents: checkoutDiscountAmountCents,
    checkout_discount_amount_usd: centsToUsd(checkoutDiscountAmountCents),
    checkout_potential_revenue_cents: isCheckoutRedesign
      ? checkoutChargeAmountCents
      : checkoutOriginalAmountCents,
    checkout_potential_revenue_usd: centsToUsd(
      isCheckoutRedesign ? checkoutChargeAmountCents : checkoutOriginalAmountCents,
    ),
    checkout_initial_charge_cents: checkoutAmountDueTodayCents,
    checkout_future_recurring_amount_cents: isCheckoutRedesign
      ? checkoutFutureRecurringAmountCents
      : checkoutOriginalAmountCents,
    checkout_has_initial_charge: checkoutAmountDueTodayCents > 0,
    checkout_is_setup_mode: stripeMode === 'setup',
    checkout_revenue_tracking_basis: stripeMode === 'setup'
      ? 'payment_method_setup_then_future_subscription'
      : 'initial_payment',
  }), [
    checkoutAmountDueTodayCents,
    checkoutChargeAmountCents,
    checkoutDiscountAmountCents,
    checkoutFutureRecurringAmountCents,
    checkoutOriginalAmountCents,
    configuredPlanAmountCents,
    isCheckoutRedesign,
    stripeMode,
  ]);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectParam = queryParams.get('redirect');
  const leadTransactionId = queryParams.get('leadId') || null;
  const promptPrefillParam = queryParams.get('promptPrefill') || '';
  const variantParam = queryParams.get('variant') || experimentVariant;
  const salesPageExperimentParams = useMemo(() => (
    SALES_PAGE_EXPERIMENT_PARAM_KEYS.reduce((props, key) => {
      const value = queryParams.get(key);
      if (value) {
        props[key] = value;
      }
      return props;
    }, {})
  ), [queryParams]);
  const salesSignupV4Attribution = useMemo(
    () => buildSalesSignupAttribution(salesPageExperimentParams),
    [salesPageExperimentParams],
  );
  const couponParam = queryParams.get('coupon') || promoCode; // Use variant promoCode if no URL param
  // Cross-funnel handoff: the marketplace CTA guard passes successUrl when a user
  // clicks "Start My Strategy" unpaid, pointing back to the marketplace detail
  // flow. Without this, every completed checkout lands on /ai-agent regardless
  // of where the user started, stranding marketplace-origin buyers on the wrong
  // page. When source=marketplace, we prefer this URL over the /ai-agent default.
  const successUrlParam = queryParams.get('successUrl');
  const checkoutSourceParam = queryParams.get('source');

  const promptFromSession = useMemo(() => loadPromptFromSession(), []);
  const activePrompt = promptFromSession || promptPrefillParam;
  const checkoutNextStep = useMemo(() => {
    if (checkoutSourceParam === 'marketplace') {
      return {
        title: 'Return to the strategy you picked',
        primary: 'After payment, BotSpot sends you back to the marketplace strategy you chose.',
        secondary: 'Start in paper trading first, review the bot settings, then decide if you want to connect a broker.',
      };
    }

    if (activePrompt) {
      return {
        title: 'Continue from your AI prompt',
        primary: 'After payment, BotSpot opens the AI Agent with the trading idea you already submitted.',
        secondary: 'You can review the generated logic, run tests, and start with paper trading before using real money.',
      };
    }

    if (redirectParam === 'starter_plan') {
      return {
        title: 'Go straight to the AI Agent',
        primary: 'After payment, BotSpot opens the AI Agent so you can build and test your trading workflow.',
        secondary: 'Start with paper trading, watch the logs, and stop any bot at any time.',
      };
    }

    return {
      title: 'Your BotSpot account opens instantly',
      primary: 'After payment, you go to the AI Agent to build and test your trading workflow.',
      secondary: 'Start with paper trading, connect a broker when ready, and stop any bot at any time.',
    };
  }, [activePrompt, checkoutSourceParam, redirectParam]);
  const redesignCheckoutNote = useMemo(() => {
    if (checkoutSourceParam === 'marketplace') {
      return 'Software, not investment advice.';
    }

    if (activePrompt) {
      return 'Software, not investment advice.';
    }

    return variantConfig.redesignCheckoutNote || 'Software, not investment advice.';
  }, [activePrompt, checkoutSourceParam, variantConfig.redesignCheckoutNote]);

  const utmData = useMemo(() => getUtmParamsForApi(location.search), [location.search]);
  const attributionParams = useMemo(() => getAttributionParams(utmData), [utmData]);

  // Coupon code handlers
  const handleApplyCoupon = useCallback(async (code) => {
    if (!code || isValidatingCoupon) return;
    setIsValidatingCoupon(true);
    setCouponValidationError('');
    try {
      const token = isAuthenticated ? await getApiToken() : null;
      const catalog = await validatePromoCode(code, token);
      const discountedPlan = findDiscountedPlanForCheckout(catalog, productSlug, planKey);
      if (discountedPlan) {
        onAppliedCouponChange(couponStateFromPlan(code, discountedPlan));
      } else {
        setCouponValidationError(
          catalog?.promotionCodeValidation?.message || 'This promo code is invalid or has expired.'
        );
      }
    } catch (err) {
      setCouponValidationError('This promo code is invalid or has expired.');
    } finally {
      setIsValidatingCoupon(false);
    }
  }, [isAuthenticated, getApiToken, isValidatingCoupon, onAppliedCouponChange, planKey, productSlug]);

  const handleRemoveCoupon = useCallback(() => {
    onAppliedCouponChange(null);
    setCouponValidationError('');
  }, [onAppliedCouponChange]);

  // Effective coupon: manually entered takes priority over auto-apply variant coupon
  const effectiveCoupon = appliedCoupon?.code || couponParam;

  const checkoutAnalyticsBase = useMemo(() => ({
    source: location.pathname,
    page_path: location.pathname,
    checkout_type: 'landing_checkout_page',
    flow_context: 'sales_ai_landing_checkout',
    plan_type: productSlug || 'starter-plan',
    product_slug: productSlug || 'starter-plan',
    plan_key: planKey,
    billing_period: billingPeriod,
    stripe_mode: stripeMode,
    experiment_variant: experimentVariant,
    experiment_name: checkoutExperimentName,
    ...checkoutDesignEventProps,
    lead_transaction_id: leadTransactionId,
    checkout_source: checkoutSourceParam || null,
    redirect_param: redirectParam || null,
    success_url_present: Boolean(successUrlParam),
    prompt_provided: Boolean(activePrompt),
    has_coupon: Boolean(effectiveCoupon),
    coupon_code: effectiveCoupon || null,
    how_found_us: contactData.howFoundUs || null,
    how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
    ...checkoutMonetaryEventProps,
    ...salesPageExperimentParams,
    ...salesSignupV4Attribution,
    ...utmData,
  }), [
    activePrompt,
    billingPeriod,
    checkoutDesignEventProps,
    checkoutExperimentName,
    checkoutMonetaryEventProps,
    checkoutSourceParam,
    contactData.howFoundUs,
    contactData.howFoundUsOtherText,
    effectiveCoupon,
    experimentVariant,
    leadTransactionId,
    location.pathname,
    planKey,
    productSlug,
    redirectParam,
    salesPageExperimentParams,
    salesSignupV4Attribution,
    stripeMode,
    successUrlParam,
    utmData,
  ]);

  const trackCheckoutDiagnostic = useCallback((event, properties = {}) => {
    pushEvent({
      ...checkoutAnalyticsBase,
      event,
      ...(isCheckoutRedesign ? {
        checkout_elapsed_ms: Math.max(0, Date.now() - checkoutMountedAtMsRef.current),
      } : {}),
      ...properties,
    });
  }, [checkoutAnalyticsBase, isCheckoutRedesign]);

  useEffect(() => {
    trackCheckoutDiagnosticRef.current = trackCheckoutDiagnostic;
  }, [trackCheckoutDiagnostic]);

  useEffect(() => {
    if (isCheckoutRedesign) return undefined;

    setPaymentMethodAvailability(null);
    setPaymentMethodAvailabilityChecked(true);
    setPaymentMethodAvailabilityError('');
    return undefined;
  }, [isCheckoutRedesign]);

  const savedPaymentMethods = useMemo(
    () => savedMethods?.paymentMethods || [],
    [savedMethods?.paymentMethods],
  );
  const hasSavedPaymentMethods = Boolean(savedMethods?.hasMethods && savedPaymentMethods.length > 0);
  const selectedSavedPaymentMethod = useMemo(
    () => savedPaymentMethods.find((method) => method.id === selectedSavedPaymentMethodId) || null,
    [savedPaymentMethods, selectedSavedPaymentMethodId],
  );
  const preferredSavedPaymentMethod = useMemo(
    () => savedPaymentMethods.find((method) => method.isDefault) || savedPaymentMethods[0] || null,
    [savedPaymentMethods],
  );
  const postWalletFallbackPaymentMethod = hasSavedPaymentMethods ? 'saved' : 'bank';
  const unavailableExpressFallbackPaymentMethod = isCheckoutRedesign ? null : postWalletFallbackPaymentMethod;
  const unavailableExpressFallbackIsManual = Boolean(
    unavailableExpressFallbackPaymentMethod && unavailableExpressFallbackPaymentMethod !== 'wallet',
  );

  useEffect(() => {
    if (!hasSavedPaymentMethods) {
      if (selectedSavedPaymentMethodId) {
        setSelectedSavedPaymentMethodId(null);
      }
      if (selectedPaymentMethod === 'saved') {
        setSelectedPaymentMethod(isCheckoutRedesign ? null : 'card');
      }
      return;
    }

    if (!selectedSavedPaymentMethod || !savedPaymentMethods.some((method) => method.id === selectedSavedPaymentMethodId)) {
      setSelectedSavedPaymentMethodId(preferredSavedPaymentMethod?.id || null);
    }
  }, [
    hasSavedPaymentMethods,
    isCheckoutRedesign,
    preferredSavedPaymentMethod?.id,
    savedPaymentMethods,
    selectedPaymentMethod,
    selectedSavedPaymentMethod,
    selectedSavedPaymentMethodId,
  ]);

  useEffect(() => {
    if (!isCheckoutRedesign || expressCheckoutChecked) return undefined;
    const fallbackTimer = window.setTimeout(() => {
      setExpressCheckoutChecked(true);
      setExpressCheckoutAvailable(false);
      setAvailableWalletMethods([]);
      setGooglePayButtonReady(false);
      setGooglePayButtonFailed(false);
      updateCheckoutExpressAvailabilityCache({
        expressCheckoutAvailable: false,
        expressCheckoutChecked: true,
        availableWalletMethods: [],
        linkCheckoutExpected: true,
      });
      if (!walletAvailabilityTimeoutTrackedRef.current) {
        walletAvailabilityTimeoutTrackedRef.current = true;
        trackCheckoutDiagnosticRef.current?.('wallet_availability_timed_out');
      }
    }, 2000);
    return () => window.clearTimeout(fallbackTimer);
  }, [expressCheckoutChecked, isCheckoutRedesign]);

  useEffect(() => {
    if (
      !isCheckoutRedesign ||
      !expressCheckoutChecked ||
      !linkCheckoutExpected ||
      linkCheckoutAvailable ||
      availableWalletMethods.length > 0
    ) {
      return undefined;
    }

    const fallbackTimer = window.setTimeout(() => {
      setLinkCheckoutExpected(false);
      setLinkCheckoutAvailable(false);
      setSelectedPaymentMethod((currentMethod) => (
        currentMethod === 'wallet' ? unavailableExpressFallbackPaymentMethod : currentMethod
      ));
      setManualPaymentMethodSelected(unavailableExpressFallbackIsManual);
      manualPaymentMethodSelectedRef.current = unavailableExpressFallbackIsManual;
      updateCheckoutExpressAvailabilityCache({
        expressCheckoutAvailable: false,
        expressCheckoutChecked: true,
        availableWalletMethods: [],
        linkCheckoutExpected: false,
        linkCheckoutAvailable: false,
      });
      trackCheckoutDiagnosticRef.current?.('link_availability_timed_out');
    }, 1600);

    return () => window.clearTimeout(fallbackTimer);
  }, [
    availableWalletMethods.length,
    expressCheckoutChecked,
    isCheckoutRedesign,
    linkCheckoutAvailable,
    linkCheckoutExpected,
    unavailableExpressFallbackIsManual,
    unavailableExpressFallbackPaymentMethod,
  ]);

  const requiresHowFoundUs = !isCheckoutRedesign;
  const howFoundUsComplete =
    !requiresHowFoundUs ||
    (
      contactData.howFoundUs &&
      (contactData.howFoundUs !== 'other' || contactData.howFoundUsOtherText?.trim())
    );
  const hasPrefilledHowFoundUs =
    initialUserInfo?.howFoundUs &&
    (initialUserInfo.howFoundUs !== 'other' || initialUserInfo.howFoundUsOtherText?.trim());

  const contactComplete =
    contactData.name.trim() &&
    contactData.email.trim() &&
    contactData.phone.trim() &&
    howFoundUsComplete;
  const contactWasPrefilledOnLoad = Boolean(
    initialUserInfo?.name && initialUserInfo?.email && initialUserInfo?.phone,
  );

  useEffect(() => {
    if (checkoutPageLoadedTrackedRef.current) return;
    checkoutPageLoadedTrackedRef.current = true;
    trackCheckoutDiagnostic('checkout_page_loaded', {
      user_type: isAuthenticated ? 'registered' : 'guest',
      has_prefilled_contact: Boolean(initialUserInfo?.email || initialUserInfo?.phone),
      has_prefilled_how_found_us: Boolean(hasPrefilledHowFoundUs),
    });
  }, [
    hasPrefilledHowFoundUs,
    initialUserInfo?.email,
    initialUserInfo?.phone,
    isAuthenticated,
    trackCheckoutDiagnostic,
  ]);

  useEffect(() => {
    if (checkoutReadyTrackedRef.current) return;
    const resolvedPaymentMethod =
      selectedPaymentMethod === 'saved'
        ? 'saved_card'
        :
      selectedPaymentMethod === 'bank'
        ? 'bank'
        : isCheckoutRedesign && isRedesignDynamicPaymentMethod(selectedPaymentMethod)
        ? selectedPaymentMethod
        : isCheckoutRedesign && !selectedPaymentMethod
        ? null
        : expressCheckoutAvailable || !expressCheckoutChecked
          ? selectedPaymentMethod
          : 'card';
    const selectedPaymentReady =
      (resolvedPaymentMethod === 'wallet' && expressCheckoutAvailable) ||
      (resolvedPaymentMethod === 'saved_card' && Boolean(selectedSavedPaymentMethodId)) ||
      (resolvedPaymentMethod === 'card' && paymentElementReady) ||
      (resolvedPaymentMethod === 'bank' && Boolean(stripe)) ||
      (isRedesignDynamicPaymentMethod(resolvedPaymentMethod) && dynamicPaymentElementReady);
    if (!resolvedPaymentMethod || !contactComplete || !termsAccepted || !selectedPaymentReady || !stripe || !elements) return;
    checkoutReadyTrackedRef.current = true;
    trackCheckoutDiagnostic('checkout_form_ready', {
      user_type: isAuthenticated ? 'registered' : 'guest',
      payment_source: resolvedPaymentMethod,
    });
  }, [
    contactComplete,
    elements,
    expressCheckoutAvailable,
    expressCheckoutChecked,
    dynamicPaymentElementReady,
    isAuthenticated,
    isCheckoutRedesign,
    paymentElementReady,
    selectedPaymentMethod,
    selectedSavedPaymentMethodId,
    stripe,
    termsAccepted,
    trackCheckoutDiagnostic,
  ]);

  // Keep checkout validation observable without changing which fields are required.
  const getValidationState = useCallback(() => {
    const hasName = Boolean(contactData.name.trim());
    const hasEmail = Boolean(contactData.email.trim());
    const hasPhone = Boolean(contactData.phone.trim());
    const hasHowFoundUs = Boolean(contactData.howFoundUs);
    const hasHowFoundUsDetails = Boolean(contactData.howFoundUsOtherText?.trim());
    const usesDynamicPaymentElement = isCheckoutRedesign && isRedesignDynamicPaymentMethod(selectedPaymentMethod);
    const usesBankPayment = selectedPaymentMethod === 'bank';
    const usesSavedPaymentMethod = selectedPaymentMethod === 'saved';
    const hasPaymentSelection = !isCheckoutRedesign || Boolean(selectedPaymentMethod);
    const currentPaymentElementReady = !hasPaymentSelection
      ? false
      : usesDynamicPaymentElement
      ? Boolean(dynamicPaymentElementMounted && stripe && elements)
      : usesBankPayment
        ? Boolean(stripe)
        : usesSavedPaymentMethod
          ? Boolean(selectedSavedPaymentMethodId)
      : paymentElementReady;

    const buildState = (validationReason, validationMessage) => ({
      validation_reason: validationReason,
      validation_message: validationMessage,
      has_name: hasName,
      has_email: hasEmail,
      has_phone: hasPhone,
      sms_consent_accepted: false,
      has_how_found_us: hasHowFoundUs,
      how_found_us_value: contactData.howFoundUs || null,
      has_how_found_us_details: hasHowFoundUsDetails,
      terms_accepted: Boolean(termsAccepted),
      stripe_ready: Boolean(stripe),
      elements_ready: Boolean(elements),
      payment_element_ready: Boolean(currentPaymentElementReady),
      dynamic_payment_element_ready: Boolean(dynamicPaymentElementReady),
      saved_payment_method_selected: Boolean(selectedSavedPaymentMethodId),
      selected_payment_method: selectedPaymentMethod,
      processing: Boolean(processing),
      coupon_validating: Boolean(isValidatingCoupon),
      checkout_recovery_required: Boolean(checkoutRecoveryBlocked),
      contact_complete: Boolean(contactComplete),
      user_type: isAuthenticated ? 'registered' : 'guest',
    });

    if (checkoutRecoveryBlocked) {
      return buildState('payment_outcome_requires_recovery', CHECKOUT_RECOVERY_MESSAGE);
    }
    if (!hasName) return buildState('missing_name', 'Please enter your full name');
    if (!hasEmail) return buildState('missing_email', 'Please enter your email address');
    if (!hasPhone) return buildState('missing_phone', 'Please enter your phone number');
    if (requiresHowFoundUs && !hasHowFoundUs) {
      return buildState('missing_how_found_us', 'Please tell us how you found BotSpot');
    }
    if (requiresHowFoundUs && contactData.howFoundUs === 'other' && !hasHowFoundUsDetails) {
      return buildState('missing_how_found_us_details', 'Please describe how you found BotSpot');
    }
    if (!termsAccepted) return buildState('terms_not_accepted', TERMS_ACCEPTANCE_SHORT_MESSAGE);
    if (isValidatingCoupon) return buildState('coupon_validating', 'Please wait for your promo code to finish applying.');
    if (elementsUpdating) return buildState('payment_total_updating', 'Please wait while the payment total updates.');
    if (!stripe) return buildState('stripe_not_ready', 'Payment system is still loading...');
    if (!isCheckoutRedesign && paymentMethodAvailabilityError) {
      return buildState('payment_method_availability_failed', paymentMethodAvailabilityError);
    }
    if (!hasPaymentSelection) return buildState('payment_method_missing', 'Please choose a payment method.');
    if (!currentPaymentElementReady) return buildState('payment_element_incomplete', 'Please complete your payment information');
    if (processing) return buildState('already_processing', 'Processing your payment...');
    return buildState(null, '');
  }, [
    checkoutRecoveryBlocked,
    contactComplete,
    contactData.name,
    contactData.email,
    contactData.phone,
    contactData.howFoundUs,
    contactData.howFoundUsOtherText,
    dynamicPaymentElementReady,
    dynamicPaymentElementMounted,
    elements,
    elementsUpdating,
    isCheckoutRedesign,
    isAuthenticated,
    isValidatingCoupon,
    paymentMethodAvailabilityError,
    paymentElementReady,
    processing,
    requiresHowFoundUs,
    selectedPaymentMethod,
    selectedSavedPaymentMethodId,
    stripe,
    termsAccepted,
  ]);

  // Get validation message for tooltip based on current form state
  const getValidationMessage = useCallback(() => getValidationState().validation_message, [getValidationState]);

  // Handle button click when form is incomplete - show validation feedback
  const handleButtonClick = useCallback((e) => {
    const validationState = getValidationState();
    trackCheckoutDiagnostic('checkout_cta_clicked', validationState);

    const validationMsg = validationState.validation_message;
    if (validationMsg) {
      trackCheckoutDiagnostic('checkout_validation_click_blocked', validationState);
      e.preventDefault();
      setValidationAttempted(true);
      lastValidationErrorRef.current = validationMsg;
      setError(validationMsg);
      setShowTooltip(true);

      if (validationState.validation_reason?.startsWith('missing_')) {
        const contactSection = document.querySelector('.checkout-redesign-section--contact');
        if (contactSection) {
          const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
          contactSection.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        }

        const invalidControlSelectors = {
          missing_name: 'input[name="name"]',
          missing_email: 'input[name="email"]',
          missing_phone: 'input[name="phone"]',
          missing_how_found_us: '#how-found-us-select',
          missing_how_found_us_details: '.how-found-us-other-input',
        };
        const invalidControl = document.querySelector(invalidControlSelectors[validationState.validation_reason]);
        if (invalidControl instanceof HTMLElement) {
          window.requestAnimationFrame(() => invalidControl.focus({ preventScroll: true }));
        }
      }

      if (validationState.validation_reason === 'terms_not_accepted') {
        setTermsError(true);
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        const termsGroup = document.querySelector('.terms-checkbox-group');
        if (termsGroup) {
          termsGroup.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          termsGroup.classList.add('shake');
          setTimeout(() => termsGroup.classList.remove('shake'), 500);
        }
        const termsCheckbox = document.getElementById('landing-checkout-terms');
        if (termsCheckbox instanceof HTMLElement) {
          window.requestAnimationFrame(() => termsCheckbox.focus({ preventScroll: true }));
        }
      }

      // Hide tooltip after 3 seconds
      setTimeout(() => setShowTooltip(false), 3000);
    }
  }, [getValidationState, trackCheckoutDiagnostic]);

  useEffect(() => {
    if (!validationAttempted || processing || error !== lastValidationErrorRef.current) {
      return;
    }

    const nextValidationState = getValidationState();
    const nextValidationMessage = nextValidationState.validation_message;
    lastValidationErrorRef.current = nextValidationMessage;
    setError(nextValidationMessage);
    setTermsError(nextValidationState.validation_reason === 'terms_not_accepted');
  }, [error, getValidationState, processing, validationAttempted]);

  const maybeConfirmInvoicePayment = useCallback(
    async (conversionResult) => {
      if (!conversionResult?.requiresAction || !conversionResult?.invoiceClientSecret) {
        return;
      }
      if (!stripe) {
        throw new Error('Payment processor is not ready to finalize your payment.');
      }

      const { error: invoiceError } = await confirmPaymentWithClient(stripe, null, {
        clientSecret: conversionResult.invoiceClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (invoiceError) {
        throw new Error(invoiceError.message || 'Additional payment confirmation failed. Please try again.');
      }
      refreshPaymentStatus({ forceRefresh: true });
    },
    [refreshPaymentStatus, stripe],
  );

  const handleContactChange = useCallback((field, value) => {
    setContactData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Handle billing period toggle with analytics tracking
  // Uses prop callback since state is lifted to parent for Stripe mode calculation
  const handleBillingPeriodChange = useCallback((newPeriod) => {
    if (newPeriod === billingPeriod) return; // No change

    const nextStripeMode = newPeriod === 'annual'
      ? ((variantConfig.annualTrialDays > 0 || appliedCoupon?.discountedAmount === 0) ? 'setup' : 'payment')
      : ((variantConfig.trialDays > 0 || appliedCoupon?.discountedAmount === 0) ? 'setup' : 'payment');
    const nextTermsText = isCheckoutRedesign
      ? monthlyTermsText
      : newPeriod === 'annual'
        ? (annualTermsText || monthlyTermsText)
        : monthlyTermsText;
    const nextMonetaryEventProps = getCheckoutMonetaryEventProps({
      variantConfig,
      billingPeriod: newPeriod,
      stripeMode: nextStripeMode,
      // The parent clears plan-specific coupon cents when billing changes.
      appliedCoupon: null,
      promotionDuration,
      termsText: nextTermsText,
      isCheckoutRedesign,
    });

    // Track billing period change for conversion attribution
    pushEvent({
      event: 'billing_period_changed',
      billing_period: newPeriod,
      previous_period: billingPeriod,
      plan_type: productSlug || 'starter-plan',
      experiment_variant: experimentVariant,
      ...checkoutDesignEventProps,
      ...nextMonetaryEventProps,
      page: 'landing_checkout',
      // Track if this changes the Stripe mode (trial, $0 promo setup, or immediate payment)
      stripe_mode_change: stripeMode !== nextStripeMode,
    });

    // Same-mode amount changes update Elements in place. Mode changes remount it.
    onBillingPeriodChange(newPeriod);
  }, [
    appliedCoupon,
    annualTermsText,
    billingPeriod,
    checkoutDesignEventProps,
    experimentVariant,
    isCheckoutRedesign,
    monthlyTermsText,
    onBillingPeriodChange,
    productSlug,
    promotionDuration,
    stripeMode,
    variantConfig,
  ]);

  const buildAiBuilderRedirect = useCallback(() => {
    if (redirectParam !== 'starter_plan') {
      return null;
    }

    // Redirect to AI Agent instead of deprecated AI Bot Builder
    // The handoff is already set via setAgentHandoff() and will be picked up by AI Agent
    const params = new URLSearchParams();
    if (leadTransactionId) {
      params.set('leadId', leadTransactionId);
    }
    if (variantParam) {
      params.set('variant', variantParam);
    }

    const queryString = params.toString();
    return `/ai-agent${queryString ? `?${queryString}` : ''}`;
  }, [leadTransactionId, redirectParam, variantParam]);

  const attemptAutoLoginFlow = useCallback(
    async (conversion, stripeProof = {}) => {
      try {
        const autoLoginBody = {};
        if (conversion.subscriptionId) autoLoginBody.subscription_id = conversion.subscriptionId;
        if (contactData.email) autoLoginBody.email = contactData.email;
        if (conversion.customerId) autoLoginBody.customer_id = conversion.customerId;
        const paymentIntentId = stripeProof.paymentIntentId || conversion.paymentIntentId;
        const paymentIntentClientSecret = stripeProof.paymentIntentClientSecret || conversion.paymentIntentClientSecret;
        const setupIntentId = stripeProof.setupIntentId || conversion.setupIntentId;
        const setupIntentClientSecret = stripeProof.setupIntentClientSecret || conversion.setupIntentClientSecret;
        if (paymentIntentId) autoLoginBody.payment_intent_id = paymentIntentId;
        if (paymentIntentClientSecret) autoLoginBody.payment_intent_client_secret = paymentIntentClientSecret;
        if (setupIntentId) autoLoginBody.setup_intent_id = setupIntentId;
        if (setupIntentClientSecret) autoLoginBody.setup_intent_client_secret = setupIntentClientSecret;

        // Check for handoff with conversation context (saved when user clicked upgrade from AI Agent)
        // This allows us to return the user to their specific conversation after checkout
        const pendingHandoff = getAgentHandoff();
        const handoffConversationId = pendingHandoff?.conversationId;

        // Build the FINAL destination URL (where user should end up after any upsells).
        //
        // Priority order:
        //   1. AI Agent conversation handoff (user was mid-conversation when they hit a paywall)
        //   2. Marketplace-origin successUrl (user clicked "Start My Strategy" on /marketplace/strategy/:id)
        //   3. AI Agent fallback (default lead-form funnel)
        let finalDestination;
        if (handoffConversationId) {
          // Return to the specific conversation the user was on
          const params = new URLSearchParams();
          if (leadTransactionId) params.set('leadId', leadTransactionId);
          if (variantParam) params.set('variant', variantParam);
          const queryString = params.toString();
          finalDestination = `/ai-agent/${handoffConversationId}${queryString ? `?${queryString}` : ''}`;
          if (SHOULD_LOG_CHECKOUT_DEBUG) {
            console.log('[StarterCheckoutPage] Final destination conversation:', handoffConversationId);
          }
        } else if (checkoutSourceParam === 'marketplace' && successUrlParam) {
          // Marketplace funnel: user came from clicking a strategy CTA unpaid.
          // Send them back to the marketplace detail flow (via the successUrl the
          // guard built), which will show a resumeAction toast prompting them to
          // click Start My Strategy again — now as a paying user, so the deploy
          // flow in StrategyDetailsPage.handleDeploy proceeds to /deploy.
          //
          // We keep same-origin validation by parsing the successUrl and only
          // using the pathname+search (strips any attacker-controlled origin).
          try {
            const parsed = new URL(successUrlParam, window.location.origin);
            if (parsed.origin === window.location.origin) {
              finalDestination = `${parsed.pathname}${parsed.search}`;
              if (SHOULD_LOG_CHECKOUT_DEBUG) {
                console.log('[StarterCheckoutPage] Final destination marketplace:', finalDestination);
              }
            } else {
              console.warn('[StarterCheckoutPage] Cross-origin successUrl rejected:', parsed.origin);
              finalDestination = '/marketplace';
            }
          } catch (urlErr) {
            console.warn('[StarterCheckoutPage] Invalid successUrl, falling back:', urlErr?.message);
            finalDestination = '/marketplace';
          }
        } else {
          // Fallback: check for starter_plan redirect param or default to /ai-agent
          const builderRedirectTarget = buildAiBuilderRedirect();
          const defaultRedirectParams = new URLSearchParams();
          if (leadTransactionId) defaultRedirectParams.set('leadId', leadTransactionId);
          if (variantParam) defaultRedirectParams.set('variant', variantParam);
          const defaultRedirectQuery = defaultRedirectParams.toString();
          const defaultRedirectTarget = `/ai-agent${defaultRedirectQuery ? `?${defaultRedirectQuery}` : ''}`;
          finalDestination = builderRedirectTarget || defaultRedirectTarget;
        }

        const surveyParams = new URLSearchParams();
        if (contactData.email) surveyParams.set('email', contactData.email);
        if (contactData.phone) surveyParams.set('phone', contactData.phone);
        if (contactData.name) surveyParams.set('name', contactData.name);
        if (conversion.customerId) surveyParams.set('customerId', conversion.customerId);
        surveyParams.set('purchase_success', 'true');
        surveyParams.set('source_flow', 'landing_checkout_post_purchase');
        surveyParams.set('redirectTo', finalDestination);
        if (productSlug) surveyParams.set('productSlug', productSlug);
        if (planKey) surveyParams.set('planKey', planKey);
        finalDestination = `/signup-survey?${surveyParams.toString()}`;

        // Redirect through bootcamp upsell page first (post-purchase upsell)
        // The upsell page will redirect to finalDestination when user clicks skip
        const upsellParams = new URLSearchParams();
        upsellParams.set('redirect', finalDestination);
        if (leadTransactionId) upsellParams.set('leadId', leadTransactionId);
        if (variantParam) upsellParams.set('variant', variantParam);
        const redirectTarget = `/checkout/bootcamp-upsell?${upsellParams.toString()}`;
        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log('[StarterCheckoutPage] Redirecting through upsell page, final destination:', finalDestination);
        }

        const normalizedCheckoutEmail = String(contactData.email || '').trim().toLowerCase();
        const checkoutMatchesAuthenticatedAccount = Boolean(
          isAuthenticated &&
          authenticatedAccountEmail &&
          normalizedCheckoutEmail === authenticatedAccountEmail
        );

        if (checkoutMatchesAuthenticatedAccount) {
          navigate(redirectTarget, { replace: true });
          return;
        }

        autoLoginBody.redirectTo = redirectTarget;

        // NOTE: Do NOT clear handoff here - the AI Agent page will clear it after consuming the prompt
        // Previously clearing here caused the prompt to be lost because the user goes through:
        // checkout → magic link → bootcamp upsell → AI Agent, and by the time they reach AI Agent
        // the handoff was already cleared
        // See: AiAgent.js line ~384 where clearAgentHandoff() is called after processing

        const autoLoginResponse = await fetchWithTimeout(`${API_URL}/stripe/autoLoginAfterPurchase`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(autoLoginBody),
        }, AUTO_LOGIN_FETCH_TIMEOUT_MS);

        if (!autoLoginResponse.ok) {
          throw new Error(`Auto-login request failed: ${autoLoginResponse.status}`);
        }

        const autoLoginData = await autoLoginResponse.json();

        if (await completeStripeAutoLogin(autoLoginData, {
          handleCustomJwtAuth,
          fallbackUrl: redirectTarget,
          navigate,
        })) {
          return;
        }

        // Fallback navigation if no magic link returned
        navigate(redirectTarget, { replace: true });
      } catch (autoLoginError) {
        console.error('[StarterCheckoutPage] Auto-login error:', autoLoginError);
        // On error, still redirect to AI Agent page (use handoff if available)
        const pendingHandoff = getAgentHandoff();
        const handoffConversationId = pendingHandoff?.conversationId;
        let fallbackRedirect;
        if (handoffConversationId) {
          fallbackRedirect = `/ai-agent/${handoffConversationId}`;
        } else {
          const fallbackParams = new URLSearchParams();
          if (leadTransactionId) fallbackParams.set('leadId', leadTransactionId);
          if (variantParam) fallbackParams.set('variant', variantParam);
          const fallbackQuery = fallbackParams.toString();
          fallbackRedirect = `/ai-agent${fallbackQuery ? `?${fallbackQuery}` : ''}`;
        }
        navigate(buildAiBuilderRedirect() || fallbackRedirect, { replace: true });
      }
    },
    [
      buildAiBuilderRedirect,
      authenticatedAccountEmail,
      checkoutSourceParam,
      contactData.email,
      contactData.name,
      contactData.phone,
      handleCustomJwtAuth,
      isAuthenticated,
      leadTransactionId,
      navigate,
      planKey,
      productSlug,
      successUrlParam,
      variantParam,
    ],
  );

  const handleSubmit = useCallback(
    async (eventOrOptions) => {
      const isDomSubmit = Boolean(eventOrOptions?.preventDefault);
      if (isDomSubmit) {
        eventOrOptions.preventDefault();
      }

      const submitOptions = isDomSubmit ? {} : (eventOrOptions || {});
      const paymentSource = submitOptions.paymentSource || (
        selectedPaymentMethod === 'saved'
          ? 'saved_card'
          :
        selectedPaymentMethod === 'bank'
          ? 'bank'
          : isCheckoutRedesign && isRedesignDynamicPaymentMethod(selectedPaymentMethod)
          ? selectedPaymentMethod
          : isCheckoutRedesign && !selectedPaymentMethod
          ? null
          : 'card'
      );
      const isWalletPayment = paymentSource === 'wallet';
      const isBankPayment = paymentSource === 'bank';
      const isSavedPayment = paymentSource === 'saved_card';
      const isDynamicPayment = isCheckoutRedesign && isRedesignDynamicPaymentMethod(paymentSource);
      const intentPaymentType = isBankPayment ? 'us_bank_account' : isDynamicPayment ? 'automatic' : 'card';
      const useSubscriptionPaymentIntentMode = stripeMode === 'payment' && !isBankPayment;
      const walletEvent = submitOptions.walletEvent || null;
      const walletPaymentMethodId = submitOptions.walletPaymentMethodId || null;
      const walletType = submitOptions.walletType || walletEvent?.expressPaymentType || null;

      const isElementsWalletPayment = isWalletPayment && !walletPaymentMethodId;
      const selectedPaymentReady = !paymentSource
        ? false
        : isDynamicPayment
        ? Boolean(dynamicPaymentElementMounted && stripe && elements)
        : isBankPayment
          ? Boolean(stripe)
          : isSavedPayment
            ? Boolean(selectedSavedPaymentMethodId)
          : paymentElementReady;
      let walletCompletionReported = false;
      const completeWalletPayment = (status) => {
        if (walletCompletionReported || typeof submitOptions.walletComplete !== 'function') return;
        walletCompletionReported = true;
        submitOptions.walletComplete(status);
      };
      const failWalletPayment = (message, reason = 'fail') => {
        completeWalletPayment('fail');
        if (typeof walletEvent?.paymentFailed === 'function') {
          walletEvent.paymentFailed({ reason, message });
          trackCheckoutDiagnostic('wallet_confirm_failed', {
            wallet_type: walletType,
            failure_reason: reason,
            has_message: Boolean(message),
          });
        }
      };

      if (checkoutRecoveryBlocked) {
        const recoveryMessage = CHECKOUT_RECOVERY_MESSAGE;
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'payment_outcome_requires_recovery',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError(recoveryMessage);
        failWalletPayment(recoveryMessage);
        return;
      }

      if (processing) {
        trackCheckoutDiagnostic('checkout_submit_ignored', {
          blocked_reason: 'already_processing',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        failWalletPayment('Payment is already processing.');
        return;
      }

      setError('');
        trackCheckoutDiagnostic('checkout_submit_clicked', {
        user_type: isAuthenticated ? 'registered' : 'guest',
        contact_complete: Boolean(contactComplete),
        terms_accepted: Boolean(termsAccepted),
        payment_element_ready: Boolean(isWalletPayment ? walletPaymentMethodId || elements : selectedPaymentReady),
        saved_payment_method_selected: Boolean(selectedSavedPaymentMethodId),
        coupon_validating: Boolean(isValidatingCoupon),
        payment_source: paymentSource,
        wallet_type: walletType,
      });

      if (isValidatingCoupon) {
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'coupon_validating',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError('Please wait for your promo code to finish applying.');
        failWalletPayment('Promo code is still applying.');
        return;
      }

      if (!contactComplete) {
        setValidationAttempted(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'contact_incomplete',
          has_name: Boolean(contactData.name.trim()),
          has_email: Boolean(contactData.email.trim()),
          has_phone: Boolean(contactData.phone.trim()),
          has_how_found_us: Boolean(howFoundUsComplete),
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        const contactMessage =
          howFoundUsComplete
            ? 'Please fill in your name, email, and phone number to continue.'
            : 'Please tell us how you found BotSpot before completing your purchase.';
        setError(contactMessage);
        failWalletPayment(contactMessage);
        return;
      }

      if (!termsAccepted) {
        setValidationAttempted(true);
        setTermsError(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'terms_not_accepted',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError(TERMS_ACCEPTANCE_ERROR_MESSAGE);
        failWalletPayment(TERMS_ACCEPTANCE_ERROR_MESSAGE);
        return;
      }

      if (!paymentSource) {
        const paymentMessage = paymentMethodAvailabilityError ||
          (isCheckoutRedesign && !paymentMethodAvailabilityChecked
            ? 'Payment methods are still loading...'
            : 'Please choose a payment method.');
        setValidationAttempted(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: paymentMethodAvailabilityError
            ? 'payment_method_availability_failed'
            : isCheckoutRedesign && !paymentMethodAvailabilityChecked
              ? 'payment_methods_loading'
              : 'payment_method_missing',
          payment_source: null,
          wallet_type: walletType,
        });
        setError(paymentMessage);
        failWalletPayment(paymentMessage);
        return;
      }

      if (
        !stripe ||
        (isElementsWalletPayment && !elements) ||
        (isDynamicPayment && (!elements || !selectedPaymentReady)) ||
        (isSavedPayment && !selectedPaymentReady) ||
        (!isWalletPayment && !isBankPayment && !isSavedPayment && !isDynamicPayment && (!elements || !selectedPaymentReady)) ||
        (isBankPayment && !isDynamicPayment && !selectedPaymentReady)
      ) {
        setValidationAttempted(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'stripe_not_ready',
          stripe_ready: Boolean(stripe),
          elements_ready: Boolean(elements),
          payment_element_ready: Boolean(isWalletPayment ? walletPaymentMethodId || elements : selectedPaymentReady),
          saved_payment_method_selected: Boolean(selectedSavedPaymentMethodId),
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError('Payment form is not ready yet. Please wait a moment and try again.');
        failWalletPayment('Payment form is not ready yet.');
        return;
      }

      setProcessing(true);
      let checkoutFailureStage = 'submission';
      let stripeConfirmationAttempted = false;
      let stripeConfirmationCompleted = false;

      try {
        if (isWalletPayment) {
          trackCheckoutDiagnostic('wallet_confirm_started', {
            payment_source: paymentSource,
            wallet_type: walletType,
          });
        }

        if (isElementsWalletPayment || isDynamicPayment) {
          checkoutFailureStage = 'elements_submit';
          const { error: submitError } = await elements.submit();
          if (submitError) {
            trackCheckoutDiagnostic('stripe_elements_submit_failed', {
              stripe_error_type: submitError.type || null,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const submitMessage = submitError.message || (
              isDynamicPayment
                ? 'Failed to submit payment details.'
                : 'Failed to submit Apple Pay or Google Pay details.'
            );
            setError(submitMessage);
            failWalletPayment(submitMessage, 'invalid_payment_data');
            setProcessing(false);
            return;
          }
        }

        identifyUserFromLeadCapture(
          contactData.email,
          {
            name: contactData.name,
            phone: contactData.phone,
            lead_type: 'sales_ai_checkout',
            form_step: 'payment_submission',
            checkout_type: 'landing_checkout_page',
          },
          'landing_checkout_page',
        );
        replaySalesSignupExposureAfterIdentify({
          attribution: salesSignupV4Attribution,
          utmData,
        });
        replayCheckoutDesignExposureAfterIdentify({
          checkoutDesignVariant,
          checkoutDesignExperimentActive,
          checkoutDesignEventProps,
          utmData,
        });

        const userId = isAuthenticated ? user.sub : `temp_${Date.now()}`;
        const token = isAuthenticated ? await getApiToken() : null;
        const checkoutTransactionId = generateCheckoutTransactionId(
          null,
          null,
          `landing_checkout_${Date.now()}`,
        );

        pushEvent({
          event: ANALYTICS_EVENTS.BEGIN_CHECKOUT,
          transaction_id: checkoutTransactionId,
          source: location.pathname,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
          plan_type: productSlug || 'starter-plan',
          flow_context: 'sales_ai_landing_checkout',
          contact_email: contactData.email,
          contact_name: contactData.name,
          contact_phone: contactData.phone,
          lead_transaction_id: leadTransactionId,
          experiment_variant: experimentVariant,
          experiment_name: checkoutExperimentName,
          ...checkoutDesignEventProps,
          ...checkoutMonetaryEventProps,
          ...salesPageExperimentParams,
          ...salesSignupV4Attribution,
          // Annual Plan Toggle - track billing period selection
          billing_period: billingPeriod,
          plan_key: planKey,
          payment_source: paymentSource,
          wallet_type: walletType,
          ...utmData,
        });

        const termsPayload = buildTermsPayload();
        const affiliateCode = getAffiliateCodeForApi();
        const affiliateFields = affiliateCode
          ? { affiliateCode, affiliate_code: affiliateCode, aff: affiliateCode }
          : {};
        const smsConsentPayload = buildSmsConsentPayload({
          source: 'landing_checkout_page',
          phoneNumber: contactData.phone,
          accepted: false,
        });

        if (isSavedPayment) {
          if (!selectedSavedPaymentMethodId) {
            setError('Please choose a saved payment method or select another payment option.');
            setProcessing(false);
            return;
          }

          checkoutFailureStage = 'saved_payment_conversion';
          const conversion = await convertLeadToTrial(
            {
              email: contactData.email,
              phoneNumber: contactData.phone,
              paymentMethodId: selectedSavedPaymentMethodId,
              terms: termsPayload,
              ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
              productSlugs: [productSlug || 'starter-plan'],
              ...salesSignupV4Attribution,
              flowContext: 'sales_ai_landing_checkout',
              cartMetadata: {
                productSlug: productSlug || 'starter-plan',
                experiment_variant: experimentVariant,
                ...checkoutDesignEventProps,
                ...checkoutMonetaryEventProps,
                ...salesPageExperimentParams,
                ...salesSignupV4Attribution,
                billing_period: billingPeriod,
                stripe_mode: stripeMode,
                payment_source: paymentSource,
                saved_payment_method_id: selectedSavedPaymentMethodId,
              },
              howFoundUs: contactData.howFoundUs || null,
              howFoundUsOtherText: contactData.howFoundUsOtherText?.trim() || null,
              items: [
                {
                  productSlug: productSlug || 'starter-plan',
                  planKey,
                  quantity: 1,
                },
              ],
              ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
            },
            { apiUrl: API_URL, authToken: token },
          );

          await maybeConfirmInvoicePayment(conversion);
          pushEvent({
            event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
            transaction_id: conversion.subscriptionId || selectedSavedPaymentMethodId,
            value: centsToUsd(checkoutAmountDueTodayCents),
            currency: 'USD',
            plan_type: productSlug || 'starter-plan',
            source: location.pathname,
            userId,
            user_type: isAuthenticated ? 'registered' : 'guest',
            customerId: conversion.customerId,
            paymentMethodId: selectedSavedPaymentMethodId,
            lead_transaction_id: leadTransactionId,
            experiment_variant: experimentVariant,
            experiment_name: checkoutExperimentName,
            ...checkoutDesignEventProps,
            ...checkoutMonetaryEventProps,
            checkout_payment_result: 'saved_payment_method_completed',
            payment_source: paymentSource,
            how_found_us: contactData.howFoundUs || null,
            how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
            ...salesPageExperimentParams,
            ...salesSignupV4Attribution,
            ...utmData,
          });

          try {
            await refreshPaymentStatus({ forceRefresh: true });
          } catch (refreshError) {
            console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
          }

          checkoutFailureStage = 'account_handoff';
          await attemptAutoLoginFlow(conversion, {
            paymentMethodId: selectedSavedPaymentMethodId,
          });
          return;
        }

        // Create SetupIntent or PaymentIntent based on stripe mode
        // - 'setup' mode (trial): Backend should return SetupIntent (seti_...) for future charge
        // - 'payment' mode (no trial): Backend should return PaymentIntent (pi_...) for immediate charge
        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log(`[StarterCheckoutPage] Submitting with stripeMode=${stripeMode}, subscriptionPaymentIntentMode=${useSubscriptionPaymentIntentMode}`);
          console.info('[StarterCheckoutPage] Affiliate payload handoff', {
            affiliateCode: affiliateCode || null,
            hasAffiliateFields: Boolean(affiliateCode),
            email: contactData.email,
            productSlug: productSlug || 'starter-plan',
            planKey,
          });
        }

        trackCheckoutDiagnostic('checkout_payment_intent_request_started', {
          transaction_id: checkoutTransactionId,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
        });

        checkoutFailureStage = 'payment_intent_request';
        const paymentIntentRequestStartedAtMs = Date.now();
        let setupResponse;
        try {
          setupResponse = await fetchWithTimeout(`${API_URL}/stripe/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            productSlugs: [productSlug || 'starter-plan'],
            ...affiliateFields,
            email: contactData.email,
            customerName: contactData.name,
            phoneNumber: contactData.phone,
            paymentType: intentPaymentType,
            ...(isBankPayment ? { bankVerificationMethod } : {}),
            terms: termsPayload,
            ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
            leadTransactionId,
            flowContext: 'sales_ai_landing_checkout',
            ...salesSignupV4Attribution,
            cartMetadata: {
              productSlug: productSlug || 'starter-plan',
              promptProvided: Boolean(activePrompt),
              variant: variantParam || experimentVariant || undefined,
              source: 'sales_ai_prompt_landing_checkout',
              experiment_variant: experimentVariant,
              ...checkoutDesignEventProps,
              ...checkoutMonetaryEventProps,
              ...salesPageExperimentParams,
              ...salesSignupV4Attribution,
              billing_period: billingPeriod,  // Track monthly vs annual selection
              stripe_mode: stripeMode,  // Track which Stripe mode frontend expects
              payment_source: paymentSource,
              wallet_type: walletType,
              bank_verification_method: isBankPayment ? bankVerificationMethod : undefined,
            },
            howFoundUs: contactData.howFoundUs || null,
            howFoundUsOtherText: contactData.howFoundUsOtherText?.trim() || null,
            ...affiliateFields,
            // Pass planKey to select the correct ProductPlan based on billing period and variant
            items: [
              {
                productSlug: productSlug || 'starter-plan',
                planKey: planKey,  // Computed from billingPeriod: 'default'/'annual' for control, 'no_trial'/'annual_no_trial' for others
                quantity: 1,
              },
            ],
            // Promo code support for ABC pricing test (discount variant)
            ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
            forceSubscriptionCreation: true,
            // CRITICAL: subscriptionPaymentIntentMode tells backend which intent type to return
            // - true: Return PaymentIntent for immediate charge (payment mode, no trial)
            // - false: Return SetupIntent for future charge (setup mode, with trial)
            // Bank checkout uses SetupIntent first so Stripe Financial Connections can open
            // before the slower subscription/invoice creation path.
            subscriptionPaymentIntentMode: useSubscriptionPaymentIntentMode,
            ...(utmData?.utm_source ? { utmSource: utmData.utm_source } : {}),
            ...(utmData?.utm_medium ? { utmMedium: utmData.utm_medium } : {}),
            ...(utmData?.utm_campaign ? { utmCampaign: utmData.utm_campaign } : {}),
            ...(utmData?.utm_term ? { utmTerm: utmData.utm_term } : {}),
            ...(utmData?.utm_content ? { utmContent: utmData.utm_content } : {}),
            ...(utmData?.referrer ? { referrer: utmData.referrer } : {}),
            landingPage: utmData?.landing_page || sessionStorage.getItem('landingPage') || document.referrer,
            pagePath: window.location.pathname,
            pageTitle: document.title,
            ...attributionParams,
          }),
          }, PAYMENT_INTENT_FETCH_TIMEOUT_MS);
        } catch (requestError) {
          if (!isCheckoutRedesign) throw requestError;

          const requestErrorMessage = requestError?.message || String(requestError);
          const errorKind = /timeout|abort/i.test(`${requestError?.name || ''} ${requestErrorMessage}`)
            ? 'timeout'
            : 'network';
          trackCheckoutDiagnostic('checkout_payment_intent_request_failed', {
            transaction_id: checkoutTransactionId,
            duration_ms: Math.max(0, Date.now() - paymentIntentRequestStartedAtMs),
            error_kind: errorKind,
            error_message: requestErrorMessage,
            retryable: false,
            outcome_unknown: true,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          trackCheckoutDiagnostic('checkout_payment_intent_request_outcome_unknown', {
            transaction_id: checkoutTransactionId,
            error_kind: errorKind,
            retry_safe: false,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          setLocalCheckoutRecoveryRequired(true);
          onCheckoutRecoveryRequired?.();
          const requestMessage =
            'We could not verify whether payment setup completed. Do not submit again; contact support.';
          setError(requestMessage);
          failWalletPayment(requestMessage);
          setProcessing(false);
          return;
        }

        let setupData = null;
        try {
          setupData = await setupResponse.json();
        } catch (parseError) {
          if (!isCheckoutRedesign) throw parseError;

          trackCheckoutDiagnostic('checkout_payment_intent_response_parse_failed', {
            transaction_id: checkoutTransactionId,
            status_code: setupResponse.status,
            duration_ms: Math.max(0, Date.now() - paymentIntentRequestStartedAtMs),
            error_message: parseError?.message || String(parseError),
            payment_source: paymentSource,
            wallet_type: walletType,
          });
        }
        if (!setupResponse.ok || !setupData?.clientSecret) {
          const errorKind = setupResponse.ok ? 'invalid_response' : 'backend_response';
          const responseOutcomeUnknown =
            isCheckoutRedesign && (setupResponse.ok || setupResponse.status >= 500);
          trackCheckoutDiagnostic('checkout_payment_intent_request_failed', {
            transaction_id: checkoutTransactionId,
            status_code: setupResponse.status,
            has_message: Boolean(setupData?.message),
            ...(isCheckoutRedesign ? {
              duration_ms: Math.max(0, Date.now() - paymentIntentRequestStartedAtMs),
              error_kind: errorKind,
              retryable: responseOutcomeUnknown
                ? false
                : setupResponse.ok || setupResponse.status >= 500,
              outcome_unknown: responseOutcomeUnknown,
            } : {}),
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          if (responseOutcomeUnknown) {
            trackCheckoutDiagnostic('checkout_payment_intent_request_outcome_unknown', {
              transaction_id: checkoutTransactionId,
              status_code: setupResponse.status,
              error_kind: errorKind,
              retry_safe: false,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setLocalCheckoutRecoveryRequired(true);
            onCheckoutRecoveryRequired?.();
          }
          const setupMessage = responseOutcomeUnknown
            ? 'We could not verify whether payment setup completed. Do not submit again; contact support.'
            : setupData?.message || (isCheckoutRedesign
              ? 'Payment service is temporarily unavailable. Please try again.'
              : 'Failed to initialize payment. Please try again.');
          setError(setupMessage);
          failWalletPayment(setupMessage);
          setProcessing(false);
          return;
        }

        // Determine which confirmation flow to use based on stripeMode
        // - 'payment' mode: Use confirmPayment() for PaymentIntent (pi_...)
        // - 'setup' mode: Use confirmSetup() for SetupIntent (seti_...)
        //
        // VALIDATION: Verify backend returned the expected intent type
        const isPaymentIntent = setupData.clientSecret?.startsWith('pi_');
        const isSetupIntent = setupData.clientSecret?.startsWith('seti_');

        trackCheckoutDiagnostic('checkout_payment_intent_created', {
          transaction_id: checkoutTransactionId,
          ...(isCheckoutRedesign ? {
            duration_ms: Math.max(0, Date.now() - paymentIntentRequestStartedAtMs),
          } : {}),
          returned_intent_type: isPaymentIntent ? 'payment_intent' : isSetupIntent ? 'setup_intent' : 'unknown',
          expected_intent_type: useSubscriptionPaymentIntentMode ? 'payment_intent' : 'setup_intent',
          subscription_id_present: Boolean(setupData.subscriptionId),
          customer_id_present: Boolean(setupData.customerId),
          payment_source: paymentSource,
          wallet_type: walletType,
        });

        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log(`[StarterCheckoutPage] Backend returned: ${isPaymentIntent ? 'PaymentIntent' : isSetupIntent ? 'SetupIntent' : 'unknown'}, expected: ${useSubscriptionPaymentIntentMode ? 'PaymentIntent' : 'SetupIntent'}`);
        }

        const intentTypeMismatch =
          (useSubscriptionPaymentIntentMode && !isPaymentIntent) ||
          (!useSubscriptionPaymentIntentMode && !isSetupIntent);

        // The redesign fails closed instead of sending a SetupIntent through
        // confirmPayment (or vice versa). Keep the control warning-only path.
        if (intentTypeMismatch && isCheckoutRedesign) {
          trackCheckoutDiagnostic('checkout_payment_intent_type_mismatch', {
            transaction_id: checkoutTransactionId,
            expected_intent_type: useSubscriptionPaymentIntentMode ? 'payment_intent' : 'setup_intent',
            returned_intent_type: isPaymentIntent ? 'payment_intent' : isSetupIntent ? 'setup_intent' : 'unknown',
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          setLocalCheckoutRecoveryRequired(true);
          onCheckoutRecoveryRequired?.();
          setError(CHECKOUT_RECOVERY_MESSAGE);
          failWalletPayment(CHECKOUT_RECOVERY_MESSAGE);
          setProcessing(false);
          return;
        }

        if (intentTypeMismatch) {
          console.warn(`[StarterCheckoutPage] ⚠️ Stripe mode mismatch! Frontend mode: ${stripeMode}, Backend returned: ${isPaymentIntent ? 'pi_' : isSetupIntent ? 'seti_' : 'unknown'}`);
        }

        // Use the frontend-determined mode (stripeMode) since Elements was initialized with it
        // The backend MUST return matching intent type or Stripe will throw an error
        const usingPaymentIntent = useSubscriptionPaymentIntentMode;
        checkoutFailureStage = 'stripe_confirmation';
        const bankBillingDetails = isBankPayment ? {
          name: contactData.name,
          email: contactData.email,
          phone: contactData.phone,
        } : null;
        const bankCollectionOptions = bankBillingDetails ? {
          clientSecret: setupData.clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: bankBillingDetails,
            },
          },
          expand: ['payment_method'],
        } : null;
        const walletBillingDetails = isWalletPayment ? {
          name: submitOptions.walletBillingDetails?.name || walletEvent?.billingDetails?.name || contactData.name,
          email: submitOptions.walletBillingDetails?.email || walletEvent?.billingDetails?.email || contactData.email,
          phone: submitOptions.walletBillingDetails?.phone || walletEvent?.billingDetails?.phone || contactData.phone,
        } : null;
        const walletConfirmParams = isWalletPayment ? {
          return_url: `${window.location.origin}/set-password?email=${encodeURIComponent(
            walletBillingDetails.email || contactData.email,
          )}&name=${encodeURIComponent(walletBillingDetails.name || contactData.name)}&redirectTo=onboarding&source_flow=landing_checkout_wallet_redirect`,
          ...(walletPaymentMethodId ? { payment_method: walletPaymentMethodId } : {}),
        } : null;
        const dynamicPaymentConfirmParams = isDynamicPayment ? {
          return_url: `${window.location.origin}/set-password?email=${encodeURIComponent(
            contactData.email,
          )}&name=${encodeURIComponent(contactData.name)}&redirectTo=onboarding&source_flow=${isBankPayment ? 'landing_checkout_bank_redirect' : 'landing_checkout_payment_redirect'}`,
          payment_method_data: {
            billing_details: {
              name: contactData.name,
              email: contactData.email,
              phone: contactData.phone,
            },
          },
        } : null;

        if (usingPaymentIntent) {
          const cardNumberElement = isCheckoutRedesign && !isWalletPayment && !isDynamicPayment && !isBankPayment
            ? elements?.getElement?.(CardNumberElement)
            : null;

          if (isCheckoutRedesign && !isWalletPayment && !isDynamicPayment && !isBankPayment && !cardNumberElement) {
            trackCheckoutDiagnostic('stripe_elements_submit_failed', {
              transaction_id: checkoutTransactionId,
              stripe_error_type: 'card_element_missing',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const cardMessage = 'Card form is still loading. Please try again.';
            setError(cardMessage);
            setProcessing(false);
            return;
          }

          if (!isCheckoutRedesign && !isWalletPayment && !isBankPayment) {
            const { error: submitError } = await elements.submit();
            if (submitError) {
              trackCheckoutDiagnostic('stripe_elements_submit_failed', {
                transaction_id: checkoutTransactionId,
                stripe_error_type: submitError.type || null,
                payment_source: paymentSource,
                wallet_type: walletType,
              });
              setError(submitError.message || 'Failed to submit payment details.');
              setProcessing(false);
              return;
            }
          }

          trackCheckoutDiagnostic('stripe_confirm_started', {
            transaction_id: checkoutTransactionId,
            intent_type: 'payment_intent',
            payment_source: paymentSource,
            wallet_type: walletType,
          });

          let stripeError = null;
          let paymentIntent = null;
          if (isBankPayment && !isDynamicPayment) {
            if (
              typeof stripe.collectBankAccountForPayment !== 'function' ||
              typeof stripe.confirmUsBankAccountPayment !== 'function'
            ) {
              trackCheckoutDiagnostic('stripe_confirm_failed', {
                transaction_id: checkoutTransactionId,
                intent_type: 'payment_intent',
                stripe_error_type: 'bank_api_unavailable',
                payment_source: paymentSource,
                wallet_type: walletType,
              });
              const bankUnavailableMessage = 'Bank payment is not available in this browser. Please use card instead.';
              setError(bankUnavailableMessage);
              setProcessing(false);
              return;
            }

            trackCheckoutDiagnostic('stripe_bank_account_collection_started', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              payment_source: paymentSource,
            });

            const {
              error: bankCollectionError,
              paymentIntent: collectedPaymentIntent,
            } = await stripe.collectBankAccountForPayment(bankCollectionOptions);

            if (bankCollectionError) {
              trackCheckoutDiagnostic('stripe_confirm_failed', {
                transaction_id: checkoutTransactionId,
                intent_type: 'payment_intent',
                stripe_error_type: bankCollectionError.type || 'bank_collection_failed',
                payment_source: paymentSource,
                wallet_type: walletType,
              });
              setError(bankCollectionError.message || 'Bank connection failed. Please try again or use card.');
              setProcessing(false);
              return;
            }

            if (!collectedPaymentIntent || collectedPaymentIntent.status === 'requires_payment_method') {
              trackCheckoutDiagnostic('checkout_submit_blocked', {
                transaction_id: checkoutTransactionId,
                blocked_reason: 'bank_connection_cancelled',
                payment_source: paymentSource,
              });
              setError('Bank connection was cancelled. Choose bank again or use card.');
              setProcessing(false);
              return;
            }

            if (collectedPaymentIntent.status === 'requires_confirmation') {
              stripeConfirmationAttempted = true;
              const bankConfirmResult = await stripe.confirmUsBankAccountPayment(setupData.clientSecret);
              stripeError = bankConfirmResult.error || null;
              paymentIntent = bankConfirmResult.paymentIntent || null;
            } else {
              paymentIntent = collectedPaymentIntent;
            }
          } else {
            stripeConfirmationAttempted = true;
            const confirmResult = await confirmPaymentWithClient(
              stripe,
              isElementsWalletPayment || isDynamicPayment ? elements : isWalletPayment ? null : isCheckoutRedesign ? null : elements,
              {
                clientSecret: setupData.clientSecret,
                redirect: 'if_required',
                ...(isWalletPayment ? {
                  confirmParams: walletConfirmParams,
                } : {}),
                ...(isDynamicPayment ? {
                  confirmParams: dynamicPaymentConfirmParams,
                } : {}),
                ...(isCheckoutRedesign && !isWalletPayment && !isDynamicPayment ? {
                  cardElement: cardNumberElement,
                  billingDetails: {
                    name: contactData.name,
                    email: contactData.email,
                    phone: contactData.phone,
                  },
                } : {}),
              },
            );
            stripeError = confirmResult.error || null;
            paymentIntent = confirmResult.paymentIntent || null;
          }

          if (stripeError) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              stripe_error_type: stripeError.type || null,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const stripeMessage = stripeError.message || 'Failed to confirm payment method.';
            setError(stripeMessage);
            failWalletPayment(stripeMessage);
            setProcessing(false);
            return;
          }

          const confirmedPaymentIntent =
            paymentIntent || {
              id: setupData.paymentIntentId,
              status: setupData.paymentIntentStatus || setupData.status || 'processing',
            };

          if (
            isBankPayment &&
            (confirmedPaymentIntent.status === 'requires_action' ||
              confirmedPaymentIntent.next_action?.type === 'verify_with_microdeposits')
          ) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              payment_intent_status: confirmedPaymentIntent.status,
              blocked_reason: 'bank_verification_required',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setError('Bank account verification is needed before checkout can finish. Use card for instant access.');
            setProcessing(false);
            return;
          }

          const acceptableStatuses = new Set(['succeeded', 'processing', 'requires_capture']);
          if (!confirmedPaymentIntent?.id || !acceptableStatuses.has(confirmedPaymentIntent.status)) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              payment_intent_status: confirmedPaymentIntent?.status || null,
              blocked_reason: 'unexpected_payment_intent_status',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const paymentStatusMessage = 'Payment confirmation failed. Please try again.';
            setError(paymentStatusMessage);
            failWalletPayment(paymentStatusMessage);
            setProcessing(false);
            return;
          }

          trackCheckoutDiagnostic('stripe_confirm_succeeded', {
            transaction_id: checkoutTransactionId,
            intent_type: 'payment_intent',
            payment_intent_status: confirmedPaymentIntent.status,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          stripeConfirmationCompleted = true;
          checkoutFailureStage = 'conversion';
          if (isWalletPayment) {
            completeWalletPayment('success');
            trackCheckoutDiagnostic('wallet_confirm_succeeded', {
              transaction_id: checkoutTransactionId,
              wallet_type: walletType,
              intent_type: 'payment_intent',
            });
          }

          const conversion = await convertLeadToTrial(
            {
              subscriptionId: setupData.subscriptionId,
              paymentIntentId: setupData.paymentIntentId,
              paymentIntentClientSecret: setupData.clientSecret,
              customerId: setupData.customerId,
              email: contactData.email,
              phoneNumber: contactData.phone,
              terms: termsPayload,
              ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
              productSlugs: [productSlug || 'starter-plan'],
              ...salesSignupV4Attribution,
              cartMetadata: {
                productSlug: productSlug || 'starter-plan',
                experiment_variant: experimentVariant,
                ...checkoutDesignEventProps,
                ...checkoutMonetaryEventProps,
                ...salesPageExperimentParams,
                ...salesSignupV4Attribution,
                billing_period: billingPeriod,
                stripe_mode: stripeMode,
                payment_source: paymentSource,
                wallet_type: walletType,
              },
              items: [
                {
                  productSlug: productSlug || 'starter-plan',
                  planKey: planKey,  // Pass planKey for correct ProductPlan selection
                  quantity: 1,
                },
              ],
              // BUGFIX: Pass promo code to subscription creation for discount to be applied
              // Previously promoCode was sent to create-payment-intent but not to convert-lead-to-trial
              ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
              ...affiliateFields,
            },
            { apiUrl: API_URL, authToken: token },
          );

          await maybeConfirmInvoicePayment(conversion);
          pushEvent({
            event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
            transaction_id: conversion.subscriptionId || confirmedPaymentIntent.id,
            value: centsToUsd(checkoutAmountDueTodayCents),
            currency: 'USD',
            plan_type: productSlug || 'starter-plan',
            source: location.pathname,
            userId,
            user_type: isAuthenticated ? 'registered' : 'guest',
            customerId: conversion.customerId,
            paymentMethodId: confirmedPaymentIntent.payment_method || setupData.paymentMethodId,
            lead_transaction_id: leadTransactionId,
            experiment_variant: experimentVariant,
            experiment_name: checkoutExperimentName,
            ...checkoutDesignEventProps,
            ...checkoutMonetaryEventProps,
            checkout_payment_result: 'initial_payment_collected',
            payment_source: paymentSource,
            wallet_type: walletType,
            how_found_us: contactData.howFoundUs || null,
            how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
            ...salesPageExperimentParams,
            ...salesSignupV4Attribution,
            ...utmData,
          });

          try {
            refreshPaymentStatus();
          } catch (refreshError) {
            console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
          }

          checkoutFailureStage = 'account_handoff';
          await attemptAutoLoginFlow(conversion, {
            paymentIntentId: confirmedPaymentIntent.id,
            paymentIntentClientSecret: setupData.clientSecret,
          });
          return;
        }

        const cardNumberElement = isCheckoutRedesign && !isWalletPayment && !isDynamicPayment && !isBankPayment
          ? elements?.getElement?.(CardNumberElement)
          : null;

        if (isCheckoutRedesign && !isWalletPayment && !isDynamicPayment && !isBankPayment && !cardNumberElement) {
          trackCheckoutDiagnostic('stripe_elements_submit_failed', {
            transaction_id: checkoutTransactionId,
            stripe_error_type: 'card_element_missing',
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          const cardMessage = 'Card form is still loading. Please try again.';
          setError(cardMessage);
          setProcessing(false);
          return;
        }

        if (!isCheckoutRedesign && !isWalletPayment && !isBankPayment) {
          const { error: submitError } = await elements.submit();
          if (submitError) {
            trackCheckoutDiagnostic('stripe_elements_submit_failed', {
              transaction_id: checkoutTransactionId,
              stripe_error_type: submitError.type || null,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setError(submitError.message || 'Failed to submit payment details.');
            setProcessing(false);
            return;
          }
        }

        trackCheckoutDiagnostic('stripe_confirm_started', {
          transaction_id: checkoutTransactionId,
          intent_type: 'setup_intent',
          payment_source: paymentSource,
          wallet_type: walletType,
        });

        let stripeError = null;
        let setupIntent = null;
        if (isBankPayment && !isDynamicPayment) {
          if (
            typeof stripe.collectBankAccountForSetup !== 'function' ||
            typeof stripe.confirmUsBankAccountSetup !== 'function'
          ) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'setup_intent',
              stripe_error_type: 'bank_api_unavailable',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const bankUnavailableMessage = 'Bank payment is not available in this browser. Please use card instead.';
            setError(bankUnavailableMessage);
            setProcessing(false);
            return;
          }

          trackCheckoutDiagnostic('stripe_bank_account_collection_started', {
            transaction_id: checkoutTransactionId,
            intent_type: 'setup_intent',
            payment_source: paymentSource,
          });

          const {
            error: bankCollectionError,
            setupIntent: collectedSetupIntent,
          } = await stripe.collectBankAccountForSetup(bankCollectionOptions);

          if (bankCollectionError) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'setup_intent',
              stripe_error_type: bankCollectionError.type || 'bank_collection_failed',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setError(bankCollectionError.message || 'Bank connection failed. Please try again or use card.');
            setProcessing(false);
            return;
          }

          if (!collectedSetupIntent || collectedSetupIntent.status === 'requires_payment_method') {
            trackCheckoutDiagnostic('checkout_submit_blocked', {
              transaction_id: checkoutTransactionId,
              blocked_reason: 'bank_connection_cancelled',
              payment_source: paymentSource,
            });
            setError('Bank connection was cancelled. Choose bank again or use card.');
            setProcessing(false);
            return;
          }

          if (collectedSetupIntent.status === 'requires_confirmation') {
            stripeConfirmationAttempted = true;
            const bankConfirmResult = await stripe.confirmUsBankAccountSetup(setupData.clientSecret);
            stripeError = bankConfirmResult.error || null;
            setupIntent = bankConfirmResult.setupIntent || null;
          } else {
            setupIntent = collectedSetupIntent;
          }
        } else {
          stripeConfirmationAttempted = true;
          const confirmResult = await confirmSetupWithClient(stripe, isElementsWalletPayment || isDynamicPayment ? elements : isWalletPayment ? null : isCheckoutRedesign ? null : elements, {
            clientSecret: setupData.clientSecret,
            confirmParams: isWalletPayment ? walletConfirmParams : isDynamicPayment ? dynamicPaymentConfirmParams : {
              return_url: `${window.location.origin}/set-password?email=${encodeURIComponent(
                contactData.email,
              )}&name=${encodeURIComponent(contactData.name)}&redirectTo=onboarding&source_flow=landing_checkout_setup_redirect`,
            },
            redirect: 'if_required',
            ...(isCheckoutRedesign && !isWalletPayment && !isDynamicPayment ? {
              cardElement: cardNumberElement,
              billingDetails: {
                name: contactData.name,
                email: contactData.email,
                phone: contactData.phone,
              },
            } : {}),
          });
          stripeError = confirmResult.error || null;
          setupIntent = confirmResult.setupIntent || null;
        }

        if (stripeError) {
          trackCheckoutDiagnostic('stripe_confirm_failed', {
            transaction_id: checkoutTransactionId,
            intent_type: 'setup_intent',
            stripe_error_type: stripeError.type || null,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          const stripeMessage = stripeError.message || 'Failed to confirm payment method.';
          setError(stripeMessage);
          failWalletPayment(stripeMessage);
          setProcessing(false);
          return;
        }

        if (!setupIntent || setupIntent.status !== 'succeeded') {
          const bankVerificationRequired =
            isBankPayment &&
            (setupIntent?.status === 'requires_action' ||
              setupIntent?.next_action?.type === 'verify_with_microdeposits');
          trackCheckoutDiagnostic('stripe_confirm_failed', {
            transaction_id: checkoutTransactionId,
            intent_type: 'setup_intent',
            setup_intent_status: setupIntent?.status || null,
            blocked_reason: bankVerificationRequired ? 'bank_verification_required' : 'unexpected_setup_intent_status',
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          const setupStatusMessage = bankVerificationRequired
            ? 'Bank account verification is needed before checkout can finish. Use card for instant access.'
            : 'Payment method validation failed. Please try again.';
          setError(setupStatusMessage);
          failWalletPayment(setupStatusMessage);
          setProcessing(false);
          return;
        }

        trackCheckoutDiagnostic('stripe_confirm_succeeded', {
          transaction_id: checkoutTransactionId,
          intent_type: 'setup_intent',
          setup_intent_status: setupIntent.status,
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        stripeConfirmationCompleted = true;
        checkoutFailureStage = 'conversion';
        if (isWalletPayment) {
          completeWalletPayment('success');
          trackCheckoutDiagnostic('wallet_confirm_succeeded', {
            transaction_id: checkoutTransactionId,
            wallet_type: walletType,
            intent_type: 'setup_intent',
          });
        }

        const conversion = await convertLeadToTrial(
            {
              setupIntentId: setupIntent.id,
              setupIntentClientSecret: setupData.clientSecret,
              customerId: setupIntent.customer || setupData.customerId,
            email: contactData.email,
            phoneNumber: contactData.phone,
            terms: termsPayload,
            ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
            productSlugs: [productSlug || 'starter-plan'],
            ...(isBankPayment ? { bankVerificationMethod } : {}),
            ...salesSignupV4Attribution,
            cartMetadata: {
              productSlug: productSlug || 'starter-plan',
              experiment_variant: experimentVariant,
              ...checkoutDesignEventProps,
              ...checkoutMonetaryEventProps,
              ...salesPageExperimentParams,
              ...salesSignupV4Attribution,
              billing_period: billingPeriod,
              stripe_mode: stripeMode,
              payment_source: paymentSource,
              wallet_type: walletType,
              bank_verification_method: isBankPayment ? bankVerificationMethod : undefined,
            },
            items: [
              {
                productSlug: productSlug || 'starter-plan',
                planKey: planKey,  // Pass planKey for correct ProductPlan selection
                quantity: 1,
              },
            ],
            // BUGFIX: Pass promo code to subscription creation for discount to be applied
            ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
            ...affiliateFields,
          },
          { apiUrl: API_URL, authToken: token },
        );

        await maybeConfirmInvoicePayment(conversion);
        pushEvent({
          event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
          transaction_id: conversion.subscriptionId || setupIntent.id,
          value: 0,
          currency: 'USD',
          plan_type: productSlug || 'starter-plan',
          source: location.pathname,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
          customerId: conversion.customerId,
          paymentMethodId: setupIntent.payment_method,
          lead_transaction_id: leadTransactionId,
          experiment_variant: experimentVariant,
          experiment_name: checkoutExperimentName,
          ...checkoutDesignEventProps,
          ...checkoutMonetaryEventProps,
          checkout_payment_result: 'payment_method_setup_completed',
          payment_source: paymentSource,
          wallet_type: walletType,
          how_found_us: contactData.howFoundUs || null,
          how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
          ...salesPageExperimentParams,
          ...salesSignupV4Attribution,
          ...utmData,
        });

        try {
          refreshPaymentStatus();
        } catch (refreshError) {
          console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
        }

        checkoutFailureStage = 'account_handoff';
        await attemptAutoLoginFlow(conversion, {
          setupIntentId: setupIntent.id,
          setupIntentClientSecret: setupData.clientSecret,
        });
      } catch (submitError) {
        console.error('[StarterCheckoutPage] Error processing payment:', submitError);
        const postConfirmationFailure = isCheckoutRedesign && stripeConfirmationCompleted;
        const confirmationOutcomeUnknown =
          isCheckoutRedesign && stripeConfirmationAttempted && !stripeConfirmationCompleted;
        trackCheckoutDiagnostic('checkout_unhandled_error', {
          error_message: submitError?.message || 'unknown_error',
          payment_source: paymentSource,
          wallet_type: walletType,
          ...(isCheckoutRedesign ? {
            failure_stage: checkoutFailureStage,
            stripe_confirmation_attempted: stripeConfirmationAttempted,
            stripe_confirmation_completed: stripeConfirmationCompleted,
            retry_safe: !stripeConfirmationAttempted && !stripeConfirmationCompleted,
          } : {}),
        });
        if (postConfirmationFailure) {
          trackCheckoutDiagnostic('checkout_post_confirmation_failed', {
            failure_stage: checkoutFailureStage,
            retry_safe: false,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
        }
        if (confirmationOutcomeUnknown) {
          trackCheckoutDiagnostic('checkout_confirmation_outcome_unknown', {
            failure_stage: checkoutFailureStage,
            retry_safe: false,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
        }
        if (postConfirmationFailure || confirmationOutcomeUnknown) {
          setLocalCheckoutRecoveryRequired(true);
          onCheckoutRecoveryRequired?.();
        }
        const fallbackMessage = postConfirmationFailure
          ? 'Your payment step completed, but account setup did not. Do not submit again; contact support.'
          : confirmationOutcomeUnknown
            ? 'We could not verify whether your payment completed. Do not submit again; contact support.'
            : 'An unexpected error occurred. Please try again.';
        setError(fallbackMessage);
        failWalletPayment(fallbackMessage);
        setProcessing(false);
      }
    },
    [
      activePrompt,
      checkoutDesignEventProps,
      checkoutDesignExperimentActive,
      checkoutDesignVariant,
      checkoutExperimentName,
      checkoutMonetaryEventProps,
      checkoutAmountDueTodayCents,
      checkoutRecoveryBlocked,
      contactComplete,
      contactData.email,
      contactData.howFoundUs,
      contactData.howFoundUsOtherText,
      contactData.name,
      contactData.phone,
      elements,
      getApiToken,
      isAuthenticated,
      isCheckoutRedesign,
      isValidatingCoupon,
      leadTransactionId,
      location.pathname,
      dynamicPaymentElementMounted,
      bankVerificationMethod,
      paymentElementReady,
      paymentMethodAvailabilityChecked,
      paymentMethodAvailabilityError,
      productSlug,
      processing,
      selectedPaymentMethod,
      stripe,
      termsAccepted,
      utmData,
      user,
      refreshPaymentStatus,
      salesPageExperimentParams,
      salesSignupV4Attribution,
      variantParam,
      attemptAutoLoginFlow,
      experimentVariant,
      planKey,
      billingPeriod,
      effectiveCoupon,
      maybeConfirmInvoicePayment,
      onCheckoutRecoveryRequired,
      attributionParams,
      stripeMode,
      trackCheckoutDiagnostic,
      howFoundUsComplete,
      selectedSavedPaymentMethodId,
    ],
  );


  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  const paymentMethodConfig = paymentMethodAvailability || {};
  const redesignPaymentElementVisible = isCheckoutRedesign;
  const cardPaymentMethodAvailable =
    !isCheckoutRedesign ||
    (paymentMethodAvailabilityChecked && Boolean(paymentMethodConfig.card));
  const bankPaymentMethodConfigured =
    isCheckoutRedesign &&
    paymentMethodAvailabilityChecked &&
    Boolean(paymentMethodConfig.us_bank_account);
  const linkPaymentMethodConfigured =
    isCheckoutRedesign &&
    paymentMethodAvailabilityChecked &&
    Boolean(paymentMethodConfig.link);
  const applePayPaymentMethodConfigured =
    !isCheckoutRedesign ||
    (paymentMethodAvailabilityChecked && Boolean(paymentMethodConfig.applePay));
  const googlePayPaymentMethodConfigured =
    !isCheckoutRedesign ||
    (paymentMethodAvailabilityChecked && Boolean(paymentMethodConfig.googlePay));
  const linkCheckoutMounted = linkPaymentMethodConfigured && (linkCheckoutExpected || linkCheckoutAvailable);
  const walletCheckoutAvailableOrPending =
    (paymentMethodAvailabilityChecked && expressCheckoutAvailable) ||
    linkCheckoutMounted ||
    !expressCheckoutChecked;
  const bankPaymentMethodAvailable =
    bankPaymentMethodConfigured && (stripeMode === 'payment' || stripeMode === 'setup');
  const dynamicPaymentMethodsVisible =
    redesignPaymentElementVisible;
  const paymentChoiceAvailableOrPending =
    redesignPaymentElementVisible ||
    walletCheckoutAvailableOrPending ||
    cardPaymentMethodAvailable ||
    bankPaymentMethodAvailable ||
    dynamicPaymentMethodsVisible ||
    hasSavedPaymentMethods;
  const hasSelectedRedesignPaymentMethod = isCheckoutRedesign ? Boolean(selectedPaymentMethod) : true;
  const activePaymentMethod = isCheckoutRedesign
    ? selectedPaymentMethod
    : walletCheckoutAvailableOrPending && !manualPaymentMethodSelected
      ? 'wallet'
      : paymentChoiceAvailableOrPending
      ? selectedPaymentMethod
      : selectedPaymentMethod === 'bank'
      ? 'bank'
      : isRedesignDynamicPaymentMethod(selectedPaymentMethod)
      ? selectedPaymentMethod
      : 'card';
  const isDynamicPaymentActive = isCheckoutRedesign && isRedesignDynamicPaymentMethod(activePaymentMethod);
  const isBankPaymentActive = activePaymentMethod === 'bank';
  const isSavedPaymentActive = activePaymentMethod === 'saved';
  const activePaymentReady = !hasSelectedRedesignPaymentMethod
    ? false
    : isDynamicPaymentActive
    ? Boolean(dynamicPaymentElementMounted && stripe && elements)
    : isBankPaymentActive
      ? Boolean(stripe)
      : isSavedPaymentActive
        ? Boolean(selectedSavedPaymentMethodId)
      : paymentElementReady;
  const isSubmitDisabled =
    processing ||
    isValidatingCoupon ||
    elementsUpdating ||
    checkoutRecoveryBlocked ||
    !termsAccepted ||
    !contactComplete ||
    !activePaymentReady;
  const showRequiredAttention = validationAttempted && !processing;
  const validationReasonForAttention = showRequiredAttention
    ? getValidationState().validation_reason
    : null;
  const accountNeedsAttention = showRequiredAttention && !contactComplete;
  const paymentNeedsAttention = showRequiredAttention && !activePaymentReady;
  const termsNeedsAttention = showRequiredAttention && contactComplete && !termsAccepted;
  const visibleWalletMethods = paymentMethodAvailabilityChecked
    ? availableWalletMethods.filter((method) => (
      method === 'applePay'
        ? applePayPaymentMethodConfigured
        : method === 'googlePay'
          ? googlePayPaymentMethodConfigured
          : true
    ))
    : [];
  const cardFallbackVisible = paymentMethodAvailabilityChecked && cardPaymentMethodAvailable;
  const bankFallbackVisible = bankPaymentMethodAvailable;
  const savedFallbackVisible = hasSavedPaymentMethods && paymentMethodAvailabilityChecked;
  const dynamicFallbackVisible = dynamicPaymentMethodsVisible;
  const hasVisibleExpressMethods =
    visibleWalletMethods.length > 0 ||
    (linkPaymentMethodConfigured && linkCheckoutAvailable) ||
    cardFallbackVisible ||
    bankFallbackVisible ||
    dynamicFallbackVisible;
  const expressCheckoutVisible =
    isCheckoutRedesign &&
    !isValidatingCoupon &&
    hasVisibleExpressMethods;
  useEffect(() => {
    if (!isCheckoutRedesign || !paymentMethodAvailabilityChecked || !selectedPaymentMethod) return;

    const selectedMethodAvailable =
      (selectedPaymentMethod === 'card' && cardFallbackVisible) ||
      (selectedPaymentMethod === 'bank' && bankFallbackVisible) ||
      (selectedPaymentMethod === 'saved' && savedFallbackVisible) ||
      (selectedPaymentMethod === 'wallet' && expressCheckoutAvailable) ||
      isRedesignDynamicPaymentMethod(selectedPaymentMethod);

    if (!selectedMethodAvailable) {
      setSelectedPaymentMethod(null);
      setPaymentElementReady(false);
      setDynamicPaymentElementMounted(false);
      setDynamicPaymentElementReady(false);
    }
  }, [
    bankFallbackVisible,
    cardFallbackVisible,
    dynamicFallbackVisible,
    expressCheckoutAvailable,
    isCheckoutRedesign,
    paymentMethodAvailabilityChecked,
    savedFallbackVisible,
    selectedPaymentMethod,
  ]);

  const configuredWalletAmountCents = configuredPlanAmountCents;

  const walletRequestAmountCents = useMemo(() => (
    Math.max(
      0,
      appliedCoupon?.discountedAmount ?? (stripeMode === 'setup' ? 0 : configuredWalletAmountCents),
    )
  ), [appliedCoupon?.discountedAmount, configuredWalletAmountCents, stripeMode]);

  const walletPaymentRequestOptions = useMemo(() => ({
    country: 'US',
    currency: 'usd',
    total: {
      label: stripeMode === 'setup' ? `${planDisplayName} 7-day access` : planDisplayName,
      amount: walletRequestAmountCents,
    },
    requestPayerName: true,
    requestPayerEmail: true,
    requestPayerPhone: true,
  }), [planDisplayName, stripeMode, walletRequestAmountCents]);

  useEffect(() => {
    if (!isCheckoutRedesign) return undefined;

    // Wallets and Link are already owned by the redesign's PaymentElement.
    // Avoid a second browser capability probe and extra wallet SDK work.
    if (redesignPaymentElementVisible) return undefined;

    setExpressCheckoutChecked(false);
    setWalletPaymentRequest(null);
    setGooglePayButtonReady(false);
    setGooglePayButtonFailed(false);
    setLinkCheckoutExpected(true);
    updateCheckoutExpressAvailabilityCache({
      expressCheckoutChecked: false,
      linkCheckoutExpected: true,
    });

    if (isValidatingCoupon) {
      return undefined;
    }

    if (!stripe) {
      setExpressCheckoutChecked(true);
      setSelectedPaymentMethod((currentMethod) => (
        currentMethod === 'wallet' ? unavailableExpressFallbackPaymentMethod : currentMethod
      ));
      setManualPaymentMethodSelected(unavailableExpressFallbackIsManual);
      manualPaymentMethodSelectedRef.current = unavailableExpressFallbackIsManual;
      setLinkCheckoutExpected(false);
      setLinkCheckoutAvailable(false);
      setGooglePayButtonReady(false);
      setGooglePayButtonFailed(false);
      updateCheckoutExpressAvailabilityCache({
        expressCheckoutAvailable: false,
        expressCheckoutChecked: true,
        availableWalletMethods: [],
        linkCheckoutExpected: false,
        linkCheckoutAvailable: false,
      });
      return undefined;
    }

    setLinkCheckoutExpected(true);
    if (!manualPaymentMethodSelectedRef.current && !isCheckoutRedesign) {
      setSelectedPaymentMethod('wallet');
    }

    if (typeof stripe.paymentRequest !== 'function') {
      setExpressCheckoutChecked(true);
      setWalletPaymentRequest(null);
      setAvailableWalletMethods([]);
      setGooglePayButtonReady(false);
      setGooglePayButtonFailed(false);
      updateCheckoutExpressAvailabilityCache({
        expressCheckoutAvailable: false,
        expressCheckoutChecked: true,
        availableWalletMethods: [],
      });
      return undefined;
    }

    let isCancelled = false;
    const paymentRequest = stripe.paymentRequest(walletPaymentRequestOptions);

    const handleWalletPaymentMethod = async (event) => {
      const paymentMethodId = event?.paymentMethod?.id;
      const walletType =
        activeWalletTypeRef.current ||
        (event?.paymentMethod?.card?.wallet?.type === 'apple_pay' ? 'applePay' : null) ||
        (event?.paymentMethod?.card?.wallet?.type === 'google_pay' ? 'googlePay' : null);

      if (!paymentMethodId) {
        event?.complete?.('fail');
        setError('Apple Pay or Google Pay did not return a payment method. Please try card instead.');
        trackCheckoutDiagnosticRef.current?.('wallet_confirm_failed', {
          wallet_type: walletType,
          failure_reason: 'missing_payment_method',
        });
        activeWalletTypeRef.current = null;
        return;
      }

      await handleSubmitRef.current?.({
        paymentSource: 'wallet',
        walletType,
        walletPaymentMethodId: paymentMethodId,
        walletComplete: event?.complete?.bind(event),
        walletBillingDetails: {
          name: event?.payerName || event?.paymentMethod?.billing_details?.name || '',
          email: event?.payerEmail || event?.paymentMethod?.billing_details?.email || '',
          phone: event?.payerPhone || event?.paymentMethod?.billing_details?.phone || '',
        },
      });
      activeWalletTypeRef.current = null;
    };

    const handleWalletCancel = () => {
      trackCheckoutDiagnosticRef.current?.('wallet_cancelled', {
        wallet_type: activeWalletTypeRef.current || null,
      });
      activeWalletTypeRef.current = null;
    };

    paymentRequest.on?.('paymentmethod', handleWalletPaymentMethod);
    paymentRequest.on?.('cancel', handleWalletCancel);

    paymentRequest.canMakePayment()
      .then((result) => {
        if (isCancelled) return;
        const availableMethods = [
          result?.applePay ? 'applePay' : null,
          result?.googlePay ? 'googlePay' : null,
        ].filter(Boolean);
        const hasWallets = availableMethods.length > 0;
        const hasAnyExpressMethod = hasWallets || checkoutExpressAvailabilityCache.linkCheckoutAvailable;
        setExpressCheckoutChecked(true);
        setExpressCheckoutAvailable(hasAnyExpressMethod);
        setWalletPaymentRequest(hasWallets ? paymentRequest : null);
        setAvailableWalletMethods(availableMethods);
        updateCheckoutExpressAvailabilityCache({
          expressCheckoutAvailable: hasAnyExpressMethod,
          expressCheckoutChecked: true,
          availableWalletMethods: availableMethods,
        });
        if (!availableMethods.includes('googlePay')) {
          setGooglePayButtonReady(false);
          setGooglePayButtonFailed(false);
        }

        if (hasWallets) {
          if (!manualPaymentMethodSelectedRef.current && !isCheckoutRedesign) {
            setSelectedPaymentMethod('wallet');
          }
          if (!expressCheckoutAvailableTrackedRef.current) {
            expressCheckoutAvailableTrackedRef.current = true;
            trackCheckoutDiagnosticRef.current?.('wallet_available', {
              available_wallets: availableMethods,
              payment_request_link_available: Boolean(result?.link),
            });
          }
        }
      })
      .catch((walletError) => {
        if (isCancelled) return;
        setExpressCheckoutChecked(true);
        setExpressCheckoutAvailable(checkoutExpressAvailabilityCache.linkCheckoutAvailable);
        setWalletPaymentRequest(null);
        setAvailableWalletMethods([]);
        setGooglePayButtonReady(false);
        setGooglePayButtonFailed(false);
        updateCheckoutExpressAvailabilityCache({
          expressCheckoutAvailable: checkoutExpressAvailabilityCache.linkCheckoutAvailable,
          expressCheckoutChecked: true,
          availableWalletMethods: [],
        });
        trackCheckoutDiagnosticRef.current?.('wallet_load_failed', {
          stripe_error_type: walletError?.type || null,
          stripe_error_code: walletError?.code || null,
        });
      });

    return () => {
      isCancelled = true;
      paymentRequest.off?.('paymentmethod', handleWalletPaymentMethod);
      paymentRequest.off?.('cancel', handleWalletCancel);
    };
  }, [
    isCheckoutRedesign,
    isValidatingCoupon,
    redesignPaymentElementVisible,
    stripe,
    unavailableExpressFallbackIsManual,
    unavailableExpressFallbackPaymentMethod,
    walletPaymentRequestOptions,
  ]);

  const handleWalletPaymentClick = useCallback((walletType) => {
    trackCheckoutDiagnostic('wallet_clicked', {
      wallet_type: walletType,
      contact_complete: Boolean(contactComplete),
      terms_accepted: Boolean(termsAccepted),
    });

    if (!walletPaymentRequest) {
      setError('Apple Pay or Google Pay is not available in this browser. Please use card instead.');
      setSelectedPaymentMethod('card');
      trackCheckoutDiagnostic('wallet_click_blocked', {
        blocked_reason: 'wallet_not_available',
        wallet_type: walletType,
      });
      return;
    }

    if (!contactComplete) {
      setError('Please enter your contact details before using Apple Pay or Google Pay.');
      trackCheckoutDiagnostic('wallet_click_blocked', {
        blocked_reason: 'contact_incomplete',
        wallet_type: walletType,
      });
      return;
    }

    if (!termsAccepted) {
      setTermsError(true);
      setError(TERMS_ACCEPTANCE_ERROR_MESSAGE);
      trackCheckoutDiagnostic('wallet_click_blocked', {
        blocked_reason: 'terms_not_accepted',
        wallet_type: walletType,
      });
      return;
    }

    setError('');
    manualPaymentMethodSelectedRef.current = false;
    setManualPaymentMethodSelected(false);
    cardPaymentSelectedByUserRef.current = false;
    setSelectedPaymentMethod('wallet');
    activeWalletTypeRef.current = walletType;

    try {
      walletPaymentRequest.update?.({
        total: walletPaymentRequestOptions.total,
      });
      walletPaymentRequest.show();
    } catch (walletError) {
      activeWalletTypeRef.current = null;
      setError('Apple Pay or Google Pay could not open. Please try card instead.');
      trackCheckoutDiagnostic('wallet_load_failed', {
        wallet_type: walletType,
        stripe_error_type: walletError?.type || null,
        stripe_error_code: walletError?.code || null,
      });
    }
  }, [
    contactComplete,
    termsAccepted,
    trackCheckoutDiagnostic,
    walletPaymentRequest,
    walletPaymentRequestOptions.total,
  ]);

  useEffect(() => {
    googlePayWalletClickRef.current = handleWalletPaymentClick;
  }, [handleWalletPaymentClick]);

  useEffect(() => {
    if (
      !isCheckoutRedesign ||
      !expressCheckoutVisible ||
      !availableWalletMethods.includes('googlePay') ||
      !googlePayButtonContainerRef.current
    ) {
      setGooglePayButtonReady(false);
      setGooglePayButtonFailed(false);
      return undefined;
    }

    let isCancelled = false;
    const container = googlePayButtonContainerRef.current;
    container.innerHTML = '';
    setGooglePayButtonReady(false);
    setGooglePayButtonFailed(false);

    loadGooglePayScript()
      .then(() => {
        if (isCancelled || !window.google?.payments?.api || !container) return;

        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST',
        });
        const googlePayButton = paymentsClient.createButton({
          buttonColor: checkoutTheme === 'dark' ? 'white' : 'black',
          buttonType: 'plain',
          buttonRadius: 4,
          buttonSizeMode: 'fill',
          onClick: () => googlePayWalletClickRef.current?.('googlePay'),
        });
        googlePayButton.classList.add('checkout-redesign-google-pay-generated-button');
        googlePayButton.setAttribute('aria-label', 'Google Pay');
        googlePayButton.setAttribute('title', 'Google Pay');
        googlePayButton.querySelectorAll('button, [role="button"]').forEach((buttonEl) => {
          buttonEl.setAttribute('aria-label', 'Google Pay');
          buttonEl.setAttribute('title', 'Google Pay');
        });
        container.innerHTML = '';
        container.appendChild(googlePayButton);
        setGooglePayButtonFailed(false);
        setGooglePayButtonReady(true);
      })
      .catch((googlePayError) => {
        if (isCancelled) return;
        setGooglePayButtonReady(false);
        setGooglePayButtonFailed(true);
        trackCheckoutDiagnosticRef.current?.('google_pay_button_load_failed', {
          error_message: googlePayError?.message || null,
        });
      });

    return () => {
      isCancelled = true;
      if (container) {
        container.innerHTML = '';
      }
      setGooglePayButtonReady(false);
      setGooglePayButtonFailed(false);
    };
  }, [
    availableWalletMethods,
    checkoutTheme,
    expressCheckoutVisible,
    isCheckoutRedesign,
  ]);

  useEffect(() => {
    if (
      !isCheckoutRedesign ||
      !expressCheckoutVisible ||
      !availableWalletMethods.includes('applePay')
    ) {
      return undefined;
    }

    let isCancelled = false;
    loadApplePayScript()
      .catch((applePayError) => {
        if (isCancelled) return;
        trackCheckoutDiagnosticRef.current?.('apple_pay_button_load_failed', {
          error_message: applePayError?.message || null,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [
    availableWalletMethods,
    expressCheckoutVisible,
    isCheckoutRedesign,
  ]);

  const paymentElementOptions = useMemo(
    () => ({
      layout: { type: 'tabs', defaultCollapsed: false },
      wallets: {
        applePay: process.env.NODE_ENV === 'production' ? 'auto' : 'never',
        googlePay: process.env.NODE_ENV === 'production' ? 'auto' : 'never',
      },
      // Hide Stripe's internal terms text since we have our own Terms of Service checkbox
      // This prevents the "By providing your card..." text from being cut off on mobile
      terms: {
        card: 'never',
        applePay: 'never',
        googlePay: 'never',
      },
      defaultValues: {
        billingDetails: {
          name: contactData.name || undefined,
          email: contactData.email || undefined,
          phone: contactData.phone || undefined,
        },
      },
    }),
    [contactData.email, contactData.name, contactData.phone],
  );

  const redesignDynamicPaymentElementOptions = useMemo(
    () => ({
      layout: {
        type: 'tabs',
        defaultCollapsed: false,
      },
      business: { name: 'BotSpot' },
      wallets: {
        applePay: 'auto',
        googlePay: 'auto',
        link: 'auto',
      },
      fields: {
        billingDetails: {
          name: 'never',
          email: 'never',
          phone: 'never',
          address: 'if_required',
        },
      },
      terms: {
        card: 'never',
        applePay: 'never',
        googlePay: 'never',
        link: 'never',
      },
      defaultValues: {
        billingDetails: {
          name: contactData.name || undefined,
          email: contactData.email || undefined,
          phone: contactData.phone || undefined,
        },
      },
    }),
    [
      contactData.email,
      contactData.name,
      contactData.phone,
    ],
  );

  const handleDynamicPaymentElementReady = useCallback(() => {
    setDynamicPaymentElementMounted(true);
    trackCheckoutDiagnostic('stripe_dynamic_payment_element_ready');
    if (!paymentElementReadyTrackedRef.current) {
      paymentElementReadyTrackedRef.current = true;
      trackCheckoutDiagnostic('stripe_elements_ready', {
        duration_ms: Math.max(0, Date.now() - checkoutMountedAtMsRef.current),
        payment_element_variant: 'dynamic',
      });
    }
  }, [trackCheckoutDiagnostic]);

  const handleDynamicPaymentElementChange = useCallback((event) => {
    setSelectedPaymentMethod(REDESIGN_STRIPE_PAYMENT_ELEMENT_METHOD);
    setDynamicPaymentElementReady(Boolean(event?.complete));
    if (event?.error?.message) {
      paymentElementValidationErrorRef.current = event.error.message;
      setError(event.error.message);
      return;
    }
    if (paymentElementValidationErrorRef.current && error === paymentElementValidationErrorRef.current) {
      paymentElementValidationErrorRef.current = '';
      setError('');
    }
  }, [error]);

  const handleDynamicPaymentElementLoadError = useCallback((paymentError) => {
    setDynamicPaymentElementMounted(true);
    setDynamicPaymentElementReady(false);
    const errorMessage = paymentError?.error?.message || paymentError?.message || 'unknown_error';
    trackCheckoutDiagnostic('stripe_dynamic_payment_element_load_error', {
      error_message: errorMessage,
    });
    trackCheckoutDiagnostic('stripe_elements_load_error', {
      error_message: errorMessage,
      duration_ms: Math.max(0, Date.now() - checkoutMountedAtMsRef.current),
      payment_element_variant: 'dynamic',
    });
    setError('Payment form failed to load. Refresh this page and try again.');
  }, [trackCheckoutDiagnostic]);

  const displayPriceText = (priceText || '').replace(/^only\s+/i, '');
  const redesignCurrentPriceText = isAnnual
    ? (annualPriceText || displayPriceText)
    : displayPriceText;
  const redesignDueTodayText = formatCheckoutAmount(checkoutAmountDueTodayCents) || '$0';
  const redesignRenewalCadence = isAnnual ? 'yearly' : 'monthly';
  const billingCycleLabel = isAnnual ? 'year' : 'month';
  const redesignRenewalAmountCents = checkoutFutureRecurringAmountCents;
  const redesignRenewalAmountText = formatCheckoutAmount(redesignRenewalAmountCents) || redesignCurrentPriceText;
  const redesignRenewalPriceText = `${redesignRenewalAmountText}/${billingCycleLabel}`;
  const redesignInitialRecurringAmountText =
    formatCheckoutAmount(checkoutChargeAmountCents) || redesignCurrentPriceText;
  const redesignInitialRecurringPriceText =
    `${redesignInitialRecurringAmountText}/${billingCycleLabel}`;
  const redesignRenewalTermsText =
    effectivePromotionDuration === 'repeating' && effectivePromotionDurationInMonths
      ? isAnnual
        ? `The discount expires after ${effectivePromotionDurationInMonths} months; your next annual renewal is ${redesignRenewalPriceText}, then yearly until canceled.`
        : `The discount lasts ${effectivePromotionDurationInMonths} months; after that, it renews monthly at ${redesignRenewalPriceText} until canceled.`
      : effectivePromotionDuration === 'once'
        ? `The discount applies to the first billing cycle; your next ${isAnnual ? 'annual' : 'monthly'} renewal is ${redesignRenewalPriceText}, then continues until canceled.`
        : `This subscription renews ${redesignRenewalCadence} at ${redesignRenewalPriceText} until canceled.`;
  const redesignTermsAuthorizationText = !isRecurringPlan
    ? `authorize BotSpot to charge my payment method ${redesignDueTodayText} today for this one-time purchase.`
    : stripeMode === 'setup'
      ? 'authorize BotSpot to save my payment method.'
      : `authorize BotSpot to charge my payment method ${redesignDueTodayText} today.`;
  const redesignTermsSupportingText = !isRecurringPlan
    ? null
    : stripeMode === 'setup'
      ? trialDays > 0
        ? `Nothing is charged today. After ${trialDays} days, the subscription starts at ${redesignInitialRecurringPriceText}. ${redesignRenewalTermsText} Cancel anytime in Account settings before renewal.`
        : `Nothing is charged today. The subscription starts now at ${redesignInitialRecurringPriceText}. ${redesignRenewalTermsText} Cancel anytime in Account settings before renewal.`
      : `${redesignRenewalTermsText} Cancel anytime in Account settings before renewal.`;
  const redesignTermsConsentText = [redesignTermsAuthorizationText, redesignTermsSupportingText]
    .filter(Boolean)
    .join(' ');
  const redesignManualSubmitLabel = `Start ${planDisplayName} · Pay ${redesignDueTodayText} today`;
  const couponOriginalAmountCents = appliedCoupon
    ? (appliedCoupon.originalAmount ?? configuredWalletAmountCents)
    : null;
  const couponDiscountedAmountCents = appliedCoupon?.discountedAmount ?? null;
  const couponDiscountAmountCents =
    couponOriginalAmountCents != null && couponDiscountedAmountCents != null
      ? Math.max(0, couponOriginalAmountCents - couponDiscountedAmountCents)
      : null;
  const couponDiscountedAmountText = formatCheckoutAmount(couponDiscountedAmountCents);
  const couponDiscountAmountText = formatCheckoutAmount(couponDiscountAmountCents);
  const couponChangesDisplayedPrice =
    couponDiscountedAmountCents != null &&
    couponDiscountedAmountCents !== configuredWalletAmountCents;
  const checkoutDesignFormClass = error?.toLowerCase().includes('already subscribed')
    ? 'landing-checkout-form checkout-redesign-layout checkout-form-disabled'
    : 'landing-checkout-form checkout-redesign-layout';
  const redesignVisibleError = checkoutRecoveryBlocked
    ? (/contact support/i.test(error || '') ? error : CHECKOUT_RECOVERY_MESSAGE)
    : error;

  const useStaticBillingSummary = Boolean(variantConfig.fixedBillingPeriod || !annualPlanKey);

  const renderRedesignBillingToggle = () => {
    const monthlyBillingPriceText = monthlyPriceText || displayPriceText;
    const monthlyOriginalPriceText = monthlyOriginalPrice || defaultOriginalPrice;
    const monthlyButtonLabel = monthlyOriginalPriceText
      ? `Pay monthly, ${monthlyBillingPriceText}, was ${monthlyOriginalPriceText}`
      : `Pay monthly, ${monthlyBillingPriceText}`;
    const annualBillingPriceText = annualPriceText || annualMonthlyEquivalent;
    const annualButtonLabel = annualSavingsText
      ? `Pay annually, ${annualBillingPriceText}, ${annualSavingsText}`
      : `Pay annually, ${annualBillingPriceText}`;

    if (useStaticBillingSummary) {
      const fixedBillingTitle = !isRecurringPlan
        ? 'One-time purchase'
        : isAnnual
          ? 'Annual subscription'
          : 'Monthly subscription';
      const fixedBillingPriceText = isAnnual
        ? annualBillingPriceText
        : monthlyBillingPriceText;
      const fixedOriginalPriceText = isAnnual
        ? annualOriginalPrice
        : monthlyOriginalPriceText;

      return (
        <section
          className="checkout-redesign-billing-summary"
          aria-label={`${fixedBillingTitle}, ${fixedBillingPriceText}`}
        >
          <h2>Billing summary</h2>
          <div className="checkout-redesign-billing-summary__row">
            <span className="checkout-redesign-billing-summary__copy">
              <span className="checkout-redesign-billing-summary__price-line">
                <strong>{fixedBillingPriceText}</strong>
                {fixedOriginalPriceText && (
                  <span className="checkout-redesign-billing-summary__was">Was {fixedOriginalPriceText}</span>
                )}
              </span>
            </span>
            {fixedOriginalPriceText && (
              <span className="checkout-redesign-billing-summary__badge">Launch price</span>
            )}
          </div>
        </section>
      );
    }

    return (
      <div className="billing-toggle-section checkout-redesign-billing-toggle">
        <h2 className="checkout-redesign-billing-toggle__label">Billing options</h2>
        <div className="billing-toggle-container" role="group" aria-label="Billing period">
          <button
            type="button"
            className={`billing-toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
            aria-pressed={billingPeriod === 'monthly'}
            aria-label={monthlyButtonLabel}
            onClick={() => handleBillingPeriodChange('monthly')}
            disabled={processing || elementsUpdating}
          >
            <span className="billing-toggle-btn__radio" aria-hidden="true" />
            <span className="billing-toggle-btn__copy">
              <span className="billing-toggle-btn__title">Pay monthly</span>
              <span className="billing-toggle-btn__price-line">
                <span className="billing-toggle-btn__price">{monthlyBillingPriceText}</span>
                {monthlyOriginalPriceText && (
                  <span className="billing-toggle-btn__was">Was {monthlyOriginalPriceText}</span>
                )}
              </span>
            </span>
          </button>
          {annualPlanKey && (
            <button
              type="button"
              className={`billing-toggle-btn ${billingPeriod === 'annual' ? 'active' : ''}`}
              aria-pressed={billingPeriod === 'annual'}
              aria-label={annualButtonLabel}
              onClick={() => handleBillingPeriodChange('annual')}
              disabled={processing || elementsUpdating}
            >
              <span className="billing-toggle-btn__radio" aria-hidden="true" />
              <span className="billing-toggle-btn__copy">
                <span className="billing-toggle-btn__title">Pay annually</span>
                <span className="billing-toggle-btn__price-line">
                  <span className="billing-toggle-btn__price">{annualBillingPriceText}</span>
                  {annualOriginalPrice && (
                    <span className="billing-toggle-btn__was">Was {annualOriginalPrice}</span>
                  )}
                </span>
              </span>
              {annualSavingsText && (
                <span className="billing-savings-badge" aria-hidden="true">
                  <span className="billing-savings-badge__full">{annualSavingsText}</span>
                  <span className="billing-savings-badge__compact">
                    {annualSavingsText
                      .replace(/^50%\s*off\s*\+\s*/i, '')
                      .replace(/\bmonths\b/i, 'mo')}
                  </span>
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderRedesignCouponSummary = () => {
    if (!appliedCoupon || !couponChangesDisplayedPrice || couponDiscountedAmountText == null) {
      return null;
    }

    return (
      <div className="checkout-redesign-coupon-summary" aria-label="Updated total">
        <div className="checkout-redesign-coupon-summary__row">
          <span>Due today</span>
          <strong>{couponDiscountedAmountText}</strong>
        </div>
      </div>
    );
  };

  const renderRedesignPaymentElement = () => {
    return (
      <div
        className={`checkout-redesign-dynamic-payment-element${dynamicPaymentElementMounted ? '' : ' checkout-redesign-dynamic-payment-element--loading'}`}
        aria-label="Payment method"
      >
        {!dynamicPaymentElementMounted && (
          <div
            className="checkout-redesign-payment-loading"
            role="status"
            aria-live="polite"
          >
            <span className="checkout-redesign-payment-loading__icon" aria-hidden="true">
              <FaShieldAlt focusable="false" />
            </span>
            <span className="checkout-redesign-payment-loading__copy">
              <strong>Loading secure payment form…</strong>
              <span>Connecting to Stripe</span>
            </span>
          </div>
        )}
        <div
          className="checkout-redesign-payment-element-frame"
          aria-hidden={dynamicPaymentElementMounted ? undefined : 'true'}
        >
          <StripeErrorBoundary>
            <PaymentElement
              options={redesignDynamicPaymentElementOptions}
              onReady={handleDynamicPaymentElementReady}
              onChange={handleDynamicPaymentElementChange}
              onLoadError={handleDynamicPaymentElementLoadError}
            />
          </StripeErrorBoundary>
        </div>
      </div>
    );
  };

  const renderRedesignCouponSection = () => (
    <div className="coupon-code-section">
      {appliedCoupon ? (
        <div
          className="coupon-code-applied"
          aria-label={`Applied promo code ${appliedCoupon.code}`}
          aria-live="polite"
        >
          <div className="coupon-code-applied__info">
            <FaTag className="coupon-code-applied__icon" aria-hidden="true" focusable="false" />
            <span className="coupon-code-applied__code">{appliedCoupon.code}</span>
            <span className="coupon-code-applied__status">Applied</span>
          </div>
          {couponDiscountAmountCents > 0 && couponDiscountAmountText && (
            <span
              className="coupon-code-applied__saving"
              aria-label={`Discount ${couponDiscountAmountText}`}
            >
              −{couponDiscountAmountText}
            </span>
          )}
          <button
            type="button"
            className="coupon-code-applied__remove"
            onClick={handleRemoveCoupon}
            disabled={processing || isValidatingCoupon || elementsUpdating}
            aria-label={`Remove promo code ${appliedCoupon.code}`}
          >
            Remove
          </button>
        </div>
      ) : (
        <CouponCodeInput
          onApply={handleApplyCoupon}
          onClearValidationError={() => setCouponValidationError('')}
          isValidating={isValidatingCoupon}
          validationError={couponValidationError}
          disabled={processing || elementsUpdating}
          variant="compact"
        />
      )}
    </div>
  );

  const renderRedesignError = () => {
    if (!redesignVisibleError) return null;
    if (termsError && isTermsAcceptanceErrorMessage(redesignVisibleError)) return null;
    return (
      <div id="checkout-error-banner" className="checkout-error-banner" role="alert" aria-live="assertive">
        {redesignVisibleError.toLowerCase().includes('already subscribed') ? (
          <>
            You're already subscribed! No need to pay again.
            <Link to="/ai-agent" className="checkout-error-link">
              Go to AI Agent →
            </Link>
          </>
        ) : (
          redesignVisibleError
        )}
      </div>
    );
  };

  const renderRedesignTerms = () => (
    <>
      <label className={`terms-checkbox-group ${termsError || termsNeedsAttention ? 'error' : ''}`}>
        <input
          type="checkbox"
          className="terms-checkbox-input"
          id="landing-checkout-terms"
          aria-label={`I agree to the Terms of Service and ${redesignTermsConsentText}`}
          aria-invalid={termsError || termsNeedsAttention}
          aria-describedby={termsError || termsNeedsAttention ? 'checkout-terms-error' : undefined}
          checked={termsAccepted}
          onChange={(event) => {
            setTermsAccepted(event.target.checked);
            if (event.target.checked) {
              setTermsError(false);
              if (isTermsAcceptanceErrorMessage(error)) {
                setError('');
              }
            }
          }}
          disabled={processing}
          required
        />
        <span className="terms-text">
          <span className="terms-text__agreement">
            {termsNeedsAttention && (
              <span className="checkout-redesign-required-marker" aria-label="Required">*</span>
            )}
            I agree to the{' '}
            <a
              href="/terms"
              className="terms-link"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Service
            </a>{' '}
            and {redesignTermsAuthorizationText}
          </span>
          {redesignTermsSupportingText && (
            <span className="terms-text__support">{redesignTermsSupportingText}</span>
          )}
        </span>
      </label>
      {(termsError || termsNeedsAttention) && (
        <div id="checkout-terms-error" className="terms-error-text" role="alert" aria-live="assertive">
          Please accept the Terms of Service
        </div>
      )}
    </>
  );

  const renderRedesignSubmit = () => {
    const submitLabel = redesignManualSubmitLabel;
    const processingLabel = isBankPaymentActive ? 'Opening bank selector...' : 'Processing...';

    return (
      <div className="checkout-button-wrapper">
        <button
          ref={buttonRef}
          type="submit"
          className={`checkout-cta-button bsui-button bsui-button--primary bsui-button--full ${processing || isValidatingCoupon || elementsUpdating || checkoutRecoveryBlocked ? 'disabled' : ''}`}
          onClick={handleButtonClick}
          onMouseEnter={() => isSubmitDisabled && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          disabled={processing || isValidatingCoupon || elementsUpdating || checkoutRecoveryBlocked}
        >
          {processing ? processingLabel : isValidatingCoupon ? 'Applying coupon...' : elementsUpdating ? 'Updating total…' : submitLabel}
        </button>
        <Overlay target={buttonRef.current} show={showTooltip} placement="top">
          {(props) => (
            <Tooltip id="checkout-validation-tooltip" className="checkout-tooltip" {...props}>
              {getValidationMessage()}
            </Tooltip>
          )}
        </Overlay>
      </div>
    );
  };

  const renderRedesignContactSection = () => (
    <div
      className={`checkout-redesign-section checkout-redesign-section--contact ${accountNeedsAttention ? 'checkout-redesign-section--attention' : ''}`}
      aria-label="Account details"
    >
      <CheckoutContactFields
        value={contactData}
        onChange={handleContactChange}
        disabled={processing}
        invalidFields={{
          name: validationReasonForAttention === 'missing_name',
          email: validationReasonForAttention === 'missing_email',
          phone: validationReasonForAttention === 'missing_phone',
        }}
        className="checkout-contact-fields--expanded"
        collapseWhenComplete={false}
        editBehavior="live"
        showSmsConsent={false}
      />
      {contactData.howFoundUs ? (
        <HowFoundUsDropdown
          value={contactData.howFoundUs}
          otherText={contactData.howFoundUsOtherText}
          onChange={(val) => handleContactChange('howFoundUs', val)}
          onOtherTextChange={(val) => handleContactChange('howFoundUsOtherText', val)}
          disabled={processing}
        />
      ) : null}
    </div>
  );

  if (isCheckoutRedesign) {
    return (
      <>
        <form
          className={`${checkoutDesignFormClass}${useStaticBillingSummary ? ' checkout-redesign-layout--static-billing' : ''}`}
          onSubmit={handleSubmit}
        >
          <header className="checkout-redesign-hero" aria-label="Selected plan">
            <div className="checkout-redesign-hero__content">
              <span className="checkout-redesign-hero-mark" aria-label="BotSpot">
                <img
                  src={botspotLogo}
                  alt=""
                  width="56"
                  height="56"
                  className="checkout-redesign-hero-mark__image"
                  aria-hidden="true"
                />
              </span>
              <h1>
                {planDisplayName === 'BotSpot Light' ? (
                  <>BotSpot <span className="checkout-redesign-plan-accent">Light</span></>
                ) : planDisplayName}
              </h1>
              <p className="checkout-redesign-plan-copy">{redesignPlanDescription}</p>
            </div>
          </header>

          {useStaticBillingSummary && renderRedesignBillingToggle()}

          <div className="checkout-redesign-main-column">
            <div className="checkout-redesign-account-panel">
              {renderRedesignContactSection()}
            </div>

            <section className="checkout-redesign-form-panel" aria-label="Payment details">
              <div className={`checkout-redesign-section checkout-redesign-section--payment ${paymentNeedsAttention ? 'checkout-redesign-section--attention' : ''}`}>
                <h2 className="checkout-redesign-sr-only">
                  Payment method
                  {paymentNeedsAttention && (
                    <span className="checkout-redesign-required-marker" aria-label="Required">*</span>
                  )}
                </h2>
                {renderRedesignPaymentElement()}
                {paymentNeedsAttention && (
                  <div
                    id="checkout-payment-error"
                    className="checkout-redesign-payment-error"
                    role="alert"
                    aria-live="assertive"
                  >
                    Please complete your payment information
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="checkout-redesign-side-column">
            <aside
              className="checkout-redesign-summary"
              aria-label={useStaticBillingSummary ? 'Order options' : 'Billing options'}
            >
              <div className="checkout-redesign-summary__primary">
                {!useStaticBillingSummary && renderRedesignBillingToggle()}
                <div className="checkout-redesign-summary__promo">
                  {renderRedesignCouponSection()}
                </div>
                {renderRedesignCouponSummary()}
              </div>
            </aside>

            <div className="checkout-redesign-actions">
              {renderRedesignError()}
              <div className="checkout-redesign-actions-terms">
                {renderRedesignTerms()}
              </div>
              {renderRedesignSubmit()}
            </div>
          </div>

          <div className="checkout-security-badges checkout-redesign-security checkout-redesign-security--footer" aria-label="Payment protections">
            <span className="checkout-redesign-security__assurances">
              <span className="checkout-redesign-security__item">Secure checkout by Stripe</span>
              <span className="checkout-redesign-security__separator" aria-hidden="true">·</span>
              <span className="checkout-redesign-security__item">30-day money-back guarantee</span>
            </span>
            <span className="checkout-redesign-security__legal">{redesignCheckoutNote}</span>
          </div>
        </form>

      </>
    );
  }

  return (
    <form className={`landing-checkout-form checkout-form-card ${error?.toLowerCase().includes('already subscribed') ? 'checkout-form-disabled' : ''}`} onSubmit={handleSubmit}>
      <CheckoutContactFields
        value={contactData}
        onChange={handleContactChange}
        disabled={processing}
        autoFocus
        collapseWhenComplete={contactWasPrefilledOnLoad}
        showSmsConsent={false}
      />

      {/* Billing Period Toggle - only shown for variants that have an annual plan configured.
          Light variant has no annualPlanKey yet so we hide the toggle to avoid bad-UX state
          where clicking Annual would fail at the backend (no matching ProductPlan row). */}
      {annualPlanKey && (
        <div className="billing-toggle-section">
          <div className="billing-toggle-container">
            <button
              type="button"
              className={`billing-toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => handleBillingPeriodChange('monthly')}
              disabled={processing}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`billing-toggle-btn ${billingPeriod === 'annual' ? 'active' : ''}`}
              onClick={() => handleBillingPeriodChange('annual')}
              disabled={processing}
            >
              Annual
              {annualSavingsText && (
                <span className="billing-savings-badge">{annualSavingsText}</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Show trial callout for trial variants, discount banner for discount variant, or price callout for no_trial */}
      {showUrgencyTimer ? (
        <div className="checkout-urgency-banner">
          <div className="urgency-pricing">
            <span className="urgency-original-price">Was {originalPrice}</span>
            <span className="urgency-current-price">{priceText}</span>
            <span className="urgency-badge">Launch discount applied</span>
          </div>
          {/* Show monthly equivalent when annual is selected */}
          {isAnnual && annualMonthlyEquivalent && (
            <div className="urgency-monthly-equivalent">
              (Just {annualMonthlyEquivalent})
            </div>
          )}
          <div className="urgency-message">
            Price shown reflects the current launch discount.
          </div>
        </div>
      ) : (
        <CheckoutTrialCallout
          trialDays={trialDays}
          priceText={priceText}
          annualMonthlyEquivalent={isAnnual ? annualMonthlyEquivalent : null}
        />
      )}

      <section className="checkout-next-steps-panel" aria-label="What happens after payment">
        <div className="checkout-next-steps-panel__eyebrow">What happens next</div>
        <h2>{checkoutNextStep.title}</h2>
        <div className="checkout-next-steps-panel__item">
          <FaCheckCircle className="checkout-next-steps-panel__icon" />
          <span>{checkoutNextStep.primary}</span>
        </div>
        <div className="checkout-next-steps-panel__item">
          <FaCheckCircle className="checkout-next-steps-panel__icon" />
          <span>{checkoutNextStep.secondary}</span>
        </div>
        <p>BotSpot helps automate your rules. It is software, not investment advice.</p>
      </section>

      <CheckoutPaymentContainer>
        <StripeErrorBoundary>
          <PaymentElement
            options={paymentElementOptions}
            onChange={(event) => {
              setPaymentElementReady(event.complete);
              if (event.complete && !paymentElementCompleteTrackedRef.current) {
                paymentElementCompleteTrackedRef.current = true;
                trackCheckoutDiagnostic('stripe_payment_element_completed');
              }
            }}
            onReady={() => {
              setPaymentElementReady(true);
              if (!paymentElementReadyTrackedRef.current) {
                paymentElementReadyTrackedRef.current = true;
                trackCheckoutDiagnostic('stripe_elements_ready');
              }
            }}
            onLoadError={(paymentError) => {
              console.error('[StarterCheckoutPage] PaymentElement load error:', paymentError);
              trackCheckoutDiagnostic('stripe_elements_load_error', {
                error_message: paymentError?.message || 'unknown_error',
              });
              setError('Payment form failed to load. Please refresh and try again.');
            }}
          />
        </StripeErrorBoundary>
      </CheckoutPaymentContainer>

      {/* Promo code input section */}
      <div className="coupon-code-section">
        {appliedCoupon ? (
          <div className="coupon-code-applied">
            <div className="coupon-code-applied__info">
              <FaTag className="coupon-code-applied__icon" />
              <div className="coupon-code-applied__details">
                <span className="coupon-code-applied__code">{appliedCoupon.code}</span>
                {appliedCoupon.description && (
                  <span className="coupon-code-applied__description">{appliedCoupon.description}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="coupon-code-applied__remove"
              onClick={handleRemoveCoupon}
              disabled={processing || isValidatingCoupon}
              aria-label="Remove coupon"
            >
              <FaTimes />
            </button>
          </div>
        ) : (
          <CouponCodeInput
            onApply={handleApplyCoupon}
            onClearValidationError={() => setCouponValidationError('')}
            isValidating={isValidatingCoupon}
            validationError={couponValidationError}
            disabled={processing}
          />
        )}
      </div>

      <div className="checkout-security-badges">
        <span>
          <FaShieldAlt className="badge-icon" />
          SSL Secured
        </span>
        <span>
          <FaShieldAlt className="badge-icon" />
          Encrypted
        </span>
      </div>

      {error ? (
        <div className="checkout-error-banner">
          {error.toLowerCase().includes('already subscribed') ? (
            <>
              You're already subscribed! No need to pay again.
              <Link to="/ai-agent" className="checkout-error-link">
                Go to AI Agent →
              </Link>
            </>
          ) : (
            error
          )}
        </div>
      ) : null}

      <label className={`terms-checkbox-group ${termsError ? 'error' : ''}`}>
        <input
          type="checkbox"
          className="terms-checkbox-input"
          id="landing-checkout-terms"
          aria-label={`I agree to the Terms of Service and understand this will start my ${termsText}.`}
          checked={termsAccepted}
          onChange={(event) => {
            setTermsAccepted(event.target.checked);
            if (event.target.checked) {
              setTermsError(false);
              // Clear error if terms was the issue
              if (isTermsAcceptanceErrorMessage(error)) {
                setError('');
              }
            }
          }}
          disabled={processing}
          required
        />
        <span className="terms-text">
          I agree to the{' '}
          <a
            href="/terms"
            className="terms-link"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </a>{' '}
          and understand this will start my {termsText}.
        </span>
      </label>
      {termsError && <div className="terms-error-text">Please accept the Terms of Service</div>}

      {!hasPrefilledHowFoundUs && (
        <HowFoundUsDropdown
          value={contactData.howFoundUs}
          otherText={contactData.howFoundUsOtherText}
          onChange={(val) => handleContactChange('howFoundUs', val)}
          onOtherTextChange={(val) => handleContactChange('howFoundUsOtherText', val)}
          disabled={processing}
        />
      )}

      {/* Price summary when coupon is applied */}
      {appliedCoupon && appliedCoupon.discountedAmount != null && (
        <div className="landing-checkout-price-summary">
          <div className="landing-checkout-price-summary__row">
            <span>{planDisplayName}</span>
            <span className="landing-checkout-price-summary__original">
              ${((appliedCoupon.originalAmount || 9900) / 100).toFixed(2)}/month
            </span>
          </div>
          <div className="landing-checkout-price-summary__row landing-checkout-price-summary__row--discount">
            <span>Discount ({appliedCoupon.code})</span>
            <span>-${(((appliedCoupon.originalAmount || 9900) - appliedCoupon.discountedAmount) / 100).toFixed(2)}</span>
          </div>
          <div className="landing-checkout-price-summary__row landing-checkout-price-summary__row--total">
            <span>You pay today</span>
            <span>${(appliedCoupon.discountedAmount / 100).toFixed(2)}</span>
          </div>
          {appliedCoupon.discountedAmount < (appliedCoupon.originalAmount || 9900) && (
            <div className="landing-checkout-price-summary__after">
              Then ${((appliedCoupon.originalAmount || 9900) / 100).toFixed(2)}/month after
            </div>
          )}
        </div>
      )}

      <div className="checkout-button-wrapper">
        <button
          ref={buttonRef}
          type="submit"
          className={`checkout-cta-button ${processing || isValidatingCoupon ? 'disabled' : ''}`}
          onClick={handleButtonClick}
          onMouseEnter={() => isSubmitDisabled && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          disabled={processing || isValidatingCoupon}
        >
          {processing ? 'Processing...' : isValidatingCoupon ? 'Applying coupon...' : (appliedCoupon?.discountedAmount === 0 ? 'Start for $0' : ctaText)}
        </button>
        <Overlay target={buttonRef.current} show={showTooltip} placement="top">
          {(props) => (
            <Tooltip id="checkout-validation-tooltip" className="checkout-tooltip" {...props}>
              {getValidationMessage()}
            </Tooltip>
          )}
        </Overlay>
      </div>

      <div className="checkout-reassurance-list">
        <span>
          <FaCheckCircle className="reassurance-icon" /> Instant account access
        </span>
        <span>
          <FaCheckCircle className="reassurance-icon" /> Cancel anytime • 30-day money-back guarantee
        </span>
      </div>
    </form>
  );
}

/**
 * PlanCheckoutPage - Shared checkout page for Starter and Light plan routes.
 *
 * STRIPE MODE ARCHITECTURE:
 * ========================
 * This component determines the Stripe Elements mode based on whether the user will
 * have a trial period. This is critical because Stripe Elements must be initialized
 * with the correct mode BEFORE collecting payment details:
 *
 * - 'setup' mode: For trial subscriptions. Collects payment method for future charges.
 *   Backend returns SetupIntent (seti_...). Frontend calls confirmSetup().
 *
 * - 'payment' mode: For immediate charges (no trial). Processes payment immediately.
 *   Backend returns PaymentIntent (pi_...). Frontend calls confirmPayment().
 *
 * The mode cannot be changed after Elements initialization, so we:
 * 1. Lift billingPeriod state here (not in form) to compute mode before Elements mount
 * 2. Use a dynamic key on Elements to force re-mount when mode changes
 *
 * MODE DETERMINATION LOGIC:
 * -------------------------
 * effectiveTrialDays = isAnnual ? annualTrialDays : trialDays
 * stripeMode = effectiveTrialDays > 0 ? 'setup' : 'payment'
 *
 * Variant breakdown:
 * - control monthly: trialDays=7 → setup mode
 * - control annual: annualTrialDays=0 → payment mode
 * - no_trial monthly: trialDays=0 → payment mode
 * - no_trial annual: annualTrialDays=0 → payment mode
 * - discounttest-2 monthly: trialDays=0 → payment mode
 * - discounttest-2 annual: annualTrialDays=0 → payment mode
 */
function PlanCheckoutPage({
  presentation = 'page',
  productSlugOverride = null,
  planKeyOverride = null,
  variantConfigOverride = null,
  experimentVariantOverride = null,
  checkoutDesignVariantOverride = null,
  checkoutDesignExperimentActiveOverride = null,
  checkoutThemeOverride = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const checkoutUser = useUser();
  const { userProfile } = checkoutUser;
  const [themeMode] = useThemeMode();
  const checkoutStartedAtMsRef = useRef(Date.now());
  const queryParams = new URLSearchParams(location.search);
  // Path-based default: /checkout/light-plan → always Light (user explicitly chose Light
  // on the pricing page). /checkout/starter-plan with no explicit product is the
  // sales/marketplace paywall surface and is eligible for the Light-vs-Starter price test.
  // Checkout redesign is separate: it runs across both checkout plan routes through
  // this shared component.
  const isLightPathname = location.pathname === '/checkout/light-plan';
  const pathnameSlug = isLightPathname ? 'light-plan' : 'starter-plan';
  const explicitProductSlug = queryParams.get('product');
  const productSlugFromUrl = productSlugOverride || explicitProductSlug || pathnameSlug;
  const testVariantParam = queryParams.get('testVariant');
  const checkoutDesignVariantParam = queryParams.get('checkoutDesignVariant');
  const checkoutThemeParam = queryParams.get('checkoutTheme');
  const billingPeriodParam = queryParams.get('billingPeriod');
  const isNonProduction = process.env.NODE_ENV !== 'production';
  const hasTestVariantOverride = isNonProduction && Boolean(testVariantParam && VARIANT_CONFIG[testVariantParam]);
  const hasCheckoutDesignOverride =
    isNonProduction && CHECKOUT_DESIGN_VARIANTS.includes(checkoutDesignVariantParam);
  const hasCheckoutDesignPropOverride =
    CHECKOUT_DESIGN_VARIANTS.includes(checkoutDesignVariantOverride);
  const hasCheckoutThemeOverride =
    isNonProduction && CHECKOUT_THEME_VARIANTS.includes(checkoutThemeParam);
  const hasPricingPropOverride = Boolean(variantConfigOverride && experimentVariantOverride);
  const isStarterPlanExperimentSurface =
    !productSlugOverride &&
    !isLightPathname &&
    !explicitProductSlug &&
    !hasTestVariantOverride &&
    !hasPricingPropOverride;
  const isCheckoutDesignExperimentSurface = !hasCheckoutDesignOverride && !hasCheckoutDesignPropOverride;

  const isExplicitBillingPeriod =
    billingPeriodParam === 'monthly' || billingPeriodParam === 'annual';
  const [billingPeriodOverride, setBillingPeriod] = useState(
    isExplicitBillingPeriod ? billingPeriodParam : null,
  );
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [autoCouponValidation, setAutoCouponValidation] = useState({
    key: null,
    loading: false,
  });

  // Light vs Starter Checkout A/B test (the successor to STARTER_PRICING_ABC).
  // This test runs on /checkout/starter-plan ONLY. control = Starter $99→$49,
  // test = Light $49→$24. Same urgency UX, same 50%-off framing, pure price swap.
  const lightCheckoutExperiment = ACTIVE_EXPERIMENTS.LIGHT_VS_STARTER_CHECKOUT;
  const { variant: lightCheckoutVariant, isLoading: experimentLoading } = useABTest(
    lightCheckoutExperiment.name,
    lightCheckoutExperiment.featureFlag,
    'control',
    {
      page: 'landing_checkout',
      product_slug: productSlugFromUrl,
      validVariants: CHECKOUT_DESIGN_VARIANTS,
      normalizeControlExposure: true,
      skipFeatureFlagEvaluation: !isStarterPlanExperimentSurface,
      forcedVariant: 'control',
    }
  );

  const checkoutDesignExperiment = ACTIVE_EXPERIMENTS.CHECKOUT_REDESIGN_2026_07;
  const {
    variant: checkoutDesignFlagVariant,
    isLoading: checkoutDesignExperimentLoading,
    isExperimentActive: checkoutDesignFlagExperimentActive,
  } = useABTest(
    checkoutDesignExperiment.name,
    checkoutDesignExperiment.featureFlag,
    'control',
    {
      page: 'landing_checkout',
      product_slug: productSlugFromUrl,
      validVariants: CHECKOUT_DESIGN_VARIANTS,
      normalizeControlExposure: true,
      skipFeatureFlagEvaluation: !isCheckoutDesignExperimentSurface,
      forcedVariant: hasCheckoutDesignPropOverride ? checkoutDesignVariantOverride : 'control',
    },
  );

  // Allow URL parameter override for testing: ?testVariant=control|no_trial|discounttest-2|light
  // This ONLY works in non-production environments for QA purposes

  // Resolve which variant config to use. Priority order:
  //   1. Pathname /checkout/light-plan → always Light (user clicked Light CTA)
  //   2. ?product=starter-plan or ?product=light-plan → honor explicit pricing-page choice
  //   3. ?testVariant=... URL override (QA / dev only)
  //   4. light-vs-starter-checkout flag → 'test' means Light, 'control' means Starter
  const experimentVariant = useMemo(() => {
    if (variantConfigOverride && experimentVariantOverride) {
      return experimentVariantOverride;
    }
    if (isLightPathname) {
      return 'light';
    }
    if (explicitProductSlug === 'light-plan') {
      return 'light';
    }
    if (explicitProductSlug === 'starter-plan') {
      return 'discounttest-2';
    }
    if (hasTestVariantOverride) {
      if (SHOULD_LOG_CHECKOUT_DEBUG) {
        console.log(`🧪 [A/B Test Override] Using testVariant=${testVariantParam} from URL`);
      }
      return testVariantParam;
    }
    // On /checkout/starter-plan the flag decides: test variant swaps in Light.
    if (lightCheckoutVariant === 'test') {
      return 'light';
    }
    // Default / control: Starter 50%-off (discounttest-2, the winner of the previous test).
    return 'discounttest-2';
  }, [
    experimentVariantOverride,
    explicitProductSlug,
    hasTestVariantOverride,
    isLightPathname,
    lightCheckoutVariant,
    testVariantParam,
    variantConfigOverride,
  ]);

  const checkoutDesignVariant = useMemo(() => {
    if (hasCheckoutDesignPropOverride) {
      return checkoutDesignVariantOverride;
    }
    if (hasCheckoutDesignOverride) {
      return checkoutDesignVariantParam;
    }
    return checkoutDesignFlagVariant === 'test' ? 'test' : 'control';
  }, [
    checkoutDesignFlagVariant,
    checkoutDesignVariantOverride,
    checkoutDesignVariantParam,
    hasCheckoutDesignOverride,
    hasCheckoutDesignPropOverride,
  ]);

  const isCheckoutRedesign = checkoutDesignVariant === 'test';
  // The redesign renders Stripe's PaymentElement as its only payment surface;
  // saved-card rows are not rendered here, so do not fetch them.
  const savedMethods = EMPTY_SAVED_PAYMENT_METHODS;
  const checkoutDesignExperimentActive =
    checkoutDesignExperimentActiveOverride ?? checkoutDesignFlagExperimentActive;
  const checkoutTheme = checkoutThemeOverride || (hasCheckoutThemeOverride ? checkoutThemeParam : applyThemeMode(themeMode));

  // When the A/B test assigns a user on /checkout/starter-plan to the 'light' variant,
  // we need to bill them through the light-plan product, not starter-plan. The Stripe
  // PaymentIntent backend looks up Product + ProductPlan by this slug, so getting this
  // wrong would bill Starter $49 while displaying Light $24 UX — which would be a bug.
  const productSlug = useMemo(() => {
    if (productSlugOverride) {
      return productSlugOverride;
    }
    if (experimentVariant === 'light') {
      return 'light-plan';
    }
    return productSlugFromUrl;
  }, [experimentVariant, productSlugFromUrl, productSlugOverride]);
  const checkoutRecoveryStorageKey = getCheckoutRecoveryStorageKey(productSlug);
  const [checkoutRecoveryState, setCheckoutRecoveryState] = useState({
    storageKey: null,
    required: false,
  });
  const checkoutRecoveryRequired =
    hasStoredCheckoutRecoveryRequirement(checkoutRecoveryStorageKey) ||
    (
      checkoutRecoveryState.storageKey === checkoutRecoveryStorageKey &&
      checkoutRecoveryState.required
    );
  const handleCheckoutRecoveryRequired = useCallback(() => {
    storeCheckoutRecoveryRequirement(checkoutRecoveryStorageKey);
    setCheckoutRecoveryState({
      storageKey: checkoutRecoveryStorageKey,
      required: true,
    });
  }, [checkoutRecoveryStorageKey]);

  // Get variant config based on experiment variant
  const baseVariantConfig = useMemo(
    () => variantConfigOverride || getVariantConfig(experimentVariant),
    [experimentVariant, variantConfigOverride]
  );
  const variantConfig = useMemo(
    () => isCheckoutRedesign ? resolveRedesignExactPricing(baseVariantConfig) : baseVariantConfig,
    [baseVariantConfig, isCheckoutRedesign],
  );
  const canUseAnnualBilling = Boolean(variantConfig.annualPlanKey);
  const requestedBillingPeriod = variantConfig.fixedBillingPeriod === 'annual'
    ? 'annual'
    : variantConfig.fixedBillingPeriod
      ? 'monthly'
      : billingPeriodOverride || variantConfig.defaultBillingPeriod || 'monthly';
  const billingPeriod =
    requestedBillingPeriod === 'annual' && !canUseAnnualBilling
      ? 'monthly'
      : requestedBillingPeriod;

  const selectedPlanKey = useMemo(() => {
    if (planKeyOverride) {
      return planKeyOverride;
    }
    if (billingPeriod === 'annual') {
      return variantConfig.annualPlanKey;
    }
    return variantConfig.monthlyPlanKey || variantConfig.planKey;
  }, [billingPeriod, planKeyOverride, variantConfig]);

  const urlCouponCode = queryParams.get('coupon') || queryParams.get('promoCode') || queryParams.get('promo') || '';

  const autoCouponValidationKey = urlCouponCode
    ? `${productSlug}:${selectedPlanKey || 'none'}:${urlCouponCode.toUpperCase()}`
    : null;

  useEffect(() => {
    if (!autoCouponValidationKey) return;

    let isCancelled = false;
    setAutoCouponValidation({ key: autoCouponValidationKey, loading: true });

    const validateUrlCoupon = async () => {
      try {
        const catalog = await validatePromoCode(urlCouponCode, null);
        const discountedPlan = findDiscountedPlanForCheckout(catalog, productSlug, selectedPlanKey);

        if (!isCancelled) {
          setAppliedCoupon(discountedPlan ? couponStateFromPlan(urlCouponCode, discountedPlan) : null);
          setAutoCouponValidation({ key: autoCouponValidationKey, loading: false });
        }
      } catch (err) {
        if (!isCancelled) {
          setAppliedCoupon(null);
          setAutoCouponValidation({ key: autoCouponValidationKey, loading: false });
        }
      }
    };

    validateUrlCoupon();

    return () => {
      isCancelled = true;
    };
  }, [autoCouponValidationKey, productSlug, selectedPlanKey, urlCouponCode]);

  // Compute effective trial days based on billing period
  // Annual billing typically has no trial (pay upfront for full year)
  const effectiveTrialDays = useMemo(() => {
    const isAnnual = billingPeriod === 'annual';
    return isAnnual ? (variantConfig.annualTrialDays || 0) : (variantConfig.trialDays || 0);
  }, [billingPeriod, variantConfig]);

  // Determine Stripe Elements mode based on trial days and $0-due coupons.
  // This MUST be computed before Elements initialization
  // - 'setup' mode: For trials - collect payment method for future charges (SetupIntent)
  // - 'setup' mode: For fully discounted first invoices - collect a payment method now
  // - 'payment' mode: For immediate charges (PaymentIntent)
  const zeroDueTodayFromCoupon = appliedCoupon?.discountedAmount === 0;
  const stripeMode = effectiveTrialDays > 0 || zeroDueTodayFromCoupon ? 'setup' : 'payment';
  const shouldSetupFutureUsage = variantConfig.setupFutureUsage !== false;

  useEffect(() => {
    if (!SHOULD_LOG_CHECKOUT_DEBUG) return;
    console.log(`[StarterCheckoutPage] Stripe mode: ${stripeMode}, effectiveTrialDays: ${effectiveTrialDays}, billingPeriod: ${billingPeriod}, variant: ${experimentVariant}, zeroDueTodayFromCoupon: ${zeroDueTodayFromCoupon}`);
  }, [stripeMode, effectiveTrialDays, billingPeriod, experimentVariant, zeroDueTodayFromCoupon]);

  // Priority: URL params > user profile > empty string
  // For authenticated users, pre-fill from their profile
  const profileName = userProfile?.firstName && userProfile?.lastName
    ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
    : userProfile?.firstName || '';
  const name = queryParams.get('name') || profileName || '';
  const email = queryParams.get('email') || userProfile?.email || '';
  const phone = queryParams.get('phone') || userProfile?.phone || '';
  const howFoundUs = queryParams.get('howFoundUs') || userProfile?.howFoundUs || '';
  const howFoundUsOtherText =
    queryParams.get('howFoundUsOtherText') ||
    queryParams.get('howFoundUsDetails') ||
    userProfile?.howFoundUsDetails ||
    '';
  const checkoutExitPath = useMemo(() => {
    const exitParams = new URLSearchParams(location.search);
    for (const key of CHECKOUT_EXIT_QUERY_KEYS) {
      const safePath = getSafeCheckoutExitPath(exitParams.get(key));
      if (safePath) return safePath;
    }

    const source = exitParams.get('source');
    if (source === 'marketplace') {
      const marketplacePath = getSafeCheckoutExitPath(exitParams.get('successUrl'));
      if (marketplacePath) return marketplacePath;
    }

    return CHECKOUT_SOURCE_EXIT_PATHS[source] || CHECKOUT_EXIT_FALLBACK_PATH;
  }, [location.search]);
  const checkoutOverlayBackground = useMemo(
    () => getCheckoutOverlayBackground(location.state),
    [location.state],
  );
  const handleCheckoutCloseClick = useCallback((event) => {
    if (!checkoutOverlayBackground) {
      return;
    }

    event.preventDefault();
    navigate(locationToPath(checkoutOverlayBackground), {
      replace: true,
      state: checkoutOverlayBackground.state || null,
    });
  }, [checkoutOverlayBackground, navigate]);

  const appearance = useMemo(() => {
    if (isCheckoutRedesign) {
      if (checkoutTheme === 'dark') {
        return {
          theme: 'flat',
          inputs: 'spaced',
          labels: 'above',
          variables: {
            colorPrimary: '#4285f4',
            colorBackground: '#101827',
            colorText: '#f8fafc',
            colorTextSecondary: '#cbd5e1',
            colorTextPlaceholder: '#7f8da3',
            colorBorder: '#5f6f86',
            logoColor: 'light',
            tabLogoColor: 'light',
            tabLogoSelectedColor: 'light',
            blockLogoColor: 'light',
            iconCheckmarkColor: '#cbd5e1',
            colorDanger: '#f87171',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            borderRadius: '6px',
            spacingUnit: '4px',
            accordionItemSpacing: '8px',
            gridRowSpacing: '8px',
            gridColumnSpacing: '8px',
            fontSizeBase: '14px',
            labelFontSize: '13px',
            labelFontWeight: '700',
            inputBoxShadow: 'none',
            inputFocusBoxShadow: 'none',
            focusBoxShadow: 'none',
            tabSpacing: '8px',
          },
          rules: {
            '.Tab': {
              backgroundColor: '#101827',
              border: '1px solid #5f6f86',
              borderColor: '#5f6f86',
              borderWidth: '1px',
              borderTopWidth: '1px',
              borderRightWidth: '1px',
              borderBottomWidth: '1px',
              borderLeftWidth: '1px',
              borderRadius: '6px',
              boxShadow: 'none',
              color: '#f8fafc',
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '20px',
              padding: '12px',
            },
            '.Tab:hover': {
              backgroundColor: '#141f30',
              border: '1px solid #64748b',
              borderColor: '#64748b',
              borderWidth: '1px',
              borderTopWidth: '1px',
              borderRightWidth: '1px',
              borderBottomWidth: '1px',
              borderLeftWidth: '1px',
              boxShadow: 'none',
              color: '#f8fafc',
            },
            '.Tab:focus': {
              border: '1px solid #64748b',
              borderColor: '#64748b',
              borderWidth: '1px',
              borderTopWidth: '1px',
              borderRightWidth: '1px',
              borderBottomWidth: '1px',
              borderLeftWidth: '1px',
              boxShadow: 'none',
              outline: 'none',
            },
            '.Tab--selected': {
              backgroundColor: '#101827',
              border: '1px solid #4285f4',
              borderColor: '#4285f4',
              borderWidth: '1px',
              borderTopWidth: '1px',
              borderRightWidth: '1px',
              borderBottomWidth: '1px',
              borderLeftWidth: '1px',
              boxShadow: 'none',
              outline: 'none',
              color: '#f8fafc',
            },
            '.Tab--selected:hover': {
              backgroundColor: '#101827',
              border: '1px solid #4285f4',
              borderColor: '#4285f4',
              boxShadow: 'none',
              color: '#f8fafc',
            },
            '.TabLabel': {
              color: '#cbd5e1',
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '20px',
            },
            '.TabLabel--selected': {
              color: '#f8fafc',
              fontWeight: '600',
            },
            '.TabIcon': {
              color: '#cbd5e1',
              fill: '#cbd5e1',
            },
            '.TabIcon--selected': {
              color: '#4285f4',
              fill: '#4285f4',
            },
            '.AccordionItem': {
              backgroundColor: '#141f30',
              border: '1px solid transparent',
              borderColor: 'transparent',
              borderWidth: '0px',
              borderRadius: '6px',
              boxShadow: 'none',
              color: '#cbd5e1',
              padding: '12px 0px',
            },
            '.AccordionItem--selected': {
              backgroundColor: 'transparent',
              border: '0px solid #101827',
              borderColor: '#101827',
              borderWidth: '0px',
              borderRadius: '0px',
              boxShadow: 'none',
              color: '#f8fafc',
              padding: '0px 0px 10px',
            },
            '.AccordionItem:hover': {
              backgroundColor: 'transparent',
              border: '0px solid #101827',
              borderColor: '#101827',
              borderWidth: '0px',
              boxShadow: 'none',
            },
            '.AccordionItem--selected:hover': {
              backgroundColor: 'transparent',
              border: '0px solid #101827',
              borderColor: '#101827',
              borderWidth: '0px',
              boxShadow: 'none',
            },
            '.AccordionItem:focus-visible': {
              outline: 'none',
              boxShadow: 'none',
            },
            '.PickerItem': {
              backgroundColor: 'transparent',
              border: '0px solid #101827',
              borderColor: '#101827',
              borderRadius: '0px',
              boxShadow: 'none',
              color: '#cbd5e1',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
              padding: '0px',
            },
            '.PickerItem--selected': {
              backgroundColor: 'transparent',
              border: '0px solid transparent',
              borderColor: '#101827',
              borderRadius: '0px',
              boxShadow: 'none',
              color: '#f8fafc',
            },
            '.PickerItem:hover': {
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              boxShadow: 'none',
              color: '#f8fafc',
            },
            '.PickerAction': {
              backgroundColor: 'transparent',
              border: '0px solid #101827',
              borderColor: '#101827',
              borderRadius: '0px',
              boxShadow: 'none',
              color: '#cbd5e1',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
            },
            '.ToggleItem': {
              backgroundColor: 'transparent',
              border: '0px solid transparent',
              borderColor: '#101827',
              borderRadius: '0px',
              boxShadow: 'none',
              color: '#cbd5e1',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
            },
            '.ToggleItem--selected': {
              backgroundColor: 'transparent',
              border: '0px solid transparent',
              borderColor: 'transparent',
              boxShadow: 'none',
              color: '#f8fafc',
            },
            '.Block': {
              backgroundColor: 'transparent',
              border: '0px solid transparent',
              borderColor: 'transparent',
              borderWidth: '0px',
              borderTopWidth: '0px',
              borderRightWidth: '0px',
              borderBottomWidth: '0px',
              borderLeftWidth: '0px',
              borderRadius: '0px',
              boxShadow: 'none',
              outline: '0px solid transparent',
              margin: '0px',
              padding: '0px',
            },
            '.BlockDivider': {
              backgroundColor: 'transparent',
            },
            '.RadioIcon': {
              width: '0px',
            },
            '.RadioIconOuter': {
              stroke: 'transparent',
            },
            '.RadioIconInner': {
              fill: 'transparent',
              r: '0',
            },
            '.Input': {
              backgroundColor: '#0b111b',
              border: '1px solid #5f6f86',
              borderColor: '#5f6f86',
              borderWidth: '1px',
              borderTopWidth: '1px',
              borderRightWidth: '1px',
              borderBottomWidth: '1px',
              borderLeftWidth: '1px',
              borderRadius: '6px',
              boxShadow: 'none',
              color: '#f8fafc',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
              padding: '11px 12px',
            },
            '.Input:focus': {
              borderColor: '#64748b',
              boxShadow: 'none',
            },
            '.Input::placeholder': {
              color: '#7f8da3',
              fontSize: '14px',
              fontWeight: '500',
            },
            '.Label': {
              color: '#cbd5e1',
              fontSize: '13px',
              fontWeight: '700',
              lineHeight: '18px',
              marginBottom: '4px',
            },
            '.Text': {
              color: '#cbd5e1',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
            },
            '.Text--secondary': {
              color: '#cbd5e1',
              fontSize: '13px',
              fontWeight: '500',
              lineHeight: '18px',
            },
            '.TermsText': {
              color: '#cbd5e1',
              fontSize: '13px',
              fontWeight: '500',
              lineHeight: '18px',
            },
            '.Link': {
              color: '#4285f4',
              fontSize: '13px',
              fontWeight: '600',
            },
            '.Error': {
              color: '#f87171',
              fontSize: '13px',
              fontWeight: '600',
              lineHeight: '18px',
            },
          },
        };
      }

      return {
        theme: 'flat',
        inputs: 'spaced',
        labels: 'above',
        variables: {
          colorPrimary: '#4285f4',
          colorBackground: '#ffffff',
          colorText: '#202124',
          colorTextSecondary: '#6b7280',
          colorTextPlaceholder: '#9ca3af',
          colorBorder: '#d7dde7',
          logoColor: 'dark',
          tabLogoColor: 'dark',
          tabLogoSelectedColor: 'dark',
          blockLogoColor: 'dark',
          iconCheckmarkColor: '#4b5563',
          colorDanger: '#df1b41',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            borderRadius: '6px',
            spacingUnit: '4px',
            accordionItemSpacing: '8px',
          gridRowSpacing: '8px',
          gridColumnSpacing: '8px',
          fontSizeBase: '14px',
          labelFontSize: '13px',
          labelFontWeight: '700',
          inputBoxShadow: 'none',
          inputFocusBoxShadow: 'none',
          focusBoxShadow: 'none',
          tabSpacing: '8px',
        },
        rules: {
          '.Tab': {
            backgroundColor: '#ffffff',
            border: '1px solid #d7dde7',
            borderColor: '#d7dde7',
            borderWidth: '1px',
            borderTopWidth: '1px',
            borderRightWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            borderRadius: '6px',
            boxShadow: 'none',
            color: '#4b5563',
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '20px',
            padding: '12px',
          },
          '.Tab:hover': {
            backgroundColor: '#f8fafc',
            border: '1px solid #b9c2d0',
            borderColor: '#b9c2d0',
            borderWidth: '1px',
            borderTopWidth: '1px',
            borderRightWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            boxShadow: 'none',
            color: '#202124',
          },
          '.Tab:focus': {
            border: '1px solid #b8c2d2',
            borderColor: '#b8c2d2',
            borderWidth: '1px',
            borderTopWidth: '1px',
            borderRightWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            boxShadow: 'none',
            outline: 'none',
          },
          '.Tab--selected': {
            backgroundColor: '#ffffff',
            border: '1px solid #4285f4',
            borderColor: '#4285f4',
            borderWidth: '1px',
            borderTopWidth: '1px',
            borderRightWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            boxShadow: 'none',
            outline: 'none',
            color: '#202124',
          },
          '.Tab--selected:hover': {
            backgroundColor: '#ffffff',
            border: '1px solid #4285f4',
            borderColor: '#4285f4',
            boxShadow: 'none',
            color: '#202124',
          },
          '.TabLabel': {
            color: '#4b5563',
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '20px',
          },
          '.TabLabel--selected': {
            color: '#202124',
            fontWeight: '600',
          },
          '.TabIcon': {
            color: '#4b5563',
            fill: '#4b5563',
          },
          '.TabIcon--selected': {
            color: '#4285f4',
            fill: '#4285f4',
          },
          '.AccordionItem': {
            backgroundColor: '#ffffff',
            border: '1px solid transparent',
            borderColor: 'transparent',
            borderWidth: '0px',
            borderRadius: '6px',
            boxShadow: 'none',
            color: '#4b5563',
            padding: '12px 0px',
          },
          '.AccordionItem--selected': {
            backgroundColor: 'transparent',
            border: '0px solid #ffffff',
            borderColor: '#ffffff',
            borderWidth: '0px',
            borderRadius: '0px',
            boxShadow: 'none',
            color: '#202124',
            padding: '0px 0px 10px',
          },
          '.AccordionItem:hover': {
            backgroundColor: 'transparent',
            border: '0px solid #ffffff',
            borderColor: '#ffffff',
            borderWidth: '0px',
            boxShadow: 'none',
          },
          '.AccordionItem--selected:hover': {
            backgroundColor: 'transparent',
            border: '0px solid #ffffff',
            borderColor: '#ffffff',
            borderWidth: '0px',
            boxShadow: 'none',
          },
          '.AccordionItem:focus-visible': {
            outline: 'none',
            boxShadow: 'none',
          },
          '.PickerItem': {
            backgroundColor: 'transparent',
            border: '0px solid #ffffff',
            borderColor: '#ffffff',
            borderRadius: '0px',
            boxShadow: 'none',
            color: '#4b5563',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            padding: '0px',
          },
          '.PickerItem--selected': {
            backgroundColor: 'transparent',
            border: '0px solid transparent',
            borderColor: '#ffffff',
            borderRadius: '0px',
            boxShadow: 'none',
            color: '#202124',
          },
          '.PickerItem:hover': {
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            boxShadow: 'none',
            color: '#202124',
          },
          '.PickerAction': {
            backgroundColor: 'transparent',
            border: '0px solid #ffffff',
            borderColor: '#ffffff',
            borderRadius: '0px',
            boxShadow: 'none',
            color: '#4b5563',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
          },
          '.ToggleItem': {
            backgroundColor: 'transparent',
            border: '0px solid transparent',
            borderColor: '#ffffff',
            borderRadius: '0px',
            boxShadow: 'none',
            color: '#4b5563',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
          },
          '.ToggleItem--selected': {
            backgroundColor: 'transparent',
            border: '0px solid transparent',
            borderColor: 'transparent',
            boxShadow: 'none',
            color: '#202124',
          },
          '.Block': {
            backgroundColor: 'transparent',
            border: '0px solid transparent',
            borderColor: 'transparent',
            borderWidth: '0px',
            borderTopWidth: '0px',
            borderRightWidth: '0px',
            borderBottomWidth: '0px',
            borderLeftWidth: '0px',
            borderRadius: '0px',
            boxShadow: 'none',
            outline: '0px solid transparent',
            margin: '0px',
            padding: '0px',
          },
          '.BlockDivider': {
            backgroundColor: 'transparent',
          },
          '.RadioIcon': {
            width: '0px',
          },
          '.RadioIconOuter': {
            stroke: 'transparent',
          },
          '.RadioIconInner': {
            fill: 'transparent',
            r: '0',
          },
          '.Input': {
            backgroundColor: '#ffffff',
            border: '1px solid #d7dde7',
            borderColor: '#d7dde7',
            borderWidth: '1px',
            borderRadius: '6px',
            boxShadow: 'none',
            color: '#202124',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            padding: '11px 12px',
          },
          '.Input:focus': {
            borderColor: '#b8c2d2',
            boxShadow: 'none',
          },
          '.Input::placeholder': {
            color: '#9ca3af',
            fontSize: '14px',
            fontWeight: '500',
          },
          '.Label': {
            color: '#4b5563',
            fontSize: '13px',
            fontWeight: '700',
            lineHeight: '18px',
            marginBottom: '4px',
          },
          '.Text': {
            color: '#202124',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
          },
          '.Text--secondary': {
            color: '#6b7280',
            fontSize: '13px',
            fontWeight: '500',
            lineHeight: '18px',
          },
          '.TermsText': {
            color: '#6b7280',
            fontSize: '13px',
            fontWeight: '500',
            lineHeight: '18px',
          },
          '.Link': {
            color: '#4285f4',
            fontSize: '13px',
            fontWeight: '600',
          },
          '.Error': {
            color: '#df1b41',
            fontSize: '13px',
            fontWeight: '600',
            lineHeight: '18px',
          },
        },
      };
    }

    return {
      theme: 'night',
      variables: {
        colorPrimary: '#ff6b35',
        // Note: Stripe only accepts HEX, rgb(), or hsl() - NOT rgba()
        colorBackground: '#1a1f2e',
        colorText: '#f0f0f0',
        colorDanger: '#ff5252',
        fontFamily: 'Poppins, system-ui, sans-serif',
        borderRadius: '12px',
      },
    };
  }, [checkoutTheme, isCheckoutRedesign]);

  // Dynamic Elements options based on computed stripe mode
  // CRITICAL: mode must match what backend will return (SetupIntent vs PaymentIntent)
  // For subscriptions in payment mode, backend creates PaymentIntent with setup_future_usage: 'off_session'
  // so we MUST tell Elements to expect this, otherwise Stripe throws a mismatch error
  //
  // PAYMENT MODE: Backend-created subscription invoice PaymentIntents use this exact
  // explicit method list. Deferred Elements must use the same method contract or Stripe
  // rejects confirmation before the account handoff can run.
  //
  // SETUP MODE (trials): Backend creates SetupIntent with automatic_payment_methods: { enabled: true }
  // We must NOT specify paymentMethodTypes so Elements defaults to automatic payment methods.
  const elementsOptions = useMemo(
    () => ({
      mode: stripeMode,
      currency: 'usd',
      appearance,
      loader: 'auto',
      ...(stripeMode === 'payment' ? {
        amount: appliedCoupon?.discountedAmount ?? getVariantAmountCents(variantConfig, billingPeriod),
        paymentMethodTypes: ['card', 'cashapp', 'link'],
        // Recurring plans should save the payment method for future invoices.
        ...(shouldSetupFutureUsage ? { setupFutureUsage: 'off_session' } : {}),
      } : {}),
    }),
    [appearance, stripeMode, appliedCoupon, billingPeriod, shouldSetupFutureUsage, variantConfig],
  );

  // Callback for billing period change - passed to child form
  const handleBillingPeriodChange = useCallback((newPeriod) => {
    if (newPeriod === billingPeriod) return;
    if (isCheckoutRedesign) {
      // A validated coupon contains plan-specific cents. Never carry a monthly
      // quote into a newly mounted annual Elements instance (or vice versa).
      setAppliedCoupon(null);
    }
    setBillingPeriod(newPeriod);
  }, [billingPeriod, isCheckoutRedesign]);

  const initialUserInfo = useMemo(
    () => ({
      name,
      email,
      phone,
      howFoundUs,
      howFoundUsOtherText,
    }),
    [name, email, phone, howFoundUs, howFoundUsOtherText],
  );

  if (!stripePromise) {
    return (
      <div className="checkout-landing-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center py-5">
              <h2>Configuration Error</h2>
              <p>Payment system is not properly configured. Please contact support.</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const isWaitingForUrlCoupon =
    Boolean(autoCouponValidationKey) &&
    (autoCouponValidation.loading || autoCouponValidation.key !== autoCouponValidationKey);
  const hasDeterministicRedesignPricing =
    hasPricingPropOverride ||
    hasTestVariantOverride ||
    isLightPathname ||
    Boolean(explicitProductSlug);
  const canRenderDuringCheckoutExperimentLoading =
    hasDeterministicRedesignPricing ||
    hasCheckoutDesignPropOverride ||
    hasCheckoutDesignOverride;
  const isWaitingForPricingExperiment =
    experimentLoading && !hasDeterministicRedesignPricing;
  const isWaitingForCheckoutDesignExperiment =
    checkoutDesignExperimentLoading &&
    !canRenderDuringCheckoutExperimentLoading;

  // ProtectedRoute already holds checkout until auth resolves. The form can render
  // immediately and fill any still-empty contact fields when the profile arrives.
  // Keep the blocking loader only for data that changes the displayed plan/price.
  if (isWaitingForPricingExperiment || isWaitingForCheckoutDesignExperiment || isWaitingForUrlCoupon) {
    if (isCheckoutRedesign) {
      return (
        <CheckoutRedesignLoadingShell
          checkoutTheme={checkoutTheme}
          presentation={presentation}
          onClose={checkoutOverlayBackground ? handleCheckoutCloseClick : null}
          planDisplayName={variantConfig.planDisplayName || (
            productSlug === 'light-plan' ? 'BotSpot Light' : 'Starter Plan'
          )}
        />
      );
    }

    return (
      <div className={`checkout-landing-page ${isCheckoutRedesign ? `checkout-redesign-page${presentation === 'overlay' ? ' checkout-redesign-page--overlay' : ''}` : ''}`}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center py-5">
              <p>Loading...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (isCheckoutRedesign) {
    return (
      <div
        className={`checkout-landing-page checkout-redesign-page bsui-scope${presentation === 'overlay' ? ' checkout-redesign-page--overlay' : ''}`}
        data-bs-theme={checkoutTheme}
      >
        <div className="checkout-redesign-container">
          <div className="checkout-redesign-shell">
            <header className="checkout-redesign-topbar" aria-label="Checkout">
              <Link
                to={checkoutExitPath}
                className="checkout-redesign-exit-link"
                aria-label="Close checkout"
                onClick={handleCheckoutCloseClick}
              >
                <FaTimes aria-hidden="true" focusable="false" />
              </Link>
            </header>

            <Elements
              key={`landing-checkout-${stripeMode}-${checkoutDesignVariant}`}
              stripe={stripePromise}
              options={elementsOptions}
            >
              <StarterCheckoutForm
                productSlug={productSlug}
                initialUserInfo={initialUserInfo}
                variantConfig={variantConfig}
                experimentVariant={experimentVariant}
                checkoutDesignVariant={checkoutDesignVariant}
                checkoutDesignExperimentActive={checkoutDesignExperimentActive}
                checkoutTheme={checkoutTheme}
                billingPeriod={billingPeriod}
                onBillingPeriodChange={handleBillingPeriodChange}
                stripeMode={stripeMode}
                appliedCoupon={appliedCoupon}
                onAppliedCouponChange={setAppliedCoupon}
                savedMethods={savedMethods}
                checkoutUser={checkoutUser}
                checkoutStartedAtMs={checkoutStartedAtMsRef.current}
                checkoutRecoveryRequired={checkoutRecoveryRequired}
                onCheckoutRecoveryRequired={handleCheckoutRecoveryRequired}
              />
            </Elements>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-landing-page">
      <header className="landing-checkout-header">
        <h1>{variantConfig.headerText}</h1>
        <p className="header-subtitle">
          {variantConfig.subheaderText || 'Unlock the AI Agent in under two minutes'}
        </p>
      </header>

      <div className="trust-strip-wrapper">
        <TrustStrip />
      </div>

      <Container className="landing-checkout-container">
        <Row className="justify-content-center">
          <Col lg={7} md={10}>
            {/* Key changes when stripeMode changes to force Elements re-initialization
                This is necessary because Stripe Elements mode cannot be changed after init */}
            <Elements
              key={`landing-checkout-${stripeMode}-${billingPeriod}-${appliedCoupon?.code || 'none'}-${appliedCoupon?.discountedAmount ?? 'full'}`}
              stripe={stripePromise}
              options={elementsOptions}
            >
              <StarterCheckoutForm
                productSlug={productSlug}
                initialUserInfo={initialUserInfo}
                variantConfig={variantConfig}
                experimentVariant={experimentVariant}
                checkoutDesignVariant={checkoutDesignVariant}
                checkoutDesignExperimentActive={checkoutDesignExperimentActive}
                billingPeriod={billingPeriod}
                onBillingPeriodChange={handleBillingPeriodChange}
                stripeMode={stripeMode}
                appliedCoupon={appliedCoupon}
                onAppliedCouponChange={setAppliedCoupon}
                savedMethods={savedMethods}
                checkoutUser={checkoutUser}
                checkoutStartedAtMs={checkoutStartedAtMsRef.current}
                checkoutRecoveryRequired={checkoutRecoveryRequired}
                onCheckoutRecoveryRequired={handleCheckoutRecoveryRequired}
              />
            </Elements>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default PlanCheckoutPage;
